import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './App.css';
import { transactionBadgeClass, transactionTypeLabel, isPositiveTransaction, isGoldTransaction, displayBalanceBefore } from './transactionTypes';
import Icon from './icons';
import { ThemeToggle } from './theme';
import RedeemBuy from './RedeemBuy';
import CreditToGC from './CreditToGC';

import useAppVersion from './useAppVersion';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// CCMTeamDelegates is Adobe's "Team Delegates" admin group, not a licence — it
// grants no applications, so a customer assigned it gets nothing for their
// credits. Hidden from every product picker; the server refuses it too, so this
// is convenience rather than the actual guard.
const isBlockedProduct = (name) =>
    String(name || '').toLowerCase().replace(/[\s._-]/g, '').includes('teamdelegate');
const assignableProducts = (products) =>
    (Array.isArray(products) ? products : []).filter(p => !isBlockedProduct(p));
// Negative = day plan (-7 = 7 days, -14 = 14 days), 1–12 = months, 0 = Lifetime.
// No local list of durations: which ones a product offers is the server's
// decision (it may include durations the owner added), and a second copy here
// could only ever disagree with it.
const formatMarketplaceMonth = (month) => month === 0 ? 'Lifetime' : month < 0 ? `${-month} days` : `${month} mo`;
const sortMarketplaceMonths = (a, b) => {
    if (a === 0) return 1;
    if (b === 0) return -1;
    if (a < 0 && b < 0) return b - a;
    if (a < 0) return -1;
    if (b < 0) return 1;
    return a - b;
};
const SHORT_DURATION_DEFAULT_CREDITS = { '7d': 0.4, '14d': 0.6 };
const ACTIVATION_DURATION_OPTIONS = [
    { key: '7d', label: '7 Days', defaultCredits: 0.4 },
    { key: '14d', label: '14 Days', defaultCredits: 0.6 },
    ...Array.from({ length: 12 }, (_, index) => {
        const month = index + 1;
        return { key: month, label: `${month} ${month === 1 ? 'Month' : 'Months'}`, defaultCredits: month };
    })
];

function getDurationCredits(creditsPerMonth = {}, durationKey) {
    const raw = creditsPerMonth?.[durationKey] ?? SHORT_DURATION_DEFAULT_CREDITS[durationKey];
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : (SHORT_DURATION_DEFAULT_CREDITS[durationKey] || Number(durationKey) || 0);
}

function getBackupKeyCredits(stats = {}, durationMonths) {
    const keyPricing = stats.backupKeys?.keyPricing || {};
    const rawGlobal = keyPricing?.[durationMonths] ?? keyPricing?.[String(durationMonths)];
    const globalPrice = Number(rawGlobal);
    if (Number.isFinite(globalPrice) && globalPrice > 0) {
        return globalPrice;
    }
    return getDurationCredits(stats.creditsPerMonth, durationMonths);
}

// Round to 2 decimals for both display and math, so float drift like
// `0 + 0.6` rendering as `0.6000000000000001` never reaches the UI.
function roundCredits(value) {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

// Product images are admin-pasted links, often 1.5-2 MB PNGs. Ask the server
// for a card-sized WebP instead (server/product-thumbs.js). ?v= changes when the
// image link changes, so the browser may cache each thumbnail for good.
function productThumb(p, width = 480) {
    const url = p && p.imageUrl;
    if (!url) return '';
    let h = 0;
    for (let i = 0; i < url.length; i++) h = (h * 31 + url.charCodeAt(i)) | 0;
    return `${API_URL}/credits-pool/product-image/${p.id}?w=${width}&v=${(h >>> 0).toString(36)}`;
}

// A product image that fails is retried twice, a moment apart (a thumbnail may
// still be building, or the network hiccuped). Only then does the card give up,
// and it shows the product's name on a coloured tile instead of an empty box.
function onThumbError(e, name) {
    const img = e.currentTarget;
    const tries = Number(img.dataset.retry || 0);
    if (tries < 2) {
        img.dataset.retry = String(tries + 1);
        const next = img.src.replace(/&retry=d+$/, '') + `&retry=${tries + 1}`;
        setTimeout(() => { img.src = next; }, 1500 * (tries + 1));
        return;
    }
    img.style.display = 'none';
    const box = img.parentElement;
    if (box && name) { box.classList.add('thumb-missing'); box.setAttribute('data-name', name); }
}

function formatCredits(value) {
    return roundCredits(value).toFixed(2);
}

function getDurationLabel(value, user = {}) {
    if (user.durationDays) return `${user.durationDays} day${user.durationDays === 1 ? '' : 's'}`;
    const option = ACTIVATION_DURATION_OPTIONS.find(item => String(item.key) === String(value || user.durationKey));
    if (option) return option.label.toLowerCase();
    const months = Number(user.durationMonths || value || 1);
    return `${months} month${months === 1 ? '' : 's'}`;
}

function getVisibleActivationDurationOptions(hiddenDurations = []) {
    const hidden = new Set((hiddenDurations || []).map(value => String(value)));
    return ACTIVATION_DURATION_OPTIONS.filter(option => !hidden.has(String(option.key)));
}

/**
 * Long product copy collapsed to a readable height.
 *
 * Some listings run to dozens of lines of features, which pushed the duration
 * picker and the buy button far below the fold. The toggle only appears when
 * the text actually overflows, so short descriptions are untouched — measured
 * rather than guessed from character count, because line count is what decides
 * whether it looks long.
 */
function CollapsibleDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // Measured while collapsed: once expanded, scrollHeight equals clientHeight
    // and the comparison would always say "fits".
    setExpanded(false);
    const el = ref.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 4);
  }, [text]);

  return (
    <>
      <div ref={ref} className={`mp-desc${expanded ? ' is-expanded' : ''}${overflows && !expanded ? ' is-clamped' : ''}`}>
        <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{text}</p>
      </div>
      {overflows && (
        <button type="button" className="mp-desc-toggle" onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
}

/**
 * How much room an organization has left, as a short label for the picker.
 *
 * remainingCapacity === null means nothing caps this team, which is different
 * from 0 (full) — so an unlimited team must not be labelled "0 left".
 */
/**
 * The label to show for a product, falling back to the raw name.
 *
 * Adobe's internal profile names ("CCLE Configuration") do not match what the
 * customer sees in Adobe's console ("Creative Cloud Pro"), which made a
 * legitimate product look like a banned one. The raw name is still what gets
 * submitted — this only changes what is read.
 */
function productLabelFor(org, product) {
  if (!org || !Array.isArray(org.products) || !Array.isArray(org.productLabels)) return product;
  const i = org.products.indexOf(product);
  return (i >= 0 && org.productLabels[i]) ? org.productLabels[i] : product;
}

function orgCapacityLabel(org) {
  if (!org || org.remainingCapacity === null || org.remainingCapacity === undefined) return '';
  const left = Number(org.remainingCapacity);
  if (!Number.isFinite(left)) return '';
  if (left <= 0) return 'FULL';
  return left + ' left';
}

function orgIsFull(org) {
  return !!org && org.remainingCapacity !== null && org.remainingCapacity !== undefined
    && Number(org.remainingCapacity) <= 0;
}

function getMarketplaceOptions(product) {
    const options = Array.isArray(product.purchaseOptions) && product.purchaseOptions.length > 0
        ? product.purchaseOptions
        : (Array.isArray(product.enabledMonths) ? product.enabledMonths : [1, 2]).map(month => {
            const parsedMonth = parseInt(month, 10);
            const rawPrice = product.priceByMonth?.[parsedMonth] ?? product.priceByMonth?.[String(parsedMonth)] ??
                (parsedMonth === 1 ? product.price1Month : parsedMonth === 2 ? product.price2Month : parsedMonth > 0 ? parsedMonth : 0);
            return {
                month: parsedMonth,
                priceGoldCoins: Number(rawPrice),
                availableCount: product.availableByMonth?.[parsedMonth] || product.availableByMonth?.[String(parsedMonth)] || 0
            };
        });
    return options
        .filter(option => Number.isInteger(parseInt(option.month, 10)) && Number.isFinite(Number(option.priceGoldCoins ?? option.priceCredits)))
        .map(option => ({
            month: parseInt(option.month, 10),
            priceGoldCoins: Number(option.priceGoldCoins ?? option.priceCredits),
            // Pre-sale price when a promotion is live (server-provided).
            originalPriceGoldCoins: Number(option.originalPriceGoldCoins ?? option.priceGoldCoins ?? option.priceCredits),
            availableCount: option.availableCount || 0
        }))
        .sort((a, b) => sortMarketplaceMonths(a.month, b.month));
}

// "Ends in 2d 5h" countdown label for a live promo; empty for open-ended sales.
function formatPromoCountdown(endsAt) {
    if (!endsAt) return '';
    const diffMs = new Date(endsAt).getTime() - Date.now();
    if (!Number.isFinite(diffMs) || diffMs <= 0) return '';
    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `Ends in ${days}d ${hours}h`;
    if (hours > 0) return `Ends in ${hours}h ${minutes}m`;
    return `Ends in ${minutes}m`;
}

// Auto-advancing banner slider for the modern dashboard. Pure CSS transitions;
// pauses on hover, clickable slides route to the shop or a specific product.
function CpBannerCarousel({ banners, apiUrl, onNavigate }) {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const count = banners.length;

    useEffect(() => {
        if (count <= 1 || paused) return undefined;
        const timer = setInterval(() => setIndex(i => (i + 1) % count), 5000);
        return () => clearInterval(timer);
    }, [count, paused]);

    useEffect(() => {
        if (index >= count) setIndex(0);
    }, [count, index]);

    if (count === 0) return null;

    return (
        <div
            className="cp-banner-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="cp-banner-track" style={{ transform: `translateX(-${index * 100}%)` }}>
                {banners.map(banner => (
                    <div
                        key={banner.id}
                        className={`cp-banner-slide${banner.linkTab || banner.linkProductId ? ' is-clickable' : ''}`}
                        onClick={() => {
                            if (banner.linkProductId) onNavigate({ productId: banner.linkProductId });
                            else if (banner.linkTab) onNavigate({ tab: banner.linkTab });
                        }}
                    >
                        <img src={`${apiUrl}${banner.imageUrl}`} alt={banner.title || 'banner'}
                            /* The banner is the first thing on the dashboard, so fetch it
                               eagerly and decode off the main thread. */
                            loading="eager" decoding="async" fetchpriority="high" />
                        {(banner.title || banner.subtitle) && (
                            <div className="cp-banner-caption">
                                {banner.title && <div className="cp-banner-title">{banner.title}</div>}
                                {banner.subtitle && <div className="cp-banner-subtitle">{banner.subtitle}</div>}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {count > 1 && (
                <div className="cp-banner-dots">
                    {banners.map((banner, i) => (
                        <button
                            key={banner.id}
                            type="button"
                            className={`cp-banner-dot${i === index ? ' active' : ''}`}
                            onClick={() => setIndex(i)}
                            aria-label={`Slide ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function normalizeMarketplaceCategory(value) {
    return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
}

function inferMarketplaceCategory(product = {}) {
    const explicit = normalizeMarketplaceCategory(product.category) ||
        normalizeMarketplaceCategory(product.categoryName) ||
        normalizeMarketplaceCategory(product.productCategory);
    if (explicit) return explicit;

    const text = `${product.name || ''} ${product.description || ''}`.toLowerCase();
    if (/\b(adobe|acrobat|lightroom|photoshop|premiere)\b|creative cloud|substance 3d/.test(text)) {
        return 'Adobe';
    }
    if (/\bcapcut\b/.test(text)) {
        return 'Video';
    }
    return 'Other';
}

function CreditsPoolDashboard({ reseller, token, onLogout }) {
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [redeeming, setRedeeming] = useState(false);
    const [redeemNotice, setRedeemNotice] = useState('');
    const [bulkUserLimit, setBulkUserLimit] = useState(0);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(() => {
        const hash = window.location.hash.slice(1);
        const valid = ['dashboard','assign','bulkImport','users','bulkDelete','bulkExtend','transactions','operations','backupKeys','shop','apiDocs','feedback','branding','about'];
        if (hash === 'buyKeys') return 'assign';
        return valid.includes(hash) ? hash : 'dashboard';
    });

    // Stats
    const [stats, setStats] = useState({
        creditsBalance: 0,
        goldCoinsBalance: 0,
        refundDaysBalance: 0,
        poolDaysLeft: 0,
        isPoolExpired: false,
        totalActiveUsers: 0,
        usersExpiringToday: 0,
        allowTransfer: false,
        allowReinvite: true,
        cpBulkTransferEnabled: false,
        canEditCustomerStatusNotice: false
    });

    // Organization & Products
    const [organizationAccess, setOrganizationAccess] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // User form
    const [email, setEmail] = useState('');
    const [durationMonths, setDurationMonths] = useState(1);

    // Users list
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const USERS_PER_PAGE = 50;

    // Transactions
    const [transactions, setTransactions] = useState([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [txSearchEmail, setTxSearchEmail] = useState('');
    const [txCurrentPage, setTxCurrentPage] = useState(1);
    const TX_PER_PAGE = 50;

    // Filters
    const [filterType, setFilterType] = useState('all'); // all, active, expired, expiring_week, expiring_today
    const [daysLeftOperator, setDaysLeftOperator] = useState('');
    const [daysLeftValue, setDaysLeftValue] = useState('');
    const [selectedDurationFilter, setSelectedDurationFilter] = useState('All');

    // Bulk delete
    const [bulkDeleteEmails, setBulkDeleteEmails] = useState('');

    // Bulk extend
    const [bulkExtendEmails, setBulkExtendEmails] = useState('');
    const [bulkExtendMonths, setBulkExtendMonths] = useState(1);

    // Bulk import
    const [bulkCsvContent, setBulkCsvContent] = useState('');
    const [bulkDurationMonths, setBulkDurationMonths] = useState(1);
    const [bulkImportProgress, setBulkImportProgress] = useState(null); // { totalUsers, estimatedSeconds, startTime }
    const [bulkDeleteProgress, setBulkDeleteProgress] = useState(null); // { totalUsers, startTime }

    // Backup key state
    const [assignMode, setAssignMode] = useState(() => window.location.hash.slice(1) === 'buyKeys' ? 'premade' : 'email'); // 'email' or 'premade'
    const [lastAssignedBackupKey, setLastAssignedBackupKey] = useState(null);
    const [lastAssignedEmail, setLastAssignedEmail] = useState(null);
    const [backupKeyDuration, setBackupKeyDuration] = useState(1);
    const [backupKeyInventory, setBackupKeyInventory] = useState(null);
    const [loadingBackupKey, setLoadingBackupKey] = useState(false);
    const [showPurchasedKeyModal, setShowPurchasedKeyModal] = useState(false);

    // Email transfer
    const [transferringUser, setTransferringUser] = useState(null);
    const [newEmailForTransfer, setNewEmailForTransfer] = useState('');
    // Product mapping for transfer: array where index i = target product for user.products[i]
    const [transferProductMapping, setTransferProductMapping] = useState([]);
    // Indices of licenses to exclude from transfer (when user has duplicate products)
    const [transferRemovedIndices, setTransferRemovedIndices] = useState(new Set());
    // Team the new address lands on — defaults to the one the user is already on
    const [transferTargetOrg, setTransferTargetOrg] = useState('');

    // Organization transfer
    const [transferringOrgUser, setTransferringOrgUser] = useState(null);
    const [selectedTransferOrg, setSelectedTransferOrg] = useState('');
    const [selectedTransferProducts, setSelectedTransferProducts] = useState([]); // User-selected products for target org

    // Extend duration
    const [extendingUser, setExtendingUser] = useState(null);
    const [extensionMonths, setExtensionMonths] = useState(1);

    // Bulk duration modification
    const [selectedUsersForBulk, setSelectedUsersForBulk] = useState([]);
    const [showBulkDurationModal, setShowBulkDurationModal] = useState(false);
    const [showBulkTransferModal, setShowBulkTransferModal] = useState(false); // New State
    const [selectedTransferOrgForBulk, setSelectedTransferOrgForBulk] = useState(''); // New State
    const [selectedTransferProductsForBulk, setSelectedTransferProductsForBulk] = useState([]); // New State

    // Derived state for bulk products
    const bulkDerivedProducts = useMemo(() => {
        if (!selectedTransferOrgForBulk) return [];
        const org = organizationAccess.find(o => String(o.organizationId) === String(selectedTransferOrgForBulk));
        return org ? org.products : [];
    }, [selectedTransferOrgForBulk, organizationAccess]);
    const isZelyxmain = String(reseller?.username || '').toLowerCase() === 'zelyxmain';
    // Every reseller maintains their own customer notice. This was gated on a
    // hardcoded username, and the server flag it checked first was never sent,
    // so exactly one of 429 resellers could edit it — including three who had a
    // notice already published and no way to change or remove it.
    const canEditCustomerStatusNotice = true;
    const canUseCPTransfer = stats.allowTransfer !== false || isZelyxmain;
    const canUseCPBulkTransfer = (stats.cpBulkTransferEnabled === true || isZelyxmain) && canUseCPTransfer;
    const canUseCPReinvite = stats.allowReinvite !== false || isZelyxmain;
    const getNoticeOrgId = (org) => Number(org?.organizationId || org?.id);
    const getNoticeOrgName = (org) => org?.organizationName || org?.name || `Organization ${getNoticeOrgId(org)}`;
    const createNoticeRule = () => ({
        id: `notice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        scope: 'all',
        organizationIds: [],
        message: ''
    });
    const normalizeNoticeRulesForForm = (branding = {}, canEditNotice = false) => {
        if (!canEditNotice) return [];
        const rules = Array.isArray(branding.noticeRules) ? branding.noticeRules : [];
        const normalized = rules.map((rule, index) => ({
            id: rule.id || `notice-${index}`,
            scope: rule.scope === 'organizations' ? 'organizations' : 'all',
            organizationIds: Array.isArray(rule.organizationIds)
                ? [...new Set(rule.organizationIds.map(Number).filter(id => Number.isFinite(id) && id > 0))]
                : [],
            message: rule.message || ''
        }));
        if (normalized.length === 0 && branding.notice) {
            normalized.push({
                id: 'legacy-all-notice',
                scope: 'all',
                organizationIds: [],
                message: branding.notice
            });
        }
        return normalized;
    };
    const [bulkNewDuration, setBulkNewDuration] = useState(1);

    // Bulk Reinvite Progress
    const [bulkReinviteProgress, setBulkReinviteProgress] = useState(null); // { total, current, currentEmail, results: [{email, status, message}] }

    // Single Reinvite with Product Mapping
    const [reinvitingUser, setReinvitingUser] = useState(null);
    const [reinviteProductMapping, setReinviteProductMapping] = useState([]);
    const [reinviteSelectedProducts, setReinviteSelectedProducts] = useState([]);

    // Bulk Reinvite Config Modal
    const [showBulkReinviteConfig, setShowBulkReinviteConfig] = useState(false);
    const [bulkReinviteOrgProducts, setBulkReinviteOrgProducts] = useState({}); // { orgId: [selectedProducts] }

    // Organization Filter State
    const [selectedOrgFilter, setSelectedOrgFilter] = useState('All');
    const [selectedProductFilter, setSelectedProductFilter] = useState('All');
    const [selectedTeamStatusFilter, setSelectedTeamStatusFilter] = useState('All');


    // Bulk Operations History
    const [bulkOperations, setBulkOperations] = useState([]);
    const [loadingOperations, setLoadingOperations] = useState(false);
    const [showOpsNotifications, setShowOpsNotifications] = useState(false);

    // Organization Migration Status
    const [migrationStatus, setMigrationStatus] = useState(null);
    const [migrationHistory, setMigrationHistory] = useState([]);

    // Failures Modal
    const [viewingFailures, setViewingFailures] = useState(null); // { operationType, failures: [] }

    // Announcements state
    const [announcements, setAnnouncements] = useState([]);
    const [showAnnouncementPopup, setShowAnnouncementPopup] = useState(false);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
        const saved = localStorage.getItem('cpDismissedAnnouncements');
        return saved ? JSON.parse(saved) : [];
    });

    // API Key Management State
    const [apiKeyInfo, setApiKeyInfo] = useState(null);
    const [loadingApiKey, setLoadingApiKey] = useState(false);
    const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);
    const [apiKeyCopied, setApiKeyCopied] = useState(false);
    const [showRevealKeyModal, setShowRevealKeyModal] = useState(false);
    const [revealPassword, setRevealPassword] = useState('');
    const [revealedKey, setRevealedKey] = useState(null);
    const [revealError, setRevealError] = useState(null);
    const [apiUsage, setApiUsage] = useState(null);
    const [loadingApiUsage, setLoadingApiUsage] = useState(false);

    // Feedback state
    const [myFeedback, setMyFeedback] = useState([]);
    const [loadingFeedback, setLoadingFeedback] = useState(false);
    const [feedbackType, setFeedbackType] = useState('suggestion');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [unseenFeedbackCount, setUnseenFeedbackCount] = useState(0);

    // Status page branding
    const [branding, setBranding] = useState({});
    const [brandingForm, setBrandingForm] = useState({ title: '', subtitle: '', brandColor: '', notice: '', noticeRules: [] });
    const [loadingBranding, setLoadingBranding] = useState(false);
    const [savingBranding, setSavingBranding] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingOrgIcon, setUploadingOrgIcon] = useState(false);

    // Marketplace state
    const [marketplaceProducts, setMarketplaceProducts] = useState([]);
    const [marketplaceEnabled, setMarketplaceEnabled] = useState(false);
    const [loadingMarketplace, setLoadingMarketplace] = useState(false);
    const [myMarketplaceOrders, setMyMarketplaceOrders] = useState([]);
    const [loadingMyOrders, setLoadingMyOrders] = useState(false);
    const [buyingProductId, setBuyingProductId] = useState(null);
    const [selectedRetentionByProduct, setSelectedRetentionByProduct] = useState({});
    const [selectedMarketplaceProductId, setSelectedMarketplaceProductId] = useState(null);
    const [purchasedOrder, setPurchasedOrder] = useState(null);
    // New: category filter, quantity, buyer email
    // Sentinel category value for the "New" chip. Prefixed so it can never
    // collide with a real category name an admin types.
    const NEW_FILTER = '__new__';
    // Version of the server answering this page.
    const appVersion = useAppVersion();
    const [shopCategoryFilter, setShopCategoryFilter] = useState('all');
    const [shopSearchQuery, setShopSearchQuery] = useState('');
    // Browse vs My Orders — separate sub-views so a long product grid doesn't
    // bury the order history at the bottom of an endless scroll.
    const [shopViewTab, setShopViewTab] = useState('browse');
    const [shopQuantity, setShopQuantity] = useState(1);
    const [shopBuyerEmail, setShopBuyerEmail] = useState('');
    const [purchasedOrders, setPurchasedOrders] = useState([]); // multi-order result
    // Collapsed by default; only expanded if the reseller explicitly expanded it before.
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('cpSidebarCollapsed') !== 'false');

    // Admin-controlled dashboard appearance (modern dashboard + banner slider).
    // Defaults all-off so a failed fetch just shows the classic dashboard.
    const [dashboardConfig, setDashboardConfig] = useState({ dashboardV2Enabled: false, bannerEnabled: false, banners: [] });

    const toggleSidebarCollapsed = useCallback(() => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('cpSidebarCollapsed', next ? 'true' : 'false');
            return next;
        });
    }, []);

    const marketplaceCategories = useMemo(() => {
        const counts = new Map();
        for (const product of marketplaceProducts) {
            const category = normalizeMarketplaceCategory(product.category);
            if (!category) continue;
            counts.set(category, (counts.get(category) || 0) + 1);
        }
        return Array.from(counts.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([name, count]) => ({ name, count }));
    }, [marketplaceProducts]);

    // Newest first, so "what just dropped" is answerable at a glance. The server
    // decides what counts as new (createdAt within its own window) so the badge
    // and this list can never disagree.
    const newArrivalProducts = useMemo(
        () => marketplaceProducts
            .filter(product => product.isNew)
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
        [marketplaceProducts]
    );

    const filteredMarketplaceProducts = useMemo(() => {
        const query = shopSearchQuery.trim().toLowerCase();
        let list = shopCategoryFilter === 'all'
            ? marketplaceProducts
            : shopCategoryFilter === NEW_FILTER
                ? marketplaceProducts.filter(product => product.isNew)
                : marketplaceProducts.filter(product => normalizeMarketplaceCategory(product.category) === shopCategoryFilter);
        if (query) {
            list = list.filter(product =>
                `${product.name || ''} ${product.description || ''} ${product.category || ''}`
                    .toLowerCase()
                    .includes(query));
        }
        return list;
    }, [marketplaceProducts, shopCategoryFilter, shopSearchQuery]);

    // Spotlight the top sellers so every reseller sees what's popular to buy.
    // Always aim to fill 5 slots: real best sellers first (by sales), then
    // backfill with other available products so the row never looks half-empty.
    const bestSellerProducts = useMemo(() => {
        const enrich = (product) => {
            const options = getMarketplaceOptions(product);
            const prices = options.map(option => option.priceGoldCoins).filter(Number.isFinite);
            const minPrice = prices.length > 0 ? Math.min(...prices) : null;
            const maxPrice = prices.length > 0 ? Math.max(...prices) : null;
            return { ...product, minPrice, maxPrice };
        };
        const chosen = marketplaceProducts
            .filter(product => (product.soldCount || 0) > 0)
            .sort((a, b) => (b.soldCount || 0) - (a.soldCount || 0))
            .slice(0, 5);
        if (chosen.length < 5) {
            const have = new Set(chosen.map(p => p.id));
            for (const p of marketplaceProducts) {
                if (chosen.length >= 5) break;
                if (!have.has(p.id)) { chosen.push(p); have.add(p.id); }
            }
        }
        return chosen.map(enrich);
    }, [marketplaceProducts]);

    const bestSellerIds = useMemo(
        () => new Set(bestSellerProducts.map(product => product.id)),
        [bestSellerProducts]
    );

    // Products currently on sale (server only attaches promo to visible products).
    const onSaleProducts = useMemo(
        () => marketplaceProducts.filter(product => product.promo && product.promo.discountPercent > 0),
        [marketplaceProducts]
    );
    // Dashboard shows only a small preview of the sale (a store-wide clearance can
    // put hundreds of items on sale) — the full list lives in the Shop.
    const ONSALE_DASHBOARD_LIMIT = 10;

    const openShopProduct = useCallback((productId) => {
        setActiveTab('shop');
        setSelectedMarketplaceProductId(productId);
        setShopQuantity(1);
        setShopBuyerEmail('');
        fetchMyMarketplaceOrders();
    }, []);

    const isAnnouncementVisible = (id) => {
        return !dismissedAnnouncements.includes(id);
    };

    const dismissAnnouncement = (id) => {
        const newDismissed = [...dismissedAnnouncements, id];
        setDismissedAnnouncements(newDismissed);
        localStorage.setItem('cpDismissedAnnouncements', JSON.stringify(newDismissed));
    };

    const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };

    // Shared with the admin console: the same row must not be a credit on one
    // page and a deduction on the other.
    const transactionLabel = transactionTypeLabel;

    const getRefundDaysAvailable = (user) => {
        if (!user) return 0;
        const explicitDays = parseInt(user.refundDaysAvailable, 10);
        if (Number.isFinite(explicitDays)) {
            return Math.max(0, explicitDays);
        }
        const expiry = new Date(user.expiresAt);
        if (Number.isNaN(expiry.getTime())) return 0;
        return Math.max(0, Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24)));
    };

    // The grace window this account is on. Every user row carries it, so read it
    // from the data instead of restating a number that may not apply here.
    const accountGraceHours = (() => {
        const stamped = users.find(u => Number.isFinite(Number(u?.graceHours)));
        return stamped ? Number(stamped.graceHours) : 2;
    })();

    // What deleting this user will ACTUALLY do. Mirrors the server's order of
    // precedence in getDeleteRefundOutcome: the grace-period credit refund
    // first, then pro-rata pending credits when the admin has those enabled,
    // and only then refund-days. Every delete prompt reads from here, because
    // each of them used to guess separately — and all of them assumed a fixed
    // 24h window plus refund-days, which stopped being true once the window
    // became configurable and pro-rata was turned on.
    const getDeleteOutcome = (user) => {
        const parsedGrace = Number(user?.graceHours);
        const graceHours = Number.isFinite(parsedGrace) ? parsedGrace : 2;

        // Re-added within 30 days of a grace-period delete of the same email: nothing back.
        if (user?.repeatDeletion) return { kind: 'none', repeat: true, graceHours };
        if (user?.canRefund) {
            const credits = Number(user?.graceRefundCredits);
            return { kind: 'credits', credits: Number.isFinite(credits) ? credits : null, graceHours };
        }

        const pending = Number(user?.pendingRefundCredits) || 0;
        if (user?.proRataEnabled) {
            // Pro-rata replaces refund-days on the server; it never grants both.
            return pending > 0
                ? { kind: 'pending', credits: pending, graceHours }
                : { kind: 'none', graceHours };
        }

        const days = getRefundDaysAvailable(user);
        return days > 0 ? { kind: 'days', days, graceHours } : { kind: 'none', graceHours };
    };

    const getDeleteSummary = (emailList) => {
        const selectedUsers = users.filter(u => emailList.includes(u.email.toLowerCase()));
        const outcomes = selectedUsers.map(getDeleteOutcome);
        const pendingUsers = outcomes.filter(o => o.kind === 'pending');
        const dayUsers = outcomes.filter(o => o.kind === 'days');

        const creditUsers = outcomes.filter(o => o.kind === 'credits');
        return {
            refundable: creditUsers.length,
            refundableCreditsTotal: Math.round(creditUsers.reduce((sum, o) => sum + (o.credits || 0), 0) * 100) / 100,
            repeatUsers: outcomes.filter(o => o.repeat).length,
            pendingUsers: pendingUsers.length,
            pendingCreditsTotal: Math.round(pendingUsers.reduce((sum, o) => sum + o.credits, 0) * 10) / 10,
            refundDayUsers: dayUsers.length,
            refundDaysTotal: dayUsers.reduce((sum, o) => sum + o.days, 0),
            noRefundNoDays: outcomes.filter(o => o.kind === 'none').length,
            unknownUsers: Math.max(0, emailList.length - selectedUsers.length)
        };
    };

    // Shared by the bulk prompts so the two never drift apart.
    const buildDeleteSummaryLines = (summary) => {
        const lines = [];
        if (summary.refundable > 0) lines.push(`- ${summary.refundable} will refund ${summary.refundableCreditsTotal} credit(s) immediately (inside the grace period)`);
        if (summary.repeatUsers > 0) lines.push(`- ${summary.repeatUsers} were deleted inside the grace period and re-added within 30 days: no refund in the first hours after activation`);
        if (summary.pendingUsers > 0) lines.push(`- ${summary.pendingUsers} will add ${summary.pendingCreditsTotal} refund credit(s) to your pending balance, for admin settlement`);
        if (summary.refundDayUsers > 0) lines.push(`- ${summary.refundDayUsers} will add ${summary.refundDaysTotal} refund day(s) to your refund-day balance`);
        if (summary.noRefundNoDays - summary.repeatUsers > 0) lines.push(`- ${summary.noRefundNoDays - summary.repeatUsers} will delete with nothing credited back`);
        return lines;
    };

    const getAllowedProductsForOrg = useCallback((organizationId) => {
        const org = organizationAccess.find(o => String(o.organizationId) === String(organizationId));
        return org ? [...new Set((org.products || []).filter(Boolean))] : [];
    }, [organizationAccess]);

    // Is this team flagged K12 by the admin? K12 teams are charged a flat rate
    // per activation/extension instead of the per-duration price.
    const isOrgK12 = useCallback((organizationId) => {
        if (organizationId === undefined || organizationId === null || organizationId === '') return false;
        return organizationAccess.some(o => String(o.organizationId) === String(organizationId) && o.k12 === true);
    }, [organizationAccess]);

    // Credits charged for an activation/extension on a given team. Mirrors the
    // server's creditsForActivation() so what's shown equals what's deducted.
    // `explicitK12` (from a user row's own k12 flag) wins over the access-list
    // lookup, because an org can be dropped from organizationAccess while its
    // users remain — in that case the access list can't tell us it's K12.
    const getOrgActivationCredits = useCallback((organizationId, durationKey, explicitK12) => {
        const k12 = explicitK12 === undefined || explicitK12 === null
            ? isOrgK12(organizationId)
            : explicitK12 === true;
        if (k12) {
            const raw = Number(stats.k12Cost);
            const perMonth = Number.isFinite(raw) && raw >= 0 ? raw : 1;
            // k12Cost is PER MONTH and multiplied by the duration, matching the
            // server. Sub-month plans ('7d'/'14d') are not numeric keys and stay
            // at the single-period rate rather than becoming free.
            const months = /^\d+$/.test(String(durationKey ?? '').trim())
                ? parseInt(durationKey, 10)
                : NaN;
            return Number.isFinite(months) && months > 0
                ? Math.round(perMonth * months * 100) / 100
                : perMonth;
        }
        return getDurationCredits(stats.creditsPerMonth, durationKey);
    }, [isOrgK12, stats.k12Cost, stats.creditsPerMonth]);

    // Bulk extend has no org selector — one email list can span a K12 team and a
    // Pro team — so price each email against its own user's team, exactly like
    // the server does. Emails we can't resolve locally fall back to Pro pricing.
    const estimateBulkExtendCredits = useCallback((emails, durationKey) => {
        const byEmail = new Map((users || []).map(u => [String(u.email || '').toLowerCase(), u]));
        const total = (emails || []).reduce((sum, e) => {
            const u = byEmail.get(String(e).toLowerCase());
            return sum + getOrgActivationCredits(u?.organizationId, durationKey, u?.k12);
        }, 0);
        return roundCredits(total);
    }, [users, getOrgActivationCredits]);

    // Get all unique products across ALL organizations the reseller has access to
    // Used as fallback when a user's specific org has no admin-assigned products
    const getAllAllowedProducts = useCallback(() => {
        const allProducts = [];
        for (const org of organizationAccess) {
            for (const p of (org.products || [])) {
                if (p && !allProducts.includes(p)) allProducts.push(p);
            }
        }
        return allProducts;
    }, [organizationAccess]);

    const getTransactionBadgeClass = (tx) => transactionBadgeClass(tx.type);

    const getTransactionAmountDisplay = (tx) => {
        const isPositive = isPositiveTransaction(tx.type);
        const refundDays = Math.max(0, parseInt(tx.refundDays || 0, 10) || 0);
        if (refundDays > 0) {
            return {
                text: `${isPositive ? '+' : '-'}${refundDays} day(s)`,
                color: isPositive ? '#059669' : '#dc2626'
            };
        }

        const credits = Math.abs(Number(tx.credits || 0) || 0);
        const goldPrefix = isGoldTransaction(tx.type) ? '' : '';
        if (credits === 0) {
            return { text: `${goldPrefix}0`, color: '#64748b' };
        }

        return {
            text: `${goldPrefix}${isPositive ? '+' : '-'}${formatCredits(credits)}`,
            color: isPositive ? '#059669' : '#dc2626'
        };
    };

    // Fetch announcements
    const fetchAnnouncements = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/announcements`, { headers: authHeaders });
            if (res.ok) {
                const data = await res.json();
                const activeAnnouncements = data.announcements || [];
                setAnnouncements(activeAnnouncements);
                // Show popup if there are any undismissed announcements
                const hasUndismissed = activeAnnouncements.some(a => !dismissedAnnouncements.includes(a.id));
                if (hasUndismissed) {
                    setShowAnnouncementPopup(true);
                }
            }
        } catch (err) {
            console.error('Failed to fetch announcements:', err);
        }
    };

    // ==================== API KEY MANAGEMENT ====================

    const fetchApiKeyInfo = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/api-key/info`, { headers: authHeaders });
            if (res.ok) {
                const data = await res.json();
                setApiKeyInfo(data);
            }
        } catch (err) {
            console.error('Failed to fetch API key info:', err);
        }
    };

    const fetchApiUsage = async () => {
        setLoadingApiUsage(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/api-key/usage`, { headers: authHeaders });
            if (res.ok) {
                const data = await res.json();
                setApiUsage(data);
            }
        } catch (err) {
            console.error('Failed to fetch API usage:', err);
        }
        setLoadingApiUsage(false);
    };

    const generateApiDocsMarkdown = () => {
        const base = `${window.location.origin}/api/credits-pool/external`;
        const orgLine = apiKeyInfo?.defaultOrganizationId
            ? `**Current API organization:** ${apiKeyInfo.defaultOrganizationName || `Organization #${apiKeyInfo.defaultOrganizationId}`} — omit \`organizationId\` in your requests and this target updates automatically if the admin changes your access.`
            : '';
        const sampleProduct = organizationAccess[0]?.products?.[0] || 'Product Name';

        return `# Credits Pool External API Documentation

Generated: ${new Date().toLocaleString()}
Reseller: ${reseller?.username || ''}

${orgLine}

## Authentication

All API requests require your API key in the \`x-api-key\` header.

For enhanced security, include a timestamp and HMAC signature:

\`\`\`
Headers:
  x-api-key: your_api_key_here
  x-timestamp: <Unix ms>
  x-signature: HMAC-SHA256(timestamp + body, api_key)
  Content-Type: application/json
\`\`\`

**Security notes:**
- Never expose your API key in client-side code or public repositories
- Timestamps must be within 5 minutes of server time
- Rate limit: 30 requests per minute
- API key is shown only once on generation — store it securely

## Base URL

\`\`\`
${base}
\`\`\`

## Endpoints

### POST /add-user

Add a user with duration. Use 1-12 for month plans, or "7d" / "14d" for short plans. Credits are deducted automatically. Products and organizationId are optional. Recommended: omit organizationId so admin can change your API organization without requiring code changes.

Request body:
\`\`\`json
{
  "email": "user@example.com",
  "durationMonths": "14d",
  "products": ["${sampleProduct}"]
}
\`\`\`

Success response (200):
\`\`\`json
{
  "success": true,
  "message": "User user@example.com activated for 14 days...",
  "user": {
    "email": "user@example.com",
    "organizationId": 1,
    "products": ["${sampleProduct}"],
    "durationMonths": null,
    "durationDays": 14,
    "durationKey": "14d",
    "activatedAt": "2026-03-06T...",
    "expiresAt": "2026-04-06T..."
  },
  "creditsRemaining": 999
}
\`\`\`

Error response (400/401/429):
\`\`\`json
{ "error": "Insufficient credits. Required: 1, Available: 0" }
\`\`\`

### GET /team-name?email=user@example.com

Look up the team/organization name for a customer email — the same name shown on the status page.

Success response (200):
\`\`\`json
{ "found": true, "email": "user@example.com", "teamName": "Art Grid Dynamics Amelia Engineers", "organizationId": 12 }
\`\`\`

Not found (404):
\`\`\`json
{ "found": false, "error": "No active subscription found for this email" }
\`\`\`

### GET /org-status?email=user@example.com

Check whether a customer's organization is active or disabled, and whether a self-switch to a working organization is currently available — the same check that drives the "Switch organization" button on the status page.

Success response (200) — organization disabled, switch available:
\`\`\`json
{
  "found": true,
  "email": "user@example.com",
  "teamName": "Art Grid Dynamics Amelia Engineers",
  "organizationId": 12,
  "status": "inactive",
  "switchAvailable": true,
  "switchTargetTeamName": "Blue Sky Creative Group"
}
\`\`\`

Success response (200) — organization active:
\`\`\`json
{
  "found": true,
  "email": "user@example.com",
  "teamName": "Art Grid Dynamics Amelia Engineers",
  "organizationId": 12,
  "status": "active",
  "switchAvailable": false,
  "switchTargetTeamName": null
}
\`\`\`

Not found (404):
\`\`\`json
{ "found": false, "error": "No active subscription found for this email" }
\`\`\`

### POST /org-switch

Move a customer into an available organization once \`/org-status\` reports \`switchAvailable: true\`. Only works while the customer's current organization is disabled. The customer keeps their remaining time and products — they just need to sign out of all Adobe apps and sign back in afterward. Limited to one switch per customer every 10 minutes.

Request body:
\`\`\`json
{ "email": "user@example.com" }
\`\`\`

Success response (200):
\`\`\`json
{
  "success": true,
  "message": "Moved to Blue Sky Creative Group. Sign out of all Adobe apps and sign back in to refresh access.",
  "newOrganizationName": "Blue Sky Creative Group"
}
\`\`\`

Error response (400) — nothing available right now:
\`\`\`json
{ "error": "No organization with available capacity right now. Please try again in a few minutes." }
\`\`\`

### POST /delete-user

Delete an active user from your account. Applies the same refund policy as the panel: inside your account's grace period (2h by default — your administrator can change it, or switch it off entirely) the credits paid are refunded in full. Deleting an email you already deleted inside the grace period and re-added within 30 days refunds nothing if it is deleted again within that period; after it, it is refunded like any other user. Outside it you receive either pro-rata refund credits into your pending balance or refund days, depending on how your administrator has configured refunds. The user is also removed from Adobe automatically.

Request body:
\`\`\`json
{ "email": "user@example.com" }
\`\`\`

Success response (200) — deleted inside the grace period:
\`\`\`json
{
  "success": true,
  "message": "User user@example.com deleted. 1 credit(s) refunded (deleted within the 2h grace period).",
  "refunded": true,
  "creditsRefunded": 1,
  "refundDaysAccumulated": 0,
  "refundDaysBalance": 0,
  "creditsRemaining": 18.4
}
\`\`\`

Success response (200) — deleted after the grace period:
\`\`\`json
{
  "success": true,
  "message": "User user@example.com deleted. 45 refund day(s) added to your balance.",
  "refunded": false,
  "creditsRefunded": 0,
  "refundDaysAccumulated": 45,
  "refundDaysBalance": 45,
  "creditsRemaining": 18.4
}
\`\`\`

Error response (404):
\`\`\`json
{ "error": "User not found or already deleted." }
\`\`\`

## Code Examples

### cURL — Add User
\`\`\`bash
curl -X POST "${base}/add-user" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "user@example.com",
    "durationMonths": "7d"
  }'
\`\`\`

### cURL — Get Team Name
\`\`\`bash
curl "${base}/team-name?email=user@example.com" \\
  -H "x-api-key: YOUR_API_KEY"
\`\`\`

### cURL — Check Org Status / Switch Availability
\`\`\`bash
curl "${base}/org-status?email=user@example.com" \\
  -H "x-api-key: YOUR_API_KEY"
\`\`\`

### cURL — Switch Organization
\`\`\`bash
curl -X POST "${base}/org-switch" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "user@example.com"
  }'
\`\`\`

### cURL — Delete User
\`\`\`bash
curl -X POST "${base}/delete-user" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "user@example.com"
  }'
\`\`\`

### Python
\`\`\`python
import requests, time, hmac, hashlib, json

API_KEY = "YOUR_API_KEY"
BASE_URL = "${base}"

def add_user(email, months):
    timestamp = str(int(time.time() * 1000))
    body = {
        "email": email,
        "durationMonths": months
    }
    payload = timestamp + json.dumps(body)
    signature = hmac.new(
        API_KEY.encode(), payload.encode(), hashlib.sha256
    ).hexdigest()

    headers = {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
        "x-timestamp": timestamp,
        "x-signature": signature
    }
    resp = requests.post(f"{BASE_URL}/add-user",
                         json=body, headers=headers)
    return resp.json()

# Example usage
result = add_user("user@example.com", 1)
print(result)
\`\`\`

### JavaScript (Node.js)
\`\`\`javascript
const crypto = require('crypto');

const API_KEY = 'YOUR_API_KEY';
const BASE = '${base}';

async function addUser(email, months) {
  const timestamp = Date.now().toString();
  const body = { email, durationMonths: months };
  const payload = timestamp + JSON.stringify(body);
  const signature = crypto.createHmac('sha256', API_KEY)
    .update(payload).digest('hex');

  const res = await fetch(BASE + '/add-user', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      'x-timestamp': timestamp,
      'x-signature': signature
    },
    body: JSON.stringify(body)
  });
  return res.json();
}

// Example
addUser('user@example.com', 1).then(console.log);

async function getTeamName(email) {
  const res = await fetch(
    BASE + '/team-name?email=' + encodeURIComponent(email),
    { headers: { 'x-api-key': API_KEY } }
  );
  return res.json();
}

// Example
getTeamName('user@example.com').then(console.log);
\`\`\`

## Error Codes

| HTTP Code | Meaning |
|---|---|
| 200 | OK – Request succeeded |
| 400 | Bad Request – Missing fields, invalid email, duplicate user, insufficient credits, expired pool |
| 401 | Unauthorized – Invalid or missing API key |
| 403 | Forbidden – API access disabled by admin |
| 404 | Not Found – No active subscription found for this email |
| 409 | Conflict – User is already being processed (duplicate request) |
| 429 | Too Many Requests – Rate limit exceeded (30 req/min) |
| 500 | Server Error – Adobe API failure (may be retryable) |
`;
    };

    const handleDownloadApiDocs = () => {
        const markdown = generateApiDocsMarkdown();
        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `credits-pool-api-docs-${reseller?.username || 'reseller'}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const generateApiKey = async () => {
        if (!window.confirm('Generate a new API key? You will only see the key ONCE. Make sure to copy it.')) return;
        setLoadingApiKey(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/api-key/generate`, {
                method: 'POST',
                headers: authHeaders
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setNewlyGeneratedKey(data.apiKey);
                setApiKeyCopied(false);
                fetchApiKeyInfo();
            } else {
                setError(data.error || 'Failed to generate API key');
            }
        } catch (err) {
            setError('Failed to generate API key: ' + err.message);
        }
        setLoadingApiKey(false);
    };

    const revokeApiKey = async () => {
        if (!window.confirm('Revoke your API key? This will immediately disable all API access. You can generate a new key afterwards.')) return;
        setLoadingApiKey(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/api-key/revoke`, {
                method: 'POST',
                headers: authHeaders
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setResult('API key revoked successfully.');
                setNewlyGeneratedKey(null);
                fetchApiKeyInfo();
            } else {
                setError(data.error || 'Failed to revoke API key');
            }
        } catch (err) {
            setError('Failed to revoke API key: ' + err.message);
        }
        setLoadingApiKey(false);
    };

    const revealApiKey = async () => {
        if (!revealPassword) return;
        setLoadingApiKey(true);
        setRevealError(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/api-key/reveal`, {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: revealPassword })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setRevealedKey(data.apiKey);
                setRevealPassword('');
            } else {
                setRevealError(data.error || 'Failed to reveal API key');
            }
        } catch (err) {
            setRevealError('Failed to reveal API key: ' + err.message);
        }
        setLoadingApiKey(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setApiKeyCopied(true);
            setTimeout(() => setApiKeyCopied(false), 3000);
        });
    };

    // Fetch stats on mount
    useEffect(() => {
        fetchStats();
        fetchProducts();
        fetchBulkOperations();
        fetchApiKeyInfo();
        fetchMigrationStatus();
        fetchAnnouncements();
        fetchFeedbackUnseenCount();
        fetch(`${API_URL}/bulk-limit`).then(r => r.json()).then(d => {
            if (d.maxUsersPerOp > 0) setBulkUserLimit(d.maxUsersPerOp);
        }).catch(() => {});
    }, []);

    // activeTab initializes from the URL hash (e.g. a bookmark, a shared link,
    // or simply reloading the page while on a non-dashboard tab), but the
    // fetch for that tab's data normally only happens inside its nav item's
    // onClick handler — which never fires when you land on the tab this way
    // instead of clicking into it. Without this, e.g. loading directly into
    // #users left the list stuck at its empty initial state until the user
    // clicked away to another tab and back (which does fire the nav onClick).
    // Mirror those same fetches once here for whichever tab we started on.
    useEffect(() => {
        switch (activeTab) {
            case 'users': fetchUsers(); break;
            case 'transactions': fetchTransactions(); break;
            case 'operations': fetchMigrationHistory(); break; // fetchBulkOperations already runs above
            case 'backupKeys': fetchBackupKeyInventory(); break;
            case 'shop': fetchMyMarketplaceOrders(); break;
            default: break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poll migration status while there's an active migration
    useEffect(() => {
        let migrationPoll = null;
        if (migrationStatus?.inMigration) {
            migrationPoll = setInterval(() => {
                fetchMigrationStatus();
            }, 5000); // Poll every 5 seconds
        }
        return () => {
            if (migrationPoll) clearInterval(migrationPoll);
        };
    }, [migrationStatus?.inMigration]);

    // Auto-poll operations when on Operations tab or when operations are in progress
    useEffect(() => {
        let pollInterval = null;

        const hasInProgressOps = bulkOperations.some(op => op.status === 'in_progress');
        const hasActiveWork = hasInProgressOps || bulkImportProgress || bulkDeleteProgress;

        if (hasActiveWork || activeTab === 'operations') {
            // Poll fast when work is actively running, slow when just viewing the tab
            const interval = hasActiveWork ? 5000 : 20000;
            pollInterval = setInterval(() => {
                fetchBulkOperations(true);
            }, interval);
        }

        return () => {
            if (pollInterval) clearInterval(pollInterval);
        };
    }, [activeTab, bulkOperations, bulkImportProgress, bulkDeleteProgress]);

    // Refetch products when switching to Activate User, Bulk Import, Users or
    // Bulk Extend. Ensures CP resellers see updated product list after admin
    // changes org access (e.g. removes a license) — and, because the per-team
    // K12 flag rides on this payload, keeps extend/bulk-extend pricing in sync
    // when an admin flags a team mid-session.
    useEffect(() => {
        if (activeTab === 'assign' || activeTab === 'bulkImport' || activeTab === 'users' || activeTab === 'bulkExtend') {
            fetchProducts();
        }
    }, [activeTab]);

    useEffect(() => {
        if (assignMode === 'premade' && stats.backupKeys && !stats.backupKeys.enabled) {
            setAssignMode('email');
        }
    }, [assignMode, stats.backupKeys]);

    // Detect marketplace availability on mount so the Shop nav item shows
    useEffect(() => {
        fetchMarketplace();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Dashboard appearance config (fails safe to the classic dashboard).
    useEffect(() => {
        fetch(`${API_URL}/credits-pool/dashboard-config`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data && data.success) {
                    setDashboardConfig({
                        dashboardV2Enabled: !!data.dashboardV2Enabled,
                        bannerEnabled: !!data.bannerEnabled,
                        banners: Array.isArray(data.banners) ? data.banners : []
                    });
                }
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (shopCategoryFilter === 'all' || shopCategoryFilter === NEW_FILTER) return;
        if (!marketplaceCategories.some(category => category.name === shopCategoryFilter)) {
            setShopCategoryFilter('all');
        }
    }, [marketplaceCategories, shopCategoryFilter]);

    // Keep URL hash in sync with active tab so refresh restores position
    useEffect(() => {
        window.history.replaceState(null, '', window.location.pathname + '#' + activeTab);
    }, [activeTab]);

    // Reset the transfer's destination team to the one the user is already on
    // each time the modal opens
    useEffect(() => {
        setTransferTargetOrg(transferringUser ? String(transferringUser.organizationId ?? '') : '');
    }, [transferringUser]);

    // Initialize product mapping when the transfer modal opens, and re-map
    // whenever the destination team changes — a different team means a
    // different set of products the reseller may assign.
    useEffect(() => {
        if (!transferringUser) {
            setTransferProductMapping([]);
            setTransferRemovedIndices(new Set());
            return;
        }
        let allowedProducts = getAllowedProductsForOrg(transferTargetOrg || transferringUser.organizationId);
        // Fallback: use all products from all accessible orgs
        if (allowedProducts.length === 0) allowedProducts = getAllAllowedProducts();
        const userProducts = transferringUser.products || [];

        const computeAutoMatch = (p) => {
            if (allowedProducts.includes(p)) return p;
            const caseInsensitiveMatch = allowedProducts.find(ap => ap.toLowerCase() === p.toLowerCase());
            if (caseInsensitiveMatch) return caseInsensitiveMatch;
            const pLower = p.toLowerCase();
            // CCMTeamDelegates (Adobe technical name) = Creative Cloud Pro
            if (/ccmteamdelegates|creative cloud pro/i.test(pLower)) {
                const match = allowedProducts.find(ap => /ccmteamdelegates|creative cloud pro/i.test(ap.toLowerCase()));
                if (match) return match;
            }
            if (pLower.includes('acrobat')) return allowedProducts.find(ap => ap.toLowerCase().includes('acrobat'));
            if (/Default All Apps.*Edition/i.test(p) || /All Apps.*Edition/i.test(p)) {
                return allowedProducts.find(ap => /All Apps.*Edition/i.test(ap)) ||
                    allowedProducts.find(ap => /All Apps.*Configuration|Default All Apps|Creative Cloud All Apps/i.test(ap));
            }
            if (/Default All Apps/i.test(p) || p === 'Creative Cloud All Apps') {
                return allowedProducts.find(ap => /All Apps.*Configuration|Creative Cloud All Apps Configuration/i.test(ap));
            }
            if (/Creative Cloud All Apps$/i.test(p)) {
                return allowedProducts.find(ap => ap === p + ' Configuration' || /Creative Cloud All Apps Configuration/i.test(ap));
            }
            if (pLower.includes('all apps')) return allowedProducts.find(ap => ap.toLowerCase().includes('all apps'));
            if (allowedProducts.length === 1) {
                const fallback = allowedProducts[0];
                const fallbackLower = fallback.toLowerCase();
                if ((pLower.includes('acrobat') && fallbackLower.includes('acrobat')) ||
                    (pLower.includes('all apps') && fallbackLower.includes('all apps'))) return fallback;
            }
            return '';
        };

        setTransferProductMapping(userProducts.map(p => computeAutoMatch(p)));
        setTransferRemovedIndices(new Set()); // Reset removed when opening
    }, [transferringUser, transferTargetOrg, getAllowedProductsForOrg, getAllAllowedProducts]);

    // Update duration to first available option when hiddenMonths changes
    useEffect(() => {
        const availableOptions = getVisibleActivationDurationOptions(stats.hiddenMonths || []);
        const availableKeys = availableOptions.map(option => option.key);
        const availableMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
            .filter(m => !(stats.hiddenMonths || []).map(String).includes(String(m)));

        if (availableKeys.length > 0) {
            if (!availableKeys.map(String).includes(String(durationMonths))) {
                setDurationMonths(availableKeys[0]);
            }
            if (!availableKeys.map(String).includes(String(bulkDurationMonths))) setBulkDurationMonths(availableKeys[0]);
            if (!availableKeys.map(String).includes(String(extensionMonths))) setExtensionMonths(availableKeys[0]);
        }

        if (availableMonths.length > 0) {
            if (!availableMonths.includes(parseInt(backupKeyDuration))) setBackupKeyDuration(availableMonths[0]);
            if (!availableMonths.includes(parseInt(bulkExtendMonths))) setBulkExtendMonths(availableMonths[0]);
            if (!availableMonths.includes(parseInt(bulkNewDuration))) setBulkNewDuration(availableMonths[0]);
        }
    }, [stats.hiddenMonths, durationMonths, bulkDurationMonths, backupKeyDuration, bulkExtendMonths, extensionMonths, bulkNewDuration]);

    const fetchStats = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/stats`, { headers: authHeaders });
            const data = await res.json();
            if (res.ok && data.success) {
                setStats(data);
            } else {
                setError(data.error || 'Failed to fetch stats');
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
            setError('Failed to fetch stats: ' + err.message);
        }
    };

    // Redeem pending refund credits. Below the admin's threshold this credits
    // the balance instantly; above it, it raises a request for admin approval.
    // The server decides which; we just relay its message and refresh.
    const redeemRefundCredits = async () => {
        setRedeeming(true);
        setError(null);
        setRedeemNotice('');
        try {
            const res = await fetch(`${API_URL}/credits-pool/redeem-refund-credits`, { method: 'POST', headers: authHeaders });
            const data = await res.json();
            if (res.ok && data.success) {
                setRedeemNotice(data.message || 'Done.');
                await fetchStats();
            } else {
                setError(data.error || 'Could not redeem refund credits');
            }
        } catch (err) {
            setError('Could not redeem refund credits: ' + err.message);
        } finally {
            setRedeeming(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/products`, {
                method: 'POST',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                setOrganizationAccess(data.organizationAccess || []);
                if (data.organizationAccess?.length > 0) {
                    const firstOrg = data.organizationAccess[0];
                    setSelectedOrg(firstOrg);
                    // Auto-select all products of the first organization
                    setSelectedProducts(firstOrg.products || []);
                }
            }
        } catch (err) {
            console.error('Failed to fetch products:', err);
        }
    };

    // Check for organization migration status
    const fetchMigrationStatus = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/migration-status`, { headers: authHeaders });
            const data = await res.json();
            setMigrationStatus(data);
        } catch (err) {
            console.error('Failed to fetch migration status:', err);
        }
    };

    const fetchBulkOperations = async (silent = false) => {
        if (!silent) setLoadingOperations(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-operations`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                const ops = data.operations || [];
                setBulkOperations(ops);

                // Sync progress bars with server state (restores progress on refresh)
                const activeImport = ops.find(op => op.type === 'bulk_assign' && op.status === 'in_progress');
                if (activeImport) {
                    setBulkImportProgress({
                        totalUsers: activeImport.totalEmails,
                        processed: (activeImport.successCount || 0) + (activeImport.failCount || 0),
                        estimatedSeconds: Math.ceil(activeImport.totalEmails * 2),
                        startTime: new Date(activeImport.createdAt).getTime()
                    });
                } else if (bulkImportProgress && !ops.some(op => op.type === 'bulk_assign' && op.status === 'in_progress')) {
                    // Import finished while we were watching
                    setBulkImportProgress(null);
                    setLoading(false);
                    fetchStats(); // Refresh stats to show new credits
                    fetchUsers(); // <--- REFRESH USER LIST
                }

                const activeDelete = ops.find(op => op.type === 'bulk_delete' && op.status === 'in_progress');
                if (activeDelete) {
                    setBulkDeleteProgress({
                        totalUsers: activeDelete.totalEmails,
                        processed: (activeDelete.deletedCount || 0) + (activeDelete.notFoundCount || 0),
                        estimatedSeconds: Math.ceil(activeDelete.totalEmails * 1), // Faster delete
                        startTime: new Date(activeDelete.createdAt).getTime()
                    });
                } else if (bulkDeleteProgress && !ops.some(op => op.type === 'bulk_delete' && op.status === 'in_progress')) {
                    setBulkDeleteProgress(null);
                    setLoading(false);
                    fetchStats();
                    fetchUsers(); // <--- REFRESH USER LIST (for delete too)
                }
            }
        } catch (err) {
            console.error('Failed to fetch operations:', err);
        }
        if (!silent) setLoadingOperations(false);
    };

    // Fetch migration history for Operations tab
    const fetchMigrationHistory = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/migration-history`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setMigrationHistory(data.migrations || []);
            }
        } catch (err) {
            console.error('Failed to fetch migration history:', err);
        }
    };

    const applyUserProductUpdate = (email, products, displayProducts) => {
        if (!email || !Array.isArray(products) || products.length === 0) return;

        setUsers(prevUsers => prevUsers.map(user =>
            user.email.toLowerCase() === email.toLowerCase()
                ? {
                    ...user,
                    products,
                    displayProducts: Array.isArray(displayProducts) && displayProducts.length > 0
                        ? displayProducts
                        : products
                }
                : user
        ));
    };

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/users`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setUsers(data.users || []);
            }
        } catch (err) {
            setError('Failed to fetch users');
        }
        setLoadingUsers(false);
    };

    const fetchBackupKeyInventory = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/backup-keys/inventory`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setBackupKeyInventory(data);
            }
        } catch (err) {
            console.error('Failed to fetch backup key inventory');
        }
    };

    const handleRedeemBackupKey = async (e) => {
        e.preventDefault();
        // Client-side validation before hitting API
        const creditsNeeded = getBackupKeyCredits(stats, backupKeyDuration);
        if (stats.creditsBalance < creditsNeeded) {
            setError(`Insufficient credits. Need ${formatCredits(creditsNeeded)}, have ${formatCredits(stats.creditsBalance)}`);
            return;
        }
        if (stats.isPoolExpired) {
            setError('Your credits pool has expired. Contact admin.');
            return;
        }
        const availableStock = backupKeyInventory?.availableByDuration?.[backupKeyDuration] || 0;
        if (availableStock === 0) {
            setError(`No backup keys available for ${backupKeyDuration} month(s).`);
            return;
        }
        setLoadingBackupKey(true);
        setError(null);
        setResult(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/redeem-backup-key`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ durationMonths: backupKeyDuration })
            });
            const data = await res.json();
            if (!res.ok || data.error) {
                setError(data.error || 'Failed to redeem key');
            } else {
                setLastAssignedBackupKey(data.key);
                setShowPurchasedKeyModal(true);
                setResult(data.message);
                fetchStats();
                fetchBackupKeyInventory();
            }
        } catch (err) {
            setError('Network error');
        }
        setLoadingBackupKey(false);
    };

    const fetchTransactions = async () => {
        setLoadingTransactions(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/transactions`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setTransactions(data.transactions || []);
            }
        } catch (err) {
            setError('Failed to fetch transactions');
        }
        setLoadingTransactions(false);
    };

    const fetchMyFeedback = async () => {
        setLoadingFeedback(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/feedback`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setMyFeedback(data.feedback || []);
                const hasUnseen = (data.feedback || []).some(fb => fb.adminNote && !fb.resellerSeenAt);
                if (hasUnseen) {
                    fetch(`${API_URL}/credits-pool/feedback/mark-seen`, { method: 'PATCH', headers: authHeaders })
                        .then(() => {
                            const seenAt = new Date().toISOString();
                            setMyFeedback(prev => prev.map(fb => (fb.adminNote && !fb.resellerSeenAt) ? { ...fb, resellerSeenAt: seenAt } : fb));
                        })
                        .catch(err => console.error('Failed to mark feedback as seen:', err.message));
                }
            }
        } catch (err) {
            console.error('Failed to fetch feedback:', err);
        }
        setLoadingFeedback(false);
    };

    // Read-only count for the nav badge — does NOT mark anything seen, so the
    // badge persists until the reseller actually opens the Feedback tab.
    const fetchFeedbackUnseenCount = async () => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/feedback`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setUnseenFeedbackCount((data.feedback || []).filter(fb => fb.adminNote && !fb.resellerSeenAt).length);
            }
        } catch (err) {
            console.error('Failed to fetch feedback unseen count:', err.message);
        }
    };

    const handleSubmitFeedback = async (e) => {
        e.preventDefault();
        if (!feedbackMessage.trim()) return;
        setSubmittingFeedback(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/feedback`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ type: feedbackType, message: feedbackMessage.trim() })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setFeedbackMessage('');
                setResult(data.message || 'Feedback submitted. Thank you!');
                fetchMyFeedback();
            } else {
                setError(data.error || 'Failed to submit feedback');
            }
        } catch (err) {
            setError('Failed to submit feedback: ' + err.message);
        }
        setSubmittingFeedback(false);
    };

    const fetchBranding = async () => {
        setLoadingBranding(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/branding`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setBranding(data.branding || {});
                const canEditNotice = data.canEditCustomerStatusNotice === true || canEditCustomerStatusNotice;
                setBrandingForm({
                    title: data.branding?.title || '',
                    subtitle: data.branding?.subtitle || '',
                    brandColor: data.branding?.brandColor || '',
                    notice: canEditNotice ? (data.branding?.notice || '') : '',
                    noticeRules: normalizeNoticeRulesForForm(data.branding || {}, canEditNotice)
                });
            }
        } catch (err) {
            console.error('Failed to fetch branding:', err);
        }
        setLoadingBranding(false);
    };

    const handleSaveBranding = async (e) => {
        e.preventDefault();
        setSavingBranding(true);
        setError(null);
        try {
            const noticeRules = (brandingForm.noticeRules || [])
                .map(rule => ({
                    id: rule.id,
                    scope: rule.scope === 'organizations' ? 'organizations' : 'all',
                    organizationIds: rule.scope === 'organizations'
                        ? [...new Set((rule.organizationIds || []).map(Number).filter(id => Number.isFinite(id) && id > 0))]
                        : [],
                    message: String(rule.message || '').trim()
                }))
                .filter(rule => rule.message && (rule.scope === 'all' || rule.organizationIds.length > 0));
            const res = await fetch(`${API_URL}/credits-pool/branding`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(canEditCustomerStatusNotice
                    ? { ...brandingForm, noticeRules, notice: noticeRules.find(rule => rule.scope === 'all')?.message || '' }
                    : {
                        title: brandingForm.title,
                        subtitle: brandingForm.subtitle,
                        brandColor: brandingForm.brandColor
                    })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setBranding(data.branding || {});
                setResult('Branding updated');
            } else {
                setError(data.error || 'Failed to update branding');
            }
        } catch (err) {
            setError('Failed to update branding: ' + err.message);
        }
        setSavingBranding(false);
    };

    const handleBrandingImageUpload = async (field, file) => {
        const endpoint = field === 'logo' ? 'logo' : 'org-icon';
        const formField = field === 'logo' ? 'logo' : 'orgIcon';
        const setUploading = field === 'logo' ? setUploadingLogo : setUploadingOrgIcon;

        setUploading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append(formField, file);
            const res = await fetch(`${API_URL}/credits-pool/branding/${endpoint}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setBranding(data.branding || {});
            } else {
                setError(data.error || 'Failed to upload image');
            }
        } catch (err) {
            setError('Failed to upload image: ' + err.message);
        }
        setUploading(false);
    };

    const handleRemoveBrandingImage = async (field) => {
        const endpoint = field === 'logo' ? 'logo' : 'org-icon';
        try {
            const res = await fetch(`${API_URL}/credits-pool/branding/${endpoint}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await res.json();
            if (res.ok && data.success) setBranding(data.branding || {});
        } catch (err) {
            console.error('Failed to remove image:', err);
        }
    };

    // Marketplace
    const fetchMarketplace = async () => {
        setLoadingMarketplace(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/marketplace/products`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setMarketplaceEnabled(!!data.enabled);
                const products = (data.products || []).map(product => ({
                    ...product,
                    category: inferMarketplaceCategory(product)
                }));
                setMarketplaceProducts(products);
                setSelectedRetentionByProduct(prev => {
                    const next = { ...prev };
                    products.forEach(product => {
                        const options = getMarketplaceOptions(product);
                        if (options.length > 0 && !options.some(option => option.month === parseInt(next[product.id], 10))) {
                            next[product.id] = options[0].month;
                        }
                    });
                    return next;
                });
                setSelectedMarketplaceProductId(prev => products.some(product => product.id === prev) ? prev : null);
            }
        } catch (err) {
            console.error('Failed to fetch marketplace');
        }
        setLoadingMarketplace(false);
    };

    const fetchMyMarketplaceOrders = async () => {
        setLoadingMyOrders(true);
        try {
            const res = await fetch(`${API_URL}/credits-pool/marketplace/orders`, { headers: authHeaders });
            const data = await res.json();
            if (data.success) {
                setMyMarketplaceOrders(data.orders || []);
            }
        } catch (err) {
            console.error('Failed to fetch my orders');
        }
        setLoadingMyOrders(false);
    };

    const handleBuyProduct = async (productId) => {
        const product = marketplaceProducts.find(p => p.id === productId);
        if (!product) return;
        const options = getMarketplaceOptions(product);
        const selectedRetention = parseInt(selectedRetentionByProduct[productId], 10);
        const selectedOption = options.find(option => option.month === selectedRetention) || options[0];
        if (!selectedOption) {
            setError('No purchase duration is configured for this product.');
            return;
        }
        const retention = selectedOption.month;
        const price = selectedOption.priceGoldCoins;
        // Cap at available stock rather than a fixed 10 — lets resellers bulk-buy
        // (e.g. to clear out an entire batch of mistakenly-priced keys).
        const qty = Math.max(1, Math.min(selectedOption.availableCount || 1, parseInt(shopQuantity) || 1));
        const totalPrice = price * qty;
        const buyerEmail = shopBuyerEmail.trim();

        if (product.requiresEmail && !buyerEmail) {
            setError('This product requires an email address for activation. Please enter it above.');
            return;
        }
        if (product.requiresEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail)) {
            setError('Please enter a valid email address.');
            return;
        }
        if ((Number(stats.goldCoinsBalance) || 0) < totalPrice) {
            setError(`Insufficient Gold Coins. Need ${formatCredits(totalPrice)}, have ${formatCredits(stats.goldCoinsBalance)}`);
            return;
        }
        if ((selectedOption.availableCount || 0) < qty) {
            setError(`Not enough stock. Requested ${qty}, available ${selectedOption.availableCount || 0}.`);
            return;
        }

        setBuyingProductId(productId);
        setError(null); setResult(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/marketplace/buy`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    productId,
                    retentionMonths: retention,
                    quantity: qty,
                    buyerEmail: buyerEmail || undefined
                })
            });
            const data = await res.json();
            if (!res.ok || data.error) {
                setError(data.error || 'Purchase failed');
            } else {
                setResult(data.message);
                setPurchasedOrders(data.orders || (data.order ? [data.order] : []));
                setPurchasedOrder(data.order);
                setShopQuantity(1);
                setShopBuyerEmail('');
                fetchStats();
                fetchMarketplace();
                fetchMyMarketplaceOrders();
            }
        } catch (err) {
            setError('Network error');
        }
        setBuyingProductId(null);
    };

    const handleAssignUser = async (e) => {
        e.preventDefault();
        if (stats.isPoolExpired) {
            setError('Your credits pool has expired. Contact admin.');
            return;
        }
        const creditsNeeded = getOrgActivationCredits(selectedOrg?.organizationId, durationMonths);
        if (stats.creditsBalance < creditsNeeded) {
            setError(`Insufficient credits. Need ${formatCredits(creditsNeeded)}, have ${formatCredits(stats.creditsBalance)}`);
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);
        setLastAssignedEmail(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/assign-user`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    email,
                    organizationId: selectedOrg?.organizationId,
                    products: selectedProducts,
                    durationMonths
                })
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                setError(data.error || 'Failed to assign user');
            } else {
                setResult(data.message);
                setLastAssignedEmail(email.toLowerCase().trim());
                setEmail('');
                fetchStats();
                fetchUsers();
            }
        } catch (err) {
            setError('Network error');
        }
        setLoading(false);
    };

    const handleRemoveUser = async (userEmail) => {
        const user = users.find(u => u.email === userEmail);
        const outcome = getDeleteOutcome(user);
        const message =
            outcome.kind === 'credits'
                ? (outcome.credits !== null
                ? `Delete ${userEmail}? ${outcome.credits} credit(s) will be refunded (inside the ${outcome.graceHours}h grace period).`
                : `Delete ${userEmail}? Credits will be refunded (inside the ${outcome.graceHours}h grace period).`)
                : outcome.kind === 'pending'
                    ? `Delete ${userEmail}? No instant credit refund. ${outcome.credits} refund credit(s) will be added to your pending balance for admin settlement.`
                    : outcome.kind === 'days'
                        ? `Delete ${userEmail}? No credit refund. ${outcome.days} refund day(s) will be added to your refund-day balance.`
                        : outcome.repeat
                            ? `Delete ${userEmail}? No refund — this email was already deleted inside the grace period and re-added within 30 days. Wait until ${user?.refundBlockHours || 2}h after activation and unused time is refunded.`
                            : `Delete ${userEmail}? No credit refund — nothing will be credited back.`;

        if (!window.confirm(message)) return;

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/remove-user`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ email: userEmail })
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                setError(data.error);
            } else {
                setResult(data.message);
                fetchStats();
                fetchUsers();
            }
        } catch (err) {
            setError('Network error');
        }
        setLoading(false);
    };

    const handleBulkDelete = async (e) => {
        e.preventDefault();
        const emails = bulkDeleteEmails.split(/[\n,;]+/).map(e => e.trim().toLowerCase()).filter(e => e && e.includes('@'));

        if (emails.length === 0) {
            setError('No valid emails found');
            return;
        }

        const deleteSummary = getDeleteSummary(emails);
        const confirmLines = [
            `Delete ${emails.length} users?`,
            ...buildDeleteSummaryLines(deleteSummary)
        ];
        if (deleteSummary.unknownUsers > 0) {
            confirmLines.push(`- ${deleteSummary.unknownUsers} additional email(s) will be checked by the server`);
        }

        if (!window.confirm(confirmLines.join('\n'))) {
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        // Set progress info for UI
        setBulkDeleteProgress({
            totalUsers: emails.length,
            startTime: Date.now()
        });

        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-delete`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ emails })
            });

            // Handle non-JSON responses (like HTML error pages)
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                if (res.status >= 500) {
                    setError('Server error during bulk delete. The operation may have partially completed. Check the Operations tab for status.');
                } else {
                    setError(`Unexpected server response (${res.status}). Check the Operations tab for status.`);
                }
                setResult('Check Operations tab for delete status');
                fetchBulkOperations();
                setBulkDeleteProgress(null);
                setLoading(false);
                return;
            }

            const data = await res.json();

            if (data.processing) {
                setResult('Bulk delete started! Check the Operations tab to monitor progress.');
                setBulkDeleteEmails('');
                fetchBulkOperations();
                setLoading(false);
                return;
            }

            if (!res.ok || data.error) {
                setError(data.error || 'Bulk delete failed');
            } else {
                setResult(data.message);
                setBulkDeleteEmails('');
                fetchStats();
                fetchUsers();
            }
        } catch (err) {
            console.error('Bulk delete error:', err);
            if (err.name === 'TypeError' || err.message?.includes('Failed to fetch') || err.message?.includes('Unexpected token')) {
                setError('Request timed out, connection lost, or server error. The deletion may still be processing. Check the Operations tab for actual status.');
                setResult('Check Operations tab for delete status');
            } else {
                setError(`Bulk delete error: ${err.message || 'Unknown error'}. Check the Operations tab for status.`);
            }
            fetchBulkOperations();
        }

        setBulkDeleteProgress(null);
        setLoading(false);
    };

    // Handle bulk extend
    const handleBulkExtend = async (e) => {
        e.preventDefault();
        const emails = bulkExtendEmails.split(/[\n,;]+/).map(e => e.trim().toLowerCase()).filter(e => e && e.includes('@'));

        if (emails.length === 0) {
            setError('No valid emails found');
            return;
        }

        const totalCredits = estimateBulkExtendCredits(emails, bulkExtendMonths);

        if (!window.confirm(`Extend ${emails.length} users by ${bulkExtendMonths} month(s)?\n\nCredits to be deducted: ${formatCredits(totalCredits)}`)) {
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-extend`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ emails, extensionMonths: bulkExtendMonths })
            });
            const data = await res.json();
            setLoading(false);

            if (res.ok) {
                setResult(`Extended ${data.extended} user(s) by ${bulkExtendMonths} month(s). ${data.creditsUsed} credits deducted.${data.notFound > 0 ? ` ${data.notFound} not found.` : ''}`);
                setBulkExtendEmails('');
                fetchStats();
                fetchUsers();
            } else {
                setError(data.error || 'Bulk extend failed');
            }
        } catch (err) {
            setLoading(false);
            setError(`Bulk extend failed: ${err.message}`);
        }
    };
    const handleLogout = () => {
        localStorage.removeItem('cpResellerToken');
        localStorage.removeItem('cpResellerInfo');
        onLogout();
    };

    // Export users to CSV
    const handleExportUsers = () => {
        if (users.length === 0) {
            setError('No users to export');
            return;
        }

        // Get organization names
        const orgMap = {};
        organizationAccess.forEach(org => {
            orgMap[org.organizationId] = org.name;
        });

        // Build CSV content
        const rows = users.map(user => {
            const expiryDate = user.expiresAt ? new Date(user.expiresAt).toLocaleDateString() : 'N/A';
            const products = Array.isArray(user.products) ? user.products.join('; ') : (user.products || 'N/A');
            const orgName = orgMap[user.organizationId] || user.organizationName || 'N/A';
            const status = user.expiresAt && new Date(user.expiresAt) < new Date() ? 'Expired' : 'Active';
            return [
                user.email || '',
                orgName,
                products,
                getDurationLabel(user.durationKey, user),
                user.expiresAt ? Math.ceil((new Date(user.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)) + ' days' : 'N/A',
                user.refundable ? 'Yes' : 'No',
                status
            ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
        });

        const headers = ['Email', 'Organization', 'Products', 'Duration', 'Days Left', 'Refundable', 'Status'];

        const csv = [headers.join(','), ...rows].join('\n');

        // Download file
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cp_users_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setResult(`Exported ${users.length} users to CSV`);
    };

    const handleTransferEmail = async (e) => {
        e.preventDefault();
        if (!transferringUser || !newEmailForTransfer) return;

        const userProducts = transferringUser.products || [];
        const targetOrgId = transferTargetOrg || transferringUser.organizationId;
        const isOrgChange = String(targetOrgId) !== String(transferringUser.organizationId);
        let allowedProducts = getAllowedProductsForOrg(targetOrgId);
        // Fallback: use all products from all accessible orgs
        if (allowedProducts.length === 0) allowedProducts = getAllAllowedProducts();

        // Filter to only included licenses (exclude removed ones)
        const includedIndices = userProducts.map((_, i) => i).filter(i => !transferRemovedIndices.has(i));

        // If user has products and we have allowed products for mapping, require all included to be mapped
        let productsToSend = null;
        if (userProducts.length > 0 && allowedProducts.length > 0) {
            productsToSend = includedIndices.map(i => transferProductMapping[i]).filter(Boolean);
            const allMapped = includedIndices.every(i => transferProductMapping[i]);
            if (!allMapped || includedIndices.length === 0) {
                setError('Please select a target product for each license (at least one license required)');
                return;
            }
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/transfer-email`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    oldEmail: transferringUser.email,
                    newEmail: newEmailForTransfer,
                    productMapping: productsToSend && productsToSend.length > 0 ? productsToSend : undefined,
                    targetOrganizationId: isOrgChange ? targetOrgId : undefined
                })
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                setError(data.error);
            } else {
                setResult(data.message);
                setTransferringUser(null);
                setNewEmailForTransfer('');
                setTransferProductMapping([]);
                setTransferRemovedIndices(new Set());
                fetchUsers();
            }
        } catch (err) {
            setError(err.message ? `Transfer failed: ${err.message}` : 'Network error');
        }
        setLoading(false);
    };

    const handleTransferOrg = async (e) => {
        e.preventDefault();
        if (!transferringOrgUser || !selectedTransferOrg) return;

        // Validate that user has selected products
        if (selectedTransferProducts.length === 0) {
            setError('Please select at least one product to assign');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/transfer-org`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    email: transferringOrgUser.email,
                    newOrganizationId: selectedTransferOrg,
                    selectedProducts: selectedTransferProducts // Send user-selected products
                })
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                setError(data.error);
            } else {
                setResult(data.message);
                setTransferringOrgUser(null);
                setSelectedTransferOrg('');
                setSelectedTransferProducts([]);
                fetchUsers();
            }
        } catch (err) {
            setError(err.message ? `Transfer failed: ${err.message}` : 'Network error');
        }
        setLoading(false);
    };

    const handleExtendUser = async (e) => {
        e.preventDefault();
        if (!extendingUser || !extensionMonths) return;

        const extensionCreditsNeeded = getOrgActivationCredits(extendingUser?.organizationId, extensionMonths, extendingUser?.k12);
        if (extensionCreditsNeeded > stats.creditsBalance) {
            setError(`Insufficient credits. Need ${formatCredits(extensionCreditsNeeded)}, have ${formatCredits(stats.creditsBalance)}`);
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/extend-user`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    email: extendingUser.email,
                    extensionMonths: extensionMonths
                })
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                setError(data.error);
            } else {
                setResult(data.message);
                setExtendingUser(null);
                setExtensionMonths(1);
                fetchStats();
                fetchUsers();
            }
        } catch (err) {
            setError('Network error');
        }
        setLoading(false);
    };

    // Handle bulk duration modification
    const handleBulkModifyDuration = async () => {
        if (selectedUsersForBulk.length === 0) {
            setError('No users selected');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            console.log('[BULK-MODIFY-CLIENT] Sending:', { userEmails: selectedUsersForBulk, newDurationMonths: bulkNewDuration, count: selectedUsersForBulk.length });
            const res = await fetch(`${API_URL}/credits-pool/bulk-modify-duration`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    userEmails: selectedUsersForBulk,
                    newDurationMonths: bulkNewDuration
                })
            });
            const data = await res.json();
            console.log('[BULK-MODIFY-CLIENT] Response:', data);

            if (!res.ok || data.error) {
                setError(data.error);
            } else {
                let msg = data.message;
                if (data.skipped > 0 && data.skippedDetails) {
                    const reasons = data.skippedDetails.map(s => `${s.email}: ${s.reason}`).join('\n');
                    console.log('[BULK-MODIFY-CLIENT] Skipped users:', data.skippedDetails);
                    msg += ` (${data.skipped} skipped)`;
                }
                setResult(msg);
                if (data.skipped > 0 && data.skippedDetails) {
                    setError(`${data.skipped} user(s) skipped: ${[...new Set(data.skippedDetails.map(s => s.reason))].join(', ')}`);
                }
                setShowBulkDurationModal(false);
                setSelectedUsersForBulk([]);
                setBulkNewDuration(1);
                fetchStats();
                fetchUsers();
            }
        } catch (err) {
            setError('Network error');
        }
        setLoading(false);
    };

    // Handle bulk delete of selected users
    const handleBulkDeleteSelected = async () => {
        if (selectedUsersForBulk.length === 0) {
            setError('No users selected');
            return;
        }

        const deleteSummary = getDeleteSummary(selectedUsersForBulk.map(email => email.toLowerCase()));
        const confirmLines = [
            `Delete ${selectedUsersForBulk.length} selected user(s)?`,
            ...buildDeleteSummaryLines(deleteSummary)
        ];

        if (!window.confirm(confirmLines.join('\n'))) {
            return;
        }
        // A large delete needs the number typed back: one OK on a misread count
        // removed 2,247 users from Adobe.
        const n = selectedUsersForBulk.length;
        if (n > 20) {
            const typed = window.prompt(`You are about to delete ${n} users. They will be removed from Adobe.\n\nType ${n} to confirm:`);
            if (typed === null) return;
            if (String(typed).trim() !== String(n)) {
                setError(`Not deleted: you typed "${typed}", not ${n}.`);
                return;
            }
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-delete`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    emails: selectedUsersForBulk
                })
            });
            const data = await res.json();

            if (data.processing) {
                setResult('Bulk delete started! Check the Operations tab to monitor progress.');
                setSelectedUsersForBulk([]);
                fetchBulkOperations();
                setLoading(false);
                return;
            }

            if (!res.ok || data.error) {
                setError(data.error);
            } else {
                setResult(data.message);
                setSelectedUsersForBulk([]);
                fetchStats();
                fetchUsers();
            }
        } catch (err) {
            setError('Network error');
        }
        setLoading(false);
    };

    // Handle single user reinvite - open modal with product mapping
    const handleReinviteUser = (user) => {
        if (!canUseCPReinvite) {
            setError('Reinvite is disabled for this account.');
            return;
        }

        let allowedProducts = getAllowedProductsForOrg(user.organizationId);
        // Fallback: use all products from all accessible orgs
        if (allowedProducts.length === 0) allowedProducts = getAllAllowedProducts();
        const userProducts = user.products || [];

        // Auto-compute product mapping
        let autoMapping;
        if (userProducts.length === 0 && allowedProducts.length > 0) {
            // No stored products - pre-select all allowed products so user can choose
            autoMapping = allowedProducts.map(() => '');
        } else {
            autoMapping = userProducts.map(p => {
                if (allowedProducts.includes(p)) return p;
                const ciMatch = allowedProducts.find(a => a.toLowerCase() === p.toLowerCase());
                if (ciMatch) return ciMatch;
                const pLower = p.toLowerCase();
                if (/ccmteamdelegates|creative cloud pro/i.test(pLower)) {
                    const match = allowedProducts.find(ap => /ccmteamdelegates|creative cloud pro/i.test(ap.toLowerCase()));
                    if (match) return match;
                }
                if (pLower.includes('acrobat')) return allowedProducts.find(a => a.toLowerCase().includes('acrobat')) || '';
                if (/Default All Apps.*Edition/i.test(p) || /All Apps.*Edition/i.test(p)) {
                    return allowedProducts.find(ap => /All Apps.*Edition/i.test(ap)) ||
                        allowedProducts.find(ap => /All Apps.*Configuration|Default All Apps|Creative Cloud All Apps/i.test(ap)) || '';
                }
                if (/Default All Apps/i.test(p) || p === 'Creative Cloud All Apps') {
                    return allowedProducts.find(ap => /All Apps.*Configuration|Creative Cloud All Apps Configuration/i.test(ap)) || '';
                }
                if (pLower.includes('all apps')) return allowedProducts.find(a => a.toLowerCase().includes('all apps')) || '';
                if (pLower.includes('creative cloud pro')) return allowedProducts.find(a => a.toLowerCase().includes('creative cloud pro')) || '';
                if (allowedProducts.length === 1) return allowedProducts[0];
                return '';
            });
        }

        setReinvitingUser(user);
        setReinviteProductMapping(autoMapping);
        setReinviteSelectedProducts([]);
    };

    // Confirm reinvite with product mapping
    const confirmReinvite = async () => {
        if (!reinvitingUser) return;
        if (!canUseCPReinvite) {
            setError('Reinvite is disabled for this account.');
            return;
        }

        let allowedProducts = getAllowedProductsForOrg(reinvitingUser.organizationId);
        // Fallback: use all products from all accessible orgs
        if (allowedProducts.length === 0) allowedProducts = getAllAllowedProducts();

        // Build product mapping to send
        let productsToSend = null;
        if (allowedProducts.length > 0) {
            const userProducts = reinvitingUser.products || [];
            if (userProducts.length === 0) {
                // No stored products - use selected products from checkboxes
                productsToSend = [...reinviteSelectedProducts];
            } else {
                // Has stored products - use mapped products from dropdowns
                productsToSend = reinviteProductMapping.filter(Boolean);
            }
            if (productsToSend.length === 0) {
                setError('Please select at least one product for the reinvite');
                return;
            }
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/reinvite-user`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    email: reinvitingUser.email,
                    productMapping: productsToSend && productsToSend.length > 0 ? productsToSend : undefined
                })
            });
            const data = await res.json();

            if (!res.ok || data.error) {
                setError(data.error || 'Reinvite failed');
            } else {
                applyUserProductUpdate(reinvitingUser.email, data.products, data.displayProducts);
                setResult(data.message || `Successfully reinvited ${reinvitingUser.email}`);
                setReinvitingUser(null);
                setReinviteProductMapping([]);
                setReinviteSelectedProducts([]);
                fetchUsers(); // Refresh user list to show updated products
            }
        } catch (err) {
            setError(err.message ? `Reinvite failed: ${err.message}` : 'Network error during reinvite');
        }
        setLoading(false);
    };

    // Open bulk reinvite config modal - groups users by org for product selection
    const handleBulkReinvite = () => {
        if (!canUseCPReinvite) {
            setError('Reinvite is disabled for this account.');
            return;
        }

        if (selectedUsersForBulk.length === 0) {
            setError('No users selected');
            return;
        }

        // Pre-populate org products: for each org, auto-select all its available products
        const orgProductDefaults = {};
        const orgUserProducts = {}; // Collect user products per org as fallback
        selectedUsersForBulk.forEach(email => {
            const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
            if (!user) return;
            const orgId = String(user.organizationId || 'unknown');
            if (!orgProductDefaults[orgId]) {
                const available = getAllowedProductsForOrg(orgId);
                orgProductDefaults[orgId] = [...available]; // auto-select all allowed products for this org
            }
            // Collect user products as fallback for orgs with no admin-configured products
            if (!orgUserProducts[orgId]) orgUserProducts[orgId] = [];
            (user.products || []).forEach(p => {
                if (!orgUserProducts[orgId].includes(p)) orgUserProducts[orgId].push(p);
            });
        });
        // For orgs with no admin-configured products, fall back to user products
        Object.keys(orgProductDefaults).forEach(orgId => {
            if (orgProductDefaults[orgId].length === 0 && orgUserProducts[orgId] && orgUserProducts[orgId].length > 0) {
                orgProductDefaults[orgId] = [...orgUserProducts[orgId]];
            }
        });

        setBulkReinviteOrgProducts(orgProductDefaults);
        setShowBulkReinviteConfig(true);
    };

    // Execute bulk reinvite with user-configured product mappings per org
    const executeBulkReinvite = async () => {
        if (!canUseCPReinvite) {
            setShowBulkReinviteConfig(false);
            setError('Reinvite is disabled for this account.');
            return;
        }

        setShowBulkReinviteConfig(false);
        setError(null);
        setResult(null);

        const emails = [...selectedUsersForBulk];
        const progress = { total: emails.length, current: 0, currentEmail: '', results: [] };
        setBulkReinviteProgress({ ...progress });

        let invited = 0, skipped = 0, failed = 0, productsAdded = 0, noProductChangeNeeded = 0;

        for (let i = 0; i < emails.length; i++) {
            const email = emails[i];
            progress.current = i + 1;
            progress.currentEmail = email;
            setBulkReinviteProgress({ ...progress });

            // Get product mapping from user's org selection
            const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
            let productsToSend = undefined;
            if (user) {
                const orgId = String(user.organizationId || 'unknown');
                const orgSelected = bulkReinviteOrgProducts[orgId];
                if (orgSelected && orgSelected.length > 0) {
                    productsToSend = orgSelected;
                }
            }

            try {
                const res = await fetch(`${API_URL}/credits-pool/reinvite-user`, {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({
                        email,
                        productMapping: productsToSend
                    })
                });
                const data = await res.json();

                if (res.ok && data.success) {
                    if (Array.isArray(data.products) && data.products.length > 0) {
                        applyUserProductUpdate(email, data.products, data.displayProducts);
                    }

                    if (data.alreadyExists) {
                        skipped++;
                        const skippedMessage = data.productGroupsAdded
                            ? 'Already in Adobe - products added'
                            : 'Already in Adobe - no product change needed';
                        if (data.productGroupsAdded) productsAdded++;
                        else noProductChangeNeeded++;
                        progress.results.push({
                            email,
                            status: 'skipped',
                            message: skippedMessage,
                            products: data.displayProducts || data.products || productsToSend || []
                        });
                    } else {
                        invited++;
                        progress.results.push({
                            email,
                            status: 'invited',
                            message: 'Invite sent',
                            products: data.displayProducts || data.products || productsToSend || []
                        });
                    }
                } else {
                    failed++;
                    progress.results.push({ email, status: 'failed', message: data.error || 'Failed' });
                }
            } catch (err) {
                failed++;
                progress.results.push({ email, status: 'failed', message: 'Network error' });
            }

            setBulkReinviteProgress({ ...progress, results: [...progress.results] });
        }

        // Done
        let msg = `Invited ${invited} user(s).`;
        if (skipped > 0) msg += ` ${skipped} already in Adobe.`;
        if (productsAdded > 0) msg += ` ${productsAdded} had products added.`;
        if (noProductChangeNeeded > 0) msg += ` ${noProductChangeNeeded} already had the requested products.`;
        if (failed > 0) msg += ` ${failed} failed.`;
        setResult(msg);
        if (failed > 0) {
            const failedItems = progress.results.filter(r => r.status === 'failed');
            setError('Failed: ' + failedItems.slice(0, 5).map(f => `${f.email}: ${f.message}`).join(', '));
        }
        setSelectedUsersForBulk([]);
        fetchUsers(); // Refresh user list to show updated products
    };

    const toggleUserSelection = (email) => {
        setSelectedUsersForBulk(prev =>
            prev.includes(email)
                ? prev.filter(e => e !== email)
                : [...prev, email]
        );
    };

    // The header checkbox selects THIS PAGE only. It used to select every
    // filtered user: on page 64 showing 2 users it selected all 3,152, and
    // "Delete Selected" deleted 2,247 of them (reseller #10, 2026-10-22).
    // Selecting everything is a separate, explicit link (selectEveryFilteredUser).
    const selectAllUsers = () => {
        const pageEmails = paginatedUsers.map(u => u.email);
        const allOnPage = pageEmails.length > 0 && pageEmails.every(e => selectedUsersForBulk.includes(e));
        setSelectedUsersForBulk(prev => allOnPage
            ? prev.filter(e => !pageEmails.includes(e))
            : [...new Set([...prev, ...pageEmails])]);
    };
    const selectEveryFilteredUser = () => setSelectedUsersForBulk(filteredUsers.map(u => u.email));

    // Calculate all unique organizations from users + organizationAccess
    // This ensures we can filter by organizations that might be deleted/defunct but still present on users
    const allUniqueOrgs = useMemo(() => {
        const orgs = [...organizationAccess];
        const orgMap = new Map();

        // Add active orgs
        orgs.forEach(o => orgMap.set(String(o.organizationId), o.organizationName));

        // Scan users for other orgs
        users.forEach(u => {
            if (u.organizationId && !orgMap.has(String(u.organizationId))) {
                orgMap.set(String(u.organizationId), u.organizationName || `Org ${u.organizationId}`);
            }
        });

        // Convert back to array
        return Array.from(orgMap.entries()).map(([id, name]) => ({
            organizationId: id,
            organizationName: name
        })).sort((a, b) => a.organizationName.localeCompare(b.organizationName));
    }, [users, organizationAccess]);

    const userMatchesOrgFilter = (u) => {
        if (selectedOrgFilter === 'All') return true;

        if (selectedOrgFilter === 'Unknown') {
            return !u.organizationId;
        }

        if (u.organizationId) {
            return String(u.organizationId) === String(selectedOrgFilter);
        }

        if (u.organizationName) {
            const orgName = allUniqueOrgs.find(o => String(o.organizationId) === String(selectedOrgFilter))?.organizationName;
            return orgName ? u.organizationName === orgName : false;
        }

        return false;
    };

    const availableProductFilters = (() => {
        const productMap = new Map();

        users
            .filter(userMatchesOrgFilter)
            .forEach(user => {
                const productNames = user.displayProducts || user.products || [];
                productNames.forEach(product => {
                    if (!product) return;
                    const key = String(product).toLowerCase();
                    if (!productMap.has(key)) {
                        productMap.set(key, product);
                    }
                });
            });

        return Array.from(productMap.values()).sort((a, b) => a.localeCompare(b));
    })();

    const availableDurationFilters = (() => {
        const durationMap = new Map();
        users
            .filter(userMatchesOrgFilter)
            .forEach(user => {
                const label = getDurationLabel(user.durationKey, user);
                if (!label) return;
                const key = String(label).toLowerCase();
                if (!durationMap.has(key)) {
                    durationMap.set(key, label);
                }
            });

        return Array.from(durationMap.values()).sort((a, b) => {
            const aNum = parseInt(a, 10);
            const bNum = parseInt(b, 10);
            if (Number.isFinite(aNum) && Number.isFinite(bNum) && a.includes('month') && b.includes('month')) {
                return aNum - bNum;
            }
            return a.localeCompare(b);
        });
    })();

    const filteredUsers = users.filter(u => {
        const matchesSearch = u.email.toLowerCase().includes(searchQuery.toLowerCase());
        if (!matchesSearch) return false;

        const now = new Date();
        const expiry = new Date(u.expiresAt);
        const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

        if (!userMatchesOrgFilter(u)) {
            return false;
        }

        if (selectedProductFilter !== 'All') {
            const productNames = u.displayProducts || u.products || [];
            const matchesProduct = productNames.some(product =>
                String(product).toLowerCase() === String(selectedProductFilter).toLowerCase()
            );
            if (!matchesProduct) return false;
        }

        if (selectedDurationFilter !== 'All') {
            const durationLabel = getDurationLabel(u.durationKey, u);
            if (String(durationLabel).toLowerCase() !== String(selectedDurationFilter).toLowerCase()) return false;
        }

        if (selectedTeamStatusFilter === 'active' && u.organizationStatus === 'inactive') return false;
        if (selectedTeamStatusFilter === 'inactive' && u.organizationStatus !== 'inactive') return false;

        if (daysLeftOperator && daysLeftValue !== '') {
            const targetDays = Number(daysLeftValue);
            if (!Number.isFinite(targetDays)) return false;
            if (daysLeftOperator === 'gt' && !(daysLeft > targetDays)) return false;
            if (daysLeftOperator === 'lt' && !(daysLeft < targetDays)) return false;
        }

        if (filterType === 'active') return daysLeft > 0;
        if (filterType === 'expired') return daysLeft <= 0;
        if (filterType === 'expiring_week') return daysLeft > 0 && daysLeft <= 7;
        if (filterType === 'expiring_today') return daysLeft > 0 && daysLeft <= 1;

        return true;
    });

    // Pagination
    const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * USERS_PER_PAGE,
        currentPage * USERS_PER_PAGE
    );

    // Reset invalid product filter when the org filter changes the available products
    useEffect(() => {
        if (selectedProductFilter !== 'All' && !availableProductFilters.includes(selectedProductFilter)) {
            setSelectedProductFilter('All');
        }
    }, [availableProductFilters, selectedProductFilter]);

    useEffect(() => {
        if (selectedDurationFilter !== 'All' && !availableDurationFilters.includes(selectedDurationFilter)) {
            setSelectedDurationFilter('All');
        }
    }, [availableDurationFilters, selectedDurationFilter]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedOrgFilter, selectedProductFilter, selectedDurationFilter, selectedTeamStatusFilter, daysLeftOperator, daysLeftValue, filterType]);

    // Reset transaction page when search changes
    useEffect(() => {
        setTxCurrentPage(1);
    }, [txSearchEmail]);

    // Handle bulk import from CSV
    const handleBulkImport = async (e) => {
        e.preventDefault();
        if (stats.isPoolExpired) {
            setError('Your credits pool has expired. Contact admin.');
            return;
        }
        if (!selectedOrg) {
            setError('Please select an organization first');
            return;
        }
        if (selectedProducts.length === 0) {
            setError('Please select at least one product');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        // Parse emails from CSV content
        const emails = bulkCsvContent
            .split(/[,\n\r]+/)
            .map(e => e.trim())
            .filter(e => e && e.includes('@'));

        if (emails.length === 0) {
            setError('No valid email addresses found');
            setLoading(false);
            return;
        }

        // Check credits for all users
        const bulkCreditsPerUser = getOrgActivationCredits(selectedOrg?.organizationId, bulkDurationMonths);
        const totalCreditsNeeded = roundCredits(emails.length * bulkCreditsPerUser);
        if (totalCreditsNeeded > stats.creditsBalance) {
            setError(`Not enough credits. Need ${formatCredits(totalCreditsNeeded)} (${emails.length} users × ${formatCredits(bulkCreditsPerUser)} credits), but you have ${formatCredits(stats.creditsBalance)}.`);
            setLoading(false);
            return;
        }

        // Set progress info for UI display (estimate ~2 seconds per user due to rate limiting)
        const estimatedSeconds = Math.ceil(emails.length * 2);
        setBulkImportProgress({
            totalUsers: emails.length,
            estimatedSeconds: estimatedSeconds,
            startTime: Date.now()
        });

        // Use optimized bulk-assign endpoint (10 users per API call, parallel processing)
        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-assign`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    emails: emails,
                    organizationId: selectedOrg?.organizationId,
                    products: selectedProducts,
                    durationMonths: bulkDurationMonths
                }),
            });
            const data = await res.json();

            if (data.processing) {
                setResult('Migration started! You can safely close this tab. Check the Operations tab to monitor progress, pause, or resume.');
                setBulkCsvContent('');
                fetchBulkOperations(); // Trigger immediate poll
                setLoading(false);
                return; // Return early to keep progress state active
            }

            if (!res.ok || data.error) {
                setError(data.error || 'Bulk import failed');
            } else {
                const { successCount, failCount, creditsUsed, failures } = data;
                if (failCount === 0) {
                    setResult(`Successfully activated ${successCount} users! (${creditsUsed} credits used)`);
                    setBulkCsvContent('');
                } else {
                    setResult(`Activated ${successCount} users. ${failCount} failed.`);
                    if (failures && failures.length > 0) {
                        setError('Failures:\n' + failures.slice(0, 5).map(f => `${f.email}: ${f.error}`).join('\n'));
                    }
                }
                fetchStats();
                fetchUsers();
            }
        } catch (err) {
            console.error('Bulk import error:', err);
            // Check if it's a timeout or network error - the operation may have completed on the server
            if (err.name === 'TypeError' || err.message?.includes('Failed to fetch')) {
                setError('Request timed out or lost connection. The import may still be processing on the server. Check the Operations tab for actual status.');
                setResult('Check Operations tab for import status');
            } else {
                setError(`Bulk import error: ${err.message || 'Unknown error'}. Check the Operations tab for status.`);
            }
            // Refresh operations list to show any completed operations
            fetchBulkOperations();
        }

        setBulkImportProgress(null);
        setLoading(false);
    };

    // Export failures to CSV
    const exportFailuresToCsv = (failures, operationType) => {
        const csvHeader = 'Email,Error\n';
        const csvRows = failures.map(f => `"${f.email}","${(f.error || 'Unknown error').replace(/"/g, '""')}"`).join('\n');
        const csvContent = csvHeader + csvRows;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${operationType}_failures_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Pause an in-progress operation
    const handlePauseOperation = async (operationId) => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-operations/${operationId}/pause`, {
                method: 'POST',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                setResult(data.message);
                fetchBulkOperations();
            } else {
                setError(data.error || 'Failed to pause operation');
            }
        } catch (err) {
            setError(`Pause error: ${err.message}`);
        }
    };

    // Resume a paused/failed operation
    const handleResumeOperation = async (operationId) => {
        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-operations/${operationId}/resume`, {
                method: 'POST',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                setResult(data.message);
                fetchBulkOperations();
            } else {
                setError(data.error || 'Failed to resume operation');
            }
        } catch (err) {
            setError(`Resume error: ${err.message}`);
        }
    };

    // Cancel an operation and download CSV report
    const handleCancelOperation = async (operationId, orgName) => {
        if (!window.confirm(`Are you sure you want to cancel this operation?\n\nA CSV report will be downloaded showing added and cancelled users.`)) {
            return;
        }

        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-operations/${operationId}/cancel`, {
                method: 'POST',
                headers: authHeaders
            });
            const data = await res.json();
            if (data.success) {
                setResult(data.message);
                fetchBulkOperations();

                // Download CSV report
                if (data.csvContent) {
                    const blob = new Blob([data.csvContent], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.setAttribute('href', url);
                    link.setAttribute('download', `operation_${operationId}_cancelled_${new Date().toISOString().slice(0, 10)}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }
            } else {
                setError(data.error || 'Failed to cancel operation');
            }
        } catch (err) {
            setError(`Cancel error: ${err.message}`);
        }
    };

    // Bulk Transfer Handler
    const handleBulkTransfer = async (e) => {
        e.preventDefault();

        if (!canUseCPBulkTransfer) {
            setError('Bulk transfer is disabled for this account.');
            return;
        }

        if (!selectedTransferOrgForBulk) {
            setError('Please select a target organization');
            return;
        }

        // Get actual products based on selection or default
        // If users selects nothing, we must rely on backend or force selection?
        // Let's force selection or default to all? 
        // The modal UI should handle selection arrays.

        let productsToSend = selectedTransferProductsForBulk;
        // If empty, user might expect defaults? 
        // Better to require selection if available products exist.
        if (bulkDerivedProducts.length > 0 && productsToSend.length === 0) {
            // If manual selection is empty, maybe default to first? 
            // Let's prompt user to be safe.
            // Actually, backend defaults to first allowed. But UI should be explicit.
            if (!window.confirm('No products selected. The first available product will be assigned by default. Continue?')) {
                return;
            }
        }

        const transferBehaviorMessage = stats.transferSourceOrgAction === 'keep'
            ? 'They will be added to the new organization and keep their current organization.'
            : 'They will be removed from their current organization and added to the new one.';

        if (!window.confirm(`Are you sure you want to transfer ${selectedUsersForBulk.length} users to the new organization?\n\n${transferBehaviorMessage}`)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${API_URL}/credits-pool/bulk-transfer-org`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    emails: selectedUsersForBulk,
                    newOrganizationId: selectedTransferOrgForBulk,
                    selectedProducts: productsToSend
                })
            });
            const data = await res.json();

            if (res.ok) {
                setResult('Bulk transfer started! Check Operations tab for progress.');
                setShowBulkTransferModal(false);
                setSelectedUsersForBulk([]);
                setSelectedTransferOrgForBulk('');
                setSelectedTransferProductsForBulk([]);
                fetchBulkOperations();
                fetchUsers();
            } else {
                setError(data.error || 'Bulk transfer failed');
            }
        } catch (err) {
            setError('Request failed: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const getPageTitle = () => {
        switch (activeTab) {
            case 'dashboard': return 'Credits Dashboard';
            case 'assign': return 'Activate User';
            case 'bulkImport': return 'Bulk Import';
            case 'users': return 'My Users';
            case 'transactions': return 'Transaction History';
            case 'bulkDelete': return 'Bulk Delete';
            case 'operations': return 'Bulk Operations';
            case 'orgFix': return 'Fix Organization Names';
            case 'apiDocs': return 'API Documentation';
            case 'about': return 'About';
            default: return 'Dashboard';
        }
    };

    // Bell icon in the top bar shows live progress for this reseller's own
    // background bulk operations (bulk-assign/delete/extend/transfer all run
    // async after an immediate 202 response). bulkOperations is already kept
    // fresh every 5s in the background by the polling effect above whenever
    // anything is in_progress, regardless of which tab is active, so this
    // just needs to render the existing state — no extra polling required.
    const inProgressOps = bulkOperations.filter(op => op.status === 'in_progress');

    return (
        <div className={`reseller-dashboard cp-dashboard-shell ${sidebarCollapsed ? 'cp-sidebar-collapsed' : ''}`}>
            {/* Sidebar */}
            <div className="sidebar cp-sidebar">
                <div className="sidebar-header cp-sidebar-header">
                    <h2>Credits Pool</h2>
                    <p className="cp-sidebar-label" style={{ fontSize: '12px', color: '#94a3b8' }}>{reseller.userGroup}</p>
                    <button
                        type="button"
                        className="cp-sidebar-toggle"
                        onClick={toggleSidebarCollapsed}
                        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {sidebarCollapsed ? '›' : '‹'}
                    </button>
                </div>
                {appVersion.version && (
                    <div className="cp-sidebar-version">
                        <span className="app-version-badge"
                            title={appVersion.startedAt ? `Server started ${new Date(appVersion.startedAt).toLocaleString()}` : undefined}>
                            v{appVersion.version}
                        </span>
                    </div>
                )}
                {/* Announcements */}
                {announcements.filter(a => isAnnouncementVisible(a.id)).length > 0 && (
                    <div style={{ margin: '0 12px 12px 12px' }}>
                        {announcements.filter(a => isAnnouncementVisible(a.id)).map(ann => (
                            <div key={ann.id} style={{
                                background: ann.type === 'warning' ? '#f59e0b' : ann.type === 'error' ? '#ef4444' : '#3b82f6',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '8px',
                                color: 'white',
                                position: 'relative',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span><Icon name="megaphone" size={16} /></span> {ann.title}
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
                                    {ann.message}
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); dismissAnnouncement(ann.id); }}
                                    style={{
                                        width: '100%',
                                        background: 'rgba(255,255,255,0.2)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: 'white',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        fontSize: '11px'
                                    }}
                                >
                                    Dismiss
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <nav className="sidebar-nav">
                    <div className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('dashboard'); fetchStats(); }}>
                        <span className="nav-icon"><Icon name="chart" /></span>
                        <span>Dashboard</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'assign' ? 'active' : ''}`}
                        onClick={() => setActiveTab('assign')}>
                        <span className="nav-icon"><Icon name="plus" /></span>
                        <span>Activate User</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'bulkImport' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bulkImport')}>
                        <span className="nav-icon"><Icon name="download" /></span>
                        <span>Bulk Import</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('users'); fetchUsers(); }}>
                        <span className="nav-icon"><Icon name="users" /></span>
                        <span>My Users</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'bulkDelete' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bulkDelete')}>
                        <span className="nav-icon"><Icon name="trash" /></span>
                        <span>Bulk Delete</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'bulkExtend' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bulkExtend')}>
                        <span className="nav-icon"><Icon name="clock" /></span>
                        <span>Bulk Extend</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'transactions' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('transactions'); fetchTransactions(); }}>
                        <span className="nav-icon"><Icon name="history" /></span>
                        <span>Transactions</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'operations' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('operations'); fetchBulkOperations(); fetchMigrationHistory(); }}>
                        <span className="nav-icon"><Icon name="clipboard" /></span>
                        <span>Operations</span>
                    </div>
                    {stats.backupKeys?.enabled && (
                        <div className={`nav-item ${activeTab === 'backupKeys' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('backupKeys'); fetchBackupKeyInventory(); }}>
                            <span className="nav-icon"><Icon name="key" /></span>
                            <span>My Keys</span>
                        </div>
                    )}
                    {marketplaceEnabled && (
                        <div className={`nav-item ${activeTab === 'shop' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('shop'); fetchMarketplace(); fetchMyMarketplaceOrders(); }}>
                            <span className="nav-icon"><Icon name="bag" /></span>
                            <span>Shop</span>
                        </div>
                    )}
                    {apiKeyInfo?.apiAccessEnabled && (
                        <div className={`nav-item ${activeTab === 'apiDocs' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('apiDocs'); fetchApiKeyInfo(); fetchApiUsage(); }}>
                            <span className="nav-icon"><Icon name="plug" /></span>
                            <span>API</span>
                        </div>
                    )}
                    <div className={`nav-item ${activeTab === 'feedback' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('feedback'); fetchMyFeedback(); setUnseenFeedbackCount(0); }}>
                        <span className="nav-icon" style={{ position: 'relative' }}>
                            <Icon name="message" />
                            {unseenFeedbackCount > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '-4px',
                                    right: '-8px',
                                    background: '#ef4444',
                                    color: 'white',
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    minWidth: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 3px'
                                }}>
                                    {unseenFeedbackCount}
                                </span>
                            )}
                        </span>
                        <span>Feedback</span>
                    </div>
                    <div className={`nav-item ${activeTab === 'branding' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('branding'); fetchBranding(); }}>
                        <span className="nav-icon"><Icon name="palette" /></span>
                        <span>Branding</span>
                    </div>

                    <div className={`nav-item ${activeTab === 'about' ? 'active' : ''}`}
                        onClick={() => setActiveTab('about')}>
                        <span className="nav-icon"><Icon name="info" /></span>
                        <span>About</span>
                    </div>

                    {/* Logout - visible in mobile nav */}
                    <div className="nav-item logout-item" onClick={handleLogout}>
                        <span className="nav-icon"><Icon name="logout" /></span>
                        <span>Logout</span>
                    </div>

                </nav>
            </div>

            {/* Main Content */}
            <div className="main-content cp-main-content">
                {/* Announcement Popup Modal */}
                {showAnnouncementPopup && announcements.filter(a => isAnnouncementVisible(a.id)).length > 0 && (
                    <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setShowAnnouncementPopup(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                            <div className="modal-header">
                                <h3 className="modal-title">New Announcements</h3>
                                <button className="modal-close" onClick={() => setShowAnnouncementPopup(false)}>×</button>
                            </div>
                            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                {announcements.filter(a => isAnnouncementVisible(a.id)).map(ann => (
                                    <div key={ann.id} style={{
                                        background: ann.type === 'alert' ? '#fee2e2' : '#f0f9ff',
                                        border: `1px solid ${ann.type === 'alert' ? '#fecaca' : '#bae6fd'}`,
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '12px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'flex-start' }}>
                                            <strong style={{ color: ann.type === 'alert' ? '#b91c1c' : '#0369a1', fontSize: '16px' }}>{ann.title}</strong>
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>{new Date(ann.createdAt).toLocaleDateString()}</span>
                                        </div>
                                        <p style={{ margin: '0 0 12px 0', whiteSpace: 'pre-wrap', color: '#334155', lineHeight: '1.5' }}>
                                            {ann.message}
                                        </p>
                                        <button
                                            onClick={() => dismissAnnouncement(ann.id)}
                                            style={{
                                                background: '#fff',
                                                border: '1px solid #cbd5e1',
                                                padding: '6px 12px',
                                                borderRadius: '4px',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                color: '#475569',
                                                width: '100%'
                                            }}
                                        >
                                            Mark as Read
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '16px', marginTop: '0', display: 'flex', justifyContent: 'flex-end' }}>
                                <button className="btn btn-primary" onClick={() => setShowAnnouncementPopup(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Purchased Pre-Made Key Modal */}
                {showPurchasedKeyModal && lastAssignedBackupKey && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                        onClick={() => setShowPurchasedKeyModal(false)}>
                        <div onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '500px', width: '90%', background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                            <h3 style={{ marginBottom: '8px', color: '#1d4ed8' }}>Pre-Made Account Key</h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                Copy your key now — it won't be shown again after you close this.
                            </p>
                            <div style={{ padding: '14px 18px', background: '#f0f9ff', border: '1px solid rgba(96, 165, 250, 0.55)', borderRadius: '8px', fontFamily: 'monospace', fontSize: '18px', fontWeight: '700', color: 'var(--app-text)', letterSpacing: '1px', wordBreak: 'break-all', userSelect: 'all', marginBottom: '12px' }}>
                                {lastAssignedBackupKey.code}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>
                                Duration: <strong>{lastAssignedBackupKey.durationMonths} {lastAssignedBackupKey.durationMonths === 1 ? 'month' : 'months'}</strong>
                                {lastAssignedBackupKey.redeemUrl && (
                                    <> · Redeem at: <a href={lastAssignedBackupKey.redeemUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--app-link)' }}>{lastAssignedBackupKey.redeemUrl}</a></>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => { navigator.clipboard.writeText(lastAssignedBackupKey.code); setResult('Key copied to clipboard!'); }}
                                    className="btn btn-primary" style={{ flex: 1 }}>
                                    Copy Key
                                </button>
                                <button onClick={() => setShowPurchasedKeyModal(false)} className="btn btn-secondary">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="top-bar">
                    <h2 className="page-title">{getPageTitle()}</h2>
                    <div className="user-info">
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setShowOpsNotifications(v => !v)}
                                title="Background operations"
                                style={{
                                    position: 'relative',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    padding: '4px',
                                    lineHeight: 1,
                                    color: 'inherit'
                                }}
                            >
                                {inProgressOps.length > 0 && (
                                    <span style={{
                                        position: 'absolute',
                                        top: '-2px',
                                        right: '-2px',
                                        background: '#ef4444',
                                        color: 'white',
                                        borderRadius: '999px',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        minWidth: '16px',
                                        height: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 3px'
                                    }}>
                                        {inProgressOps.length}
                                    </span>
                                )}
                            </button>
                            {showOpsNotifications && (
                                <>
                                    <div onClick={() => setShowOpsNotifications(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
                                    <div className="ops-notification-dropdown" style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 8px)',
                                        right: 0,
                                        width: 'min(340px, calc(100vw - 24px))',
                                        maxHeight: '420px',
                                        overflowY: 'auto',
                                        borderRadius: '10px',
                                        zIndex: 100,
                                        textAlign: 'left',
                                        boxSizing: 'border-box'
                                    }}>
                                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>Background Operations</span>
                                            <span style={{ color: '#64748b', fontWeight: 400, fontSize: '11px' }}>{inProgressOps.length} running</span>
                                        </div>
                                        {inProgressOps.length === 0 ? (
                                            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                                                No background operations running right now.
                                            </div>
                                        ) : (
                                            inProgressOps.map(op => {
                                                const done = op.type === 'bulk_delete' ? (op.deletedCount || 0) : (op.successCount || 0);
                                                const total = op.type === 'bulk_delete' ? (op.totalEmails || 0) : (op.emailsToProcess || op.totalEmails || 0);
                                                const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                                                return (
                                                    <div key={op.id} style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                            <span style={{ fontWeight: 600, fontSize: '12px' }}>
                                                                {op.type.replace(/_/g, ' ')} #{op.id}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{done}/{total}</span>
                                                        </div>
                                                        <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                                            <div style={{ width: `${pct}%`, height: '100%', background: '#f59e0b', transition: 'width 0.4s ease' }} />
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                                            {op.organizationName || ''}{op.failCount > 0 ? ` · ${op.failCount} failed` : ''}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                        <div style={{ padding: '8px 16px' }}>
                                            <button
                                                onClick={() => { setShowOpsNotifications(false); setActiveTab('operations'); fetchBulkOperations(); fetchMigrationHistory(); }}
                                                className="btn btn-secondary"
                                                style={{ width: '100%', fontSize: '12px' }}
                                            >
                                                View all operations
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <span>{reseller.username}</span>
                        <span>•</span>
                        <span className="license-badge" style={{ background: stats.isPoolExpired ? '#fef2f2' : '#ecfdf5', color: stats.isPoolExpired ? '#991b1b' : '#065f46' }}>
                            {formatCredits(stats.creditsBalance)} Credits
                        </span>
                        {marketplaceEnabled && (
                            <span className="license-badge" style={{ background: '#fffbeb', color: '#92400e' }}>
                                {formatCredits(stats.goldCoinsBalance)} GC
                            </span>
                        )}
                        {/* The admin can switch conversion off for every reseller (Settings > Bulk Operations). */}
                        {stats.creditToGcEnabled !== false && <CreditToGC creditsBalance={stats.creditsBalance} goldCoinsBalance={stats.goldCoinsBalance} token={token} onConverted={data => {
                            setStats(previous => ({ ...previous, creditsBalance: data.creditsBalance, goldCoinsBalance: data.goldCoinsBalance }));
                            fetchStats();
                            if (activeTab === 'transactions') fetchTransactions();
                        }} />}
                        <ThemeToggle compact />
                    </div>
                </div>

                <div className="content-wrapper">
                    {/* Alerts */}
                    {error && (
                        <div className="alert alert-error">
                            <span><Icon name="alert" size={16} /></span>
                            <div><strong>Error</strong><p style={{ marginTop: '4px', marginBottom: 0 }}>{error}</p></div>
                        </div>
                    )}
                    {result && (
                        <div className="alert alert-success">
                            <span><Icon name="check" size={16} /></span>
                            <div>
                                <strong>{result}</strong>
                                {lastAssignedEmail && (
                                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                                        <span
                                            style={{ cursor: 'pointer', color: '#3b82f6', textDecoration: 'underline' }}
                                            onClick={() => {
                                                const statusUrl = `${window.location.origin}/status/${encodeURIComponent(lastAssignedEmail)}`;
                                                navigator.clipboard.writeText(statusUrl).then(() => {
                                                    const el = document.getElementById('status-link-copied');
                                                    if (el) { el.textContent = 'Copied!'; setTimeout(() => { el.textContent = 'Copy status link'; }, 2000); }
                                                });
                                            }}
                                            id="status-link-copied"
                                        >Copy status link</span>
                                        <span style={{ marginLeft: '6px', color: '#9ca3af' }}>status/{lastAssignedEmail}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Backup Key Display after assignment */}
                    {lastAssignedBackupKey && (
                        <div style={{
                            padding: '16px 20px',
                            background: 'rgba(11, 125, 166, 0.10)',
                            borderRadius: '12px',
                            border: '1px solid rgba(96, 165, 250, 0.55)',
                            marginBottom: '16px',
                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.15)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <strong style={{ color: 'var(--app-link)', fontSize: '15px' }}>Pre-Made Account Key</strong>
                                    <p style={{ fontSize: '13px', color: 'var(--app-text-soft)', margin: '4px 0 8px' }}>
                                        Here is your pre-made account key. Copy it now — it won't be shown again.
                                    </p>
                                    <div style={{
                                        padding: '10px 16px',
                                        background: 'var(--surface-primary)',
                                        borderRadius: '8px',
                                        fontFamily: 'monospace',
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        color: 'var(--app-text)',
                                        letterSpacing: '1px',
                                        border: '1px solid rgba(96, 165, 250, 0.45)',
                                        display: 'inline-block',
                                        userSelect: 'all'
                                    }}>
                                        {lastAssignedBackupKey.code}
                                    </div>
                                    <div style={{ fontSize: '12px', color: 'var(--app-muted)', marginTop: '8px' }}>
                                        Duration: {lastAssignedBackupKey.durationMonths} month(s) · Redeem at: <a href={lastAssignedBackupKey.redeemUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--app-link)' }}>{lastAssignedBackupKey.redeemUrl}</a>
                                    </div>
                                </div>
                                <button onClick={() => {
                                    navigator.clipboard.writeText(lastAssignedBackupKey.code);
                                    setResult('Key copied to clipboard!');
                                }} className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    Copy
                                </button>
                            </div>
                            <button onClick={() => setLastAssignedBackupKey(null)} style={{ marginTop: '8px', background: 'none', border: 'none', color: '#94a3b8', fontSize: '12px', cursor: 'pointer' }}>
                                Dismiss
                            </button>
                        </div>
                    )}

                    {/* Pool Expired Warning */}
                    {stats.isPoolExpired && (
                        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                            <span><Icon name="alert" size={16} /></span>
                            <div>
                                <strong>Credits Pool Expired</strong>
                                <p style={{ marginTop: '4px', marginBottom: 0 }}>
                                    Your credits pool has expired. Contact your administrator to renew.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Organization Migration Banner */}
                    {migrationStatus?.inMigration && (
                        <div style={{
                            background: '#fdf6d8',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '20px',
                            border: '2px solid #f59e0b',
                            boxShadow: '0 4px 15px rgba(245, 158, 11, 0.2)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    width: '50px',
                                    height: '50px',
                                    background: '#f59e0b',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '24px',
                                    flexShrink: 0,
                                    animation: 'pulse 2s infinite'
                                }}><Icon name="clock" size={16} /></div>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: '0 0 6px 0', color: '#92400e', fontWeight: '700', fontSize: '16px' }}>
                                        Organization Migration in Progress
                                    </h4>
                                    <p style={{ margin: '0 0 10px 0', color: '#78350f', fontSize: '14px' }}>
                                        {migrationStatus.details || 'Users are being migrated to the new organization. Your users will be temporarily affected.'}
                                    </p>
                                    <div style={{
                                        height: '10px',
                                        background: 'var(--surface-primary)',
                                        borderRadius: '5px',
                                        overflow: 'hidden',
                                        marginBottom: '8px'
                                    }}>
                                        <div style={{
                                            width: `${migrationStatus.progress || 0}%`,
                                            height: '100%',
                                            background: '#ffa239',
                                            borderRadius: '5px',
                                            transition: 'width 1s ease'
                                        }} />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#78350f' }}>
                                        <span>{migrationStatus.migratedCount}/{migrationStatus.totalUsers} users migrated</span>
                                        <span>~{migrationStatus.estimatedMinutesLeft} min remaining</span>
                                    </div>
                                </div>
                            </div>
                            <p style={{ margin: '16px 0 0 0', fontSize: '12px', color: '#92400e', fontStyle: 'italic', textAlign: 'center' }}>
                                Your users may need to re-login to Adobe apps after migration completes.
                            </p>
                        </div>
                    )}

                    {/* Dashboard Tab */}
                    {activeTab === 'dashboard' && (
                        <>
                            {dashboardConfig.dashboardV2Enabled && dashboardConfig.bannerEnabled && dashboardConfig.banners.length > 0 && (
                                <CpBannerCarousel
                                    banners={dashboardConfig.banners}
                                    apiUrl={API_URL}
                                    onNavigate={({ tab, productId }) => {
                                        if (productId) openShopProduct(productId);
                                        else if (tab === 'shop') { setActiveTab('shop'); fetchMarketplace(); fetchMyMarketplaceOrders(); }
                                        else if (tab) setActiveTab(tab);
                                    }}
                                />
                            )}

                            <div className={`stats-grid${dashboardConfig.dashboardV2Enabled ? ' cp-stats-v2' : ''}`}>
                                <div className="stat-card">
                                    <div className="stat-label">Credits Balance</div>
                                    <div className="stat-value" style={{ color: stats.creditsBalance > 0 ? '#059669' : '#dc2626' }}>
                                        {formatCredits(stats.creditsBalance)}
                                    </div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#0b7da6' }}>
                                    <div className="stat-label">Credits Issued</div>
                                    <div className="stat-value">{formatCredits(stats.totalCreditsIssued)}</div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
                                    <div className="stat-label">Active Users</div>
                                    <div className="stat-value">{stats.totalActiveUsers}</div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
                                    <div className="stat-label">Pool Days Left</div>
                                    <div className="stat-value" style={{ color: stats.poolDaysLeft <= 30 ? '#dc2626' : 'inherit' }}>
                                        {stats.poolDaysLeft}
                                    </div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#ef4444' }}>
                                    <div className="stat-label">Expiring in 7 Days</div>
                                    <div className="stat-value">{stats.usersExpiringIn7Days || 0}</div>
                                </div>
                                <div
                                    className="stat-card clickable-stat"
                                    style={{ borderLeftColor: '#ef4444', cursor: 'pointer' }}
                                    onClick={() => { setActiveTab('users'); setFilterType('expiring_today'); }}
                                >
                                    <div className="stat-label">Expiring Today</div>
                                    <div className="stat-value" style={{ color: '#dc2626' }}>{stats.usersExpiringToday || 0}</div>
                                </div>
                                <div className="stat-card" style={{ borderLeftColor: '#f97316' }}>
                                    <div className="stat-label">Refund Days</div>
                                    <div className="stat-value" style={{ color: (stats.refundDaysBalance || 0) > 0 ? '#f97316' : 'inherit' }}>
                                        {stats.refundDaysBalance || 0}
                                    </div>
                                </div>
                                <div
                                    className="stat-card"
                                    style={{ borderLeftColor: '#0ea5e9' }}
                                    title="Unused credits returned from deleted users. Redeem them into your balance."
                                >
                                    <div className="stat-label">Pending Refund Credits</div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                                        <div className="stat-value" style={{ color: (stats.pendingRefundCredits || 0) > 0 ? '#0284c7' : 'inherit' }}>
                                            {formatCredits(stats.pendingRefundCredits || 0)}
                                        </div>
                                        {(stats.pendingRefundCredits || 0) > 0 && (
                                            stats.redemptionRequest ? (
                                                <span style={{ fontSize: '12px', color: '#b45309', fontWeight: 600 }}
                                                    title="An admin has been notified and will approve it.">Awaiting approval</span>
                                            ) : (
                                                <button type="button" onClick={redeemRefundCredits} disabled={redeeming}
                                                    title={(stats.selfRedeemThreshold > 0 && (stats.pendingRefundCredits || 0) <= stats.selfRedeemThreshold)
                                                        ? 'Add these credits to your balance now'
                                                        : 'Above the instant limit — this sends a request for admin approval'}
                                                    style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 600, fontSize: '13px', cursor: redeeming ? 'default' : 'pointer', textDecoration: 'underline', padding: 0 }}>
                                                    {redeeming ? '…' : 'exchange'}
                                                </button>
                                            )
                                        )}
                                    </div>
                                    {redeemNotice && (
                                        <div style={{ fontSize: '12px', color: '#047857', marginTop: '4px' }}>{redeemNotice}</div>
                                    )}
                                    {!redeemNotice && !stats.redemptionRequest && (stats.pendingRefundCredits || 0) > 0
                                        && stats.selfRedeemThreshold > 0 && (stats.pendingRefundCredits || 0) > stats.selfRedeemThreshold && (
                                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                                            Above the {formatCredits(stats.selfRedeemThreshold)} instant limit — needs admin approval.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {dashboardConfig.dashboardV2Enabled && marketplaceEnabled && onSaleProducts.length > 0 && (
                                <div className="card cp-onsale-section" style={{ marginBottom: '20px' }}>
                                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 className="card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="cp-onsale-flame" aria-hidden="true">🔥</span> On Sale Now
                                        </h3>
                                        <button onClick={() => { setActiveTab('shop'); fetchMarketplace(); fetchMyMarketplaceOrders(); }}
                                            className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                            Shop the Sale
                                        </button>
                                    </div>
                                    <div className="cp-onsale-grid">
                                        {onSaleProducts.slice(0, ONSALE_DASHBOARD_LIMIT).map(p => {
                                            const options = getMarketplaceOptions(p);
                                            const salePrices = options.map(o => o.priceGoldCoins).filter(Number.isFinite);
                                            const origPrices = options.map(o => o.originalPriceGoldCoins).filter(Number.isFinite);
                                            const minSale = salePrices.length ? Math.min(...salePrices) : null;
                                            const minOrig = origPrices.length ? Math.min(...origPrices) : null;
                                            const countdown = formatPromoCountdown(p.promo.endsAt);
                                            return (
                                                <div key={p.id}
                                                    role="button" tabIndex={0}
                                                    className="cp-onsale-item"
                                                    onClick={() => openShopProduct(p.id)}
                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openShopProduct(p.id); } }}>
                                                    {p.imageUrl ? (
                                                        <img src={productThumb(p, 320)} alt={p.name} className="cp-onsale-thumb" loading="lazy" decoding="async"
                                                            onError={e => onThumbError(e, p.name)} />
                                                    ) : (
                                                        <div className="cp-onsale-thumb cp-onsale-thumb-placeholder"><Icon name="bag" size={16} /></div>
                                                    )}
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', flexWrap: 'wrap' }}>
                                                            <span className="cp-promo-badge" style={p.promo.badgeColor ? { background: p.promo.badgeColor } : undefined}>
                                                                {p.promo.label} −{p.promo.discountPercent}%
                                                            </span>
                                                        </div>
                                                        <div style={{ fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.name}>{p.name}</div>
                                                        <div style={{ fontSize: '12px', display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                                                            {minOrig != null && minSale != null && minOrig > minSale && (
                                                                <span className="cp-price-original">{formatCredits(minOrig)} GC</span>
                                                            )}
                                                            <span className="cp-price-sale">{minSale == null ? '—' : `from ${formatCredits(minSale)} GC`}</span>
                                                        </div>
                                                        {countdown && <div className="cp-onsale-countdown">{countdown}</div>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {onSaleProducts.length > ONSALE_DASHBOARD_LIMIT && (
                                        <button
                                            type="button"
                                            onClick={() => { setActiveTab('shop'); fetchMarketplace(); fetchMyMarketplaceOrders(); }}
                                            className="btn btn-secondary"
                                            style={{ marginTop: '14px', width: '100%' }}>
                                            View all {onSaleProducts.length} deals on sale →
                                        </button>
                                    )}
                                </div>
                            )}

                            {marketplaceEnabled && newArrivalProducts.length > 0 && (
                                <div className="card" style={{ marginBottom: '20px' }}>
                                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 className="card-title" style={{ margin: 0 }}>
                                            <span className="shop-new-flag">NEW</span> Just Dropped
                                        </h3>
                                        <button onClick={() => { setActiveTab('shop'); setShopCategoryFilter(NEW_FILTER); fetchMarketplace(); fetchMyMarketplaceOrders(); }}
                                            className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                            See all
                                        </button>
                                    </div>
                                    <p style={{ margin: '0 0 18px', fontSize: '13px', color: 'var(--app-muted)' }}>
                                        Recently added to the shop.
                                    </p>
                                    <div className="bestseller-grid">
                                        {newArrivalProducts.slice(0, 5).map(p => {
                                            const opts = getMarketplaceOptions(p);
                                            const prices = opts.map(o => o.priceGoldCoins).filter(v => Number.isFinite(v));
                                            const min = prices.length ? Math.min(...prices) : null;
                                            const max = prices.length ? Math.max(...prices) : null;
                                            return (
                                                <div key={p.id} className="bestseller-card"
                                                    role="button" tabIndex={0} title={p.name}
                                                    onClick={() => openShopProduct(p.id)}
                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openShopProduct(p.id); } }}>
                                                    <div className="bestseller-card__media">
                                                        <span className="bestseller-card__rank bestseller-card__rank--new">NEW</span>
                                                        {p.imageUrl ? (
                                                            <img src={productThumb(p, 480)} alt={p.name} loading="lazy" decoding="async" onError={e => onThumbError(e, p.name)} />
                                                        ) : (
                                                            <Icon name="bag" size={30} />
                                                        )}
                                                    </div>
                                                    <div className="bestseller-card__body">
                                                        <div className="bestseller-card__name">{p.name}</div>
                                                        <div className="bestseller-card__price">
                                                            {min == null ? '\u2014'
                                                                : min === max ? `${formatCredits(min)} GC`
                                                                : `${formatCredits(min)}\u2013${formatCredits(max)} GC`}
                                                        </div>
                                                        <div className="bestseller-card__sold">
                                                            <Icon name="bolt" size={13} /> Just added
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {marketplaceEnabled && bestSellerProducts.length > 0 && (
                                <div className="card" style={{ marginBottom: '20px' }}>
                                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 className="card-title" style={{ margin: 0 }}>Best Sellers</h3>
                                        <button onClick={() => { setActiveTab('shop'); fetchMarketplace(); fetchMyMarketplaceOrders(); }}
                                            className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                            Go to Shop
                                        </button>
                                    </div>
                                    <p style={{ margin: '0 0 18px', fontSize: '13px', color: 'var(--app-muted)' }}>
                                        Popular products other resellers are buying right now.
                                    </p>
                                    <div className="bestseller-grid">
                                        {bestSellerProducts.map((p, idx) => {
                                            const topSold = bestSellerProducts[0]?.soldCount || 1;
                                            const barPct = Math.max(8, Math.round(((p.soldCount || 0) / topSold) * 100));
                                            const isSold = (p.soldCount || 0) > 0;
                                            const isTop = idx === 0 && isSold;
                                            return (
                                                <div key={p.id}
                                                    className={`bestseller-card${isTop ? ' bestseller-card--top' : ''}`}
                                                    role="button" tabIndex={0}
                                                    title={p.name}
                                                    onClick={() => openShopProduct(p.id)}
                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openShopProduct(p.id); } }}>
                                                    <div className="bestseller-card__media">
                                                        <span className={`bestseller-card__rank${isTop ? ' bestseller-card__rank--top' : ''}`}>
                                                            {isTop ? '★ #1' : `#${idx + 1}`}
                                                        </span>
                                                        {p.imageUrl ? (
                                                            <img src={productThumb(p, 480)} alt={p.name} loading="lazy" decoding="async"
                                                                onError={e => onThumbError(e, p.name)} />
                                                        ) : (
                                                            <Icon name="bag" size={30} />
                                                        )}
                                                    </div>
                                                    <div className="bestseller-card__body">
                                                        <div className="bestseller-card__name">{p.name}</div>
                                                        <div className="bestseller-card__price">
                                                            {p.minPrice == null ? '—'
                                                                : p.minPrice === p.maxPrice ? `${formatCredits(p.minPrice)} GC`
                                                                : `${formatCredits(p.minPrice)}–${formatCredits(p.maxPrice)} GC`}
                                                        </div>
                                                        {isSold ? (
                                                            <>
                                                                <div className="bestseller-card__bar"><span style={{ width: `${barPct}%` }} /></div>
                                                                <div className="bestseller-card__sold">
                                                                    <Icon name="chart" size={13} /> {(p.soldCount || 0).toLocaleString()} sold
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="bestseller-card__sold">
                                                                <Icon name="bolt" size={13} /> Popular pick
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="card" style={{ marginTop: '20px' }}>
                                <div className="card-header">
                                    <h3 className="card-title">Quick Actions</h3>
                                </div>
                                <div className="quick-actions">
                                    <button className="btn btn-primary" onClick={() => setActiveTab('assign')}
                                        disabled={stats.isPoolExpired || stats.creditsBalance === 0}>
                                        Activate New User
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => { setActiveTab('users'); fetchUsers(); }}>
                                        View My Users
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* About Tab — how the Credits Pool model works */}
                    {activeTab === 'about' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Credits Pool Model</h3>
                            </div>
                            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px' }}>
                                <p><strong>1 Credit = 1 User × 1 Month</strong></p>
                                <ul style={{ marginTop: '12px', marginLeft: '20px' }}>
                                    <li>User deletion inside the grace period → Credits refunded in full</li>
                                    <li>User deletion after it → Unused value returns as pro-rata refund credits or refund days, per your administrator's settings</li>
                                    <li>Redeemed refund days can be cleared after customer-service settlement</li>
                                    <li>Credits expire with pool validity</li>
                                    <li>Active users remain valid until their expiration</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Assign User Tab */}
                    {activeTab === 'assign' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Activate New User</h3>
                            </div>

                            {stats.creditsBalance === 0 ? (
                                <div className="alert alert-error">
                                    <span><Icon name="alert" size={16} /></span>
                                    <div>No credits available. Contact admin to add more credits.</div>
                                </div>
                            ) : (
                                <>
                                    {/* Mode Toggle */}
                                    <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                        <button
                                            onClick={() => setAssignMode('email')}
                                            style={{
                                                flex: 1, padding: '14px 16px', border: 'none', cursor: 'pointer',
                                                background: assignMode === 'email' ? '#1d4ed8' : '#f8fafc',
                                                color: assignMode === 'email' ? '#fff' : '#64748b',
                                                fontWeight: '600', fontSize: '14px', transition: 'all 0.2s'
                                            }}>
                                            Email Activation
                                        </button>
                                        {stats.backupKeys?.enabled && (
                                            <button
                                                onClick={() => setAssignMode('premade')}
                                                style={{
                                                    flex: 1, padding: '14px 16px', border: 'none', cursor: 'pointer',
                                                    borderLeft: '1px solid #e2e8f0',
                                                    background: assignMode === 'premade' ? '#1d4ed8' : 'var(--app-surface-alt)',
                                                    color: assignMode === 'premade' ? '#fff' : 'var(--app-muted)',
                                                    fontWeight: '600', fontSize: '14px', transition: 'all 0.2s'
                                                }}>
                                                Get Pre-Made Key
                                            </button>
                                        )}
                                    </div>

                                    {/* Email Activation Mode */}
                                    {assignMode === 'email' && (
                                        <form onSubmit={handleAssignUser}>
                                            <div className="form-group">
                                                <label className="form-label">Organization</label>
                                                <select className="form-input" value={selectedOrg?.organizationId || ''}
                                                    onChange={(e) => {
                                                        const org = organizationAccess.find(o => o.organizationId === parseInt(e.target.value));
                                                        setSelectedOrg(org);
                                                        setSelectedProducts(org?.products || []);
                                                    }}>
                                                    {organizationAccess.map(org => (
                                                        <option key={org.organizationId} value={org.organizationId} disabled={orgIsFull(org)}>
                                                            {org.organizationName}{orgCapacityLabel(org) ? ` \u2014 ${orgCapacityLabel(org)}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {selectedOrg && orgCapacityLabel(selectedOrg) && (
                                                <div className={`cp-capacity-note${orgIsFull(selectedOrg) ? ' is-full' : ''}`}>
                                                    {orgIsFull(selectedOrg)
                                                        ? 'This team is full \u2014 no licences left. Choose another team.'
                                                        : `${selectedOrg.remainingCapacity} licence(s) available in this team.`}
                                                    {selectedOrg.userLimit ? ` (limit ${selectedOrg.userLimit}, in use ${selectedOrg.activeUsers})` : ''}
                                                </div>
                                            )}

                                            {selectedOrg && (
                                                <div className="form-group">
                                                    <label className="form-label">Products</label>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        {assignableProducts(selectedOrg.products).map((product, idx) => (
                                                            <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: selectedProducts.includes(product) ? '#dbeafe' : '#f8fafc', borderRadius: '6px', cursor: 'pointer' }}>
                                                                <input type="checkbox" checked={selectedProducts.includes(product)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) setSelectedProducts([...selectedProducts, product]);
                                                                        else setSelectedProducts(selectedProducts.filter(p => p !== product));
                                                                    }} />
                                                                {productLabelFor(selectedOrg, product)}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="form-group">
                                                <label className="form-label">Duration (Credits to Use)</label>
                                                <select className="form-input" value={durationMonths}
                                                    onChange={(e) => setDurationMonths(e.target.value.match(/^\d+$/) ? parseInt(e.target.value, 10) : e.target.value)}>
                                                    {getVisibleActivationDurationOptions(stats.hiddenMonths || [])
                                                        .map(option => {
                                                            const credits = getOrgActivationCredits(selectedOrg?.organizationId, option.key);
                                                            return (
                                                                <option key={option.key} value={option.key} disabled={credits > stats.creditsBalance}>
                                                                    {option.label} ({formatCredits(credits)} credits)
                                                                    {credits > stats.creditsBalance ? ' - Insufficient credits' : ''}
                                                                </option>
                                                            );
                                                        })}
                                                </select>
                                                {isOrgK12(selectedOrg?.organizationId) && (
                                                    <small style={{ color: 'var(--app-link)', fontWeight: 600 }}>
                                                        K12 team — flat {formatCredits(getOrgActivationCredits(selectedOrg?.organizationId, durationMonths))} credit(s) for any duration.
                                                    </small>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Email Address</label>
                                                <input type="email" className="form-input" value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="user@example.com" required />
                                            </div>

                                            <div style={{ padding: '12px', background: 'var(--app-success-bg)', borderRadius: '6px', marginBottom: '16px', border: '1px solid rgba(27, 138, 90, 0.35)' }}>
                                                <strong style={{ color: 'var(--app-text)' }}>
                                                    {formatCredits(getOrgActivationCredits(selectedOrg?.organizationId, durationMonths))} credit(s) will be deducted
                                                </strong>
                                                <p style={{ fontSize: '12px', color: 'var(--app-text-soft)', marginTop: '4px' }}>
                                                    Balance after: {formatCredits(stats.creditsBalance - getOrgActivationCredits(selectedOrg?.organizationId, durationMonths))} credits
                                                </p>
                                            </div>

                                            <button type="submit" disabled={loading || selectedProducts.length === 0 || !email}
                                                className="btn btn-primary" style={{ width: '100%' }}>
                                                {loading ? 'Activating...' : 'Activate User'}
                                            </button>
                                        </form>
                                    )}

                                    {/* Pre-Made Key Mode */}
                                    {assignMode === 'premade' && stats.backupKeys?.enabled && (
                                        <RedeemBuy token={token} onCreditsChanged={fetchStats} embedded />
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Bulk Import Tab */}
                    {activeTab === 'bulkImport' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Bulk Import Users</h3>
                                {bulkUserLimit > 0 && (
                                    <span style={{ fontSize: '13px', color: '#f97316', fontWeight: 600, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', padding: '4px 10px' }}>
                                        Maximum Users per Bulk Operation: {bulkUserLimit}
                                    </span>
                                )}
                            </div>

                            {stats.creditsBalance === 0 ? (
                                <div className="alert alert-error">
                                    <span><Icon name="alert" size={16} /></span>
                                    <div>No credits available. Contact admin to add more credits.</div>
                                </div>
                            ) : (
                                <form onSubmit={handleBulkImport}>
                                    <div className="form-group">
                                        <label className="form-label">Organization</label>
                                        <select className="form-input" value={selectedOrg?.organizationId || ''}
                                            onChange={(e) => {
                                                const org = organizationAccess.find(o => o.organizationId === parseInt(e.target.value));
                                                setSelectedOrg(org);
                                                // Auto-select all products when org changes
                                                setSelectedProducts(org?.products || []);
                                            }}>
                                            {organizationAccess.map(org => (
                                                <option key={org.organizationId} value={org.organizationId} disabled={orgIsFull(org)}>
                                                    {org.organizationName}{orgCapacityLabel(org) ? ` \u2014 ${orgCapacityLabel(org)}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedOrg && orgCapacityLabel(selectedOrg) && (
                                        <div className={`cp-capacity-note${orgIsFull(selectedOrg) ? ' is-full' : ''}`}>
                                            {orgIsFull(selectedOrg)
                                                ? 'This team is full \u2014 no licences left. Choose another team.'
                                                : `${selectedOrg.remainingCapacity} licence(s) available in this team.`}
                                            {selectedOrg.userLimit ? ` (limit ${selectedOrg.userLimit}, in use ${selectedOrg.activeUsers})` : ''}
                                        </div>
                                    )}

                                    {selectedOrg && (
                                        <div className="form-group">
                                            <label className="form-label">Products</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {assignableProducts(selectedOrg.products).map((product, idx) => (
                                                    <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: selectedProducts.includes(product) ? '#dbeafe' : '#f8fafc', borderRadius: '6px', cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={selectedProducts.includes(product)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) setSelectedProducts([...selectedProducts, product]);
                                                                else setSelectedProducts(selectedProducts.filter(p => p !== product));
                                                            }} />
                                                        {product}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label className="form-label">Duration per User (Credits)</label>
                                        <select className="form-input" value={bulkDurationMonths}
                                            onChange={(e) => setBulkDurationMonths(e.target.value.match(/^\d+$/) ? parseInt(e.target.value, 10) : e.target.value)}>
                                            {getVisibleActivationDurationOptions(stats.hiddenMonths || [])
                                                .map(option => {
                                                    const credits = getOrgActivationCredits(selectedOrg?.organizationId, option.key);
                                                    return (
                                                        <option key={option.key} value={option.key}>
                                                            {option.label} ({formatCredits(credits)} credits per user)
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Email Addresses (one per line or comma-separated)</label>
                                        <textarea className="form-input" rows={8} value={bulkCsvContent}
                                            onChange={(e) => setBulkCsvContent(e.target.value)}
                                            placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                                            style={{ fontFamily: 'monospace' }} required />
                                    </div>

                                    {bulkCsvContent && (
                                        <div style={{ padding: '12px', background: 'var(--app-success-bg)', borderRadius: '6px', marginBottom: '16px', border: '1px solid rgba(27, 138, 90, 0.35)' }}>
                                            <strong style={{ color: 'var(--app-success-text)' }}>Credit Cost Preview</strong>
                                            <p style={{ fontSize: '13px', color: 'var(--app-success-text)', marginTop: '8px' }}>
                                                {(() => {
                                                    const count = bulkCsvContent.split(/[,\n\r]+/).map(e => e.trim()).filter(e => e && e.includes('@')).length;
                                                    const creditsPerUser = getOrgActivationCredits(selectedOrg?.organizationId, bulkDurationMonths);
                                                    const total = roundCredits(count * creditsPerUser);
                                                    return `${count} users × ${formatCredits(creditsPerUser)} credits = ${formatCredits(total)} credits needed`;
                                                })()}
                                            </p>
                                            <p style={{ fontSize: '12px', color: 'var(--app-success-text)', marginTop: '4px' }}>
                                                Current balance: {formatCredits(stats.creditsBalance)} credits
                                            </p>
                                        </div>
                                    )}

                                    {bulkImportProgress ? (
                                        <div style={{ width: '100%' }}>
                                            <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', marginBottom: '8px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                    <span style={{ fontWeight: '600', color: '#1e40af' }}>
                                                        Importing {bulkImportProgress.totalUsers} users...
                                                    </span>
                                                    <span style={{ color: '#3b82f6', fontSize: '13px' }}>
                                                        ~{Math.ceil(bulkImportProgress.estimatedSeconds / 60)} min estimated
                                                    </span>
                                                </div>
                                                <div style={{ height: '8px', background: '#dbeafe', borderRadius: '4px', overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        background: '#0b7da6',
                                                        animation: 'progress-pulse 1.5s ease-in-out infinite'
                                                    }}></div>
                                                </div>
                                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px', marginBottom: 0 }}>
                                                    Processing in batches... Please wait, do not close this page.
                                                </p>
                                            </div>
                                            <style>{`
                                                @keyframes progress-pulse {
                                                    0%, 100% { opacity: 0.6; }
                                                    50% { opacity: 1; }
                                                }
                                            `}</style>
                                        </div>
                                    ) : (
                                        <button type="submit" disabled={loading || selectedProducts.length === 0 || !bulkCsvContent.trim()}
                                            className="btn btn-primary" style={{ width: '100%' }}>
                                            Import All Users
                                        </button>
                                    )}
                                </form>
                            )}
                        </div>
                    )}

                    {/* Users Tab */}
                    {activeTab === 'users' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">My Users ({users.length})</h3>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={handleExportUsers} disabled={users.length === 0} className="btn btn-secondary" title="Export all users to CSV">
                                        Export
                                    </button>
                                    <button onClick={fetchUsers} disabled={loadingUsers} className="btn btn-secondary">
                                        Refresh
                                    </button>
                                </div>
                            </div>

                            <div className="form-group cp-search-filters" style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <input type="text" className="form-input" placeholder="Search by email..."
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ flex: '1 1 260px' }}
                                />
                                <select
                                    className="form-input"
                                    value={selectedOrgFilter}
                                    onChange={(e) => setSelectedOrgFilter(e.target.value)}
                                    style={{ flex: '1 1 180px' }}
                                >
                                    <option value="All">All Organizations</option>
                                    <option value="Unknown">Unknown / No Org</option>
                                    {allUniqueOrgs.map(org => (
                                        <option key={org.organizationId} value={org.organizationId}>
                                            {org.organizationName}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="form-input"
                                    value={selectedProductFilter}
                                    onChange={(e) => setSelectedProductFilter(e.target.value)}
                                    style={{ flex: '1 1 190px' }}
                                >
                                    <option value="All">All Products</option>
                                    {availableProductFilters.map(product => (
                                        <option key={product} value={product}>
                                            {product}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="form-input"
                                    value={selectedDurationFilter}
                                    onChange={(e) => setSelectedDurationFilter(e.target.value)}
                                    style={{ flex: '1 1 150px' }}
                                    title="Filter by subscription duration"
                                >
                                    <option value="All">All Durations</option>
                                    {availableDurationFilters.map(duration => (
                                        <option key={duration} value={duration}>
                                            {duration}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="form-input"
                                    value={selectedTeamStatusFilter}
                                    onChange={(e) => setSelectedTeamStatusFilter(e.target.value)}
                                    style={{ flex: '1 1 170px' }}
                                    title="Filter by the user's organization (team) status"
                                >
                                    <option value="All">All Team Statuses</option>
                                    <option value="active">Active Teams</option>
                                    <option value="inactive">Inactive Teams</option>
                                </select>
                                <div className="cp-days-left-filter" style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: '1 1 260px', minWidth: 0 }}>
                                    <select
                                        className="form-input"
                                        value={daysLeftOperator}
                                        onChange={(e) => setDaysLeftOperator(e.target.value)}
                                        style={{ flex: '1 1 110px', minWidth: 0 }}
                                        title="Filter users by days left"
                                    >
                                        <option value="">Days left</option>
                                        <option value="gt">&gt; days</option>
                                        <option value="lt">&lt; days</option>
                                    </select>
                                    <input
                                        className="form-input"
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={daysLeftValue}
                                        onChange={(e) => setDaysLeftValue(e.target.value)}
                                        placeholder="Days"
                                        disabled={!daysLeftOperator}
                                        style={{ flex: '1 1 80px', minWidth: 0 }}
                                    />
                                    {(daysLeftOperator || daysLeftValue) && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => { setDaysLeftOperator(''); setDaysLeftValue(''); }}
                                            style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}
                                            title="Clear days-left filter"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Filter Tabs */}
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
                                <button
                                    className={`btn ${filterType === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setFilterType('all')}
                                    style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '20px' }}
                                >
                                    All Users
                                </button>
                                <button
                                    className={`btn ${filterType === 'active' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setFilterType('active')}
                                    style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '20px' }}
                                >
                                    Active
                                </button>
                                <button
                                    className={`btn ${filterType === 'expiring_week' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setFilterType('expiring_week')}
                                    style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '20px' }}
                                >
                                    Expiring within 7 Days
                                </button>
                                <button
                                    className={`btn ${filterType === 'expiring_today' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setFilterType('expiring_today')}
                                    style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '20px', background: filterType === 'expiring_today' ? '#dc2626' : undefined, color: filterType === 'expiring_today' ? 'white' : undefined }}
                                >
                                    Expiring Today ({stats.usersExpiringToday || 0})
                                </button>
                                <button
                                    className={`btn ${filterType === 'expired' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setFilterType('expired')}
                                    style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '20px' }}
                                >
                                    Expired
                                </button>
                            </div>

                            {/* Bulk Actions Bar */}
                            {selectedUsersForBulk.length > 0 && (
                                <div className="cp-bulk-actions-bar" style={{ padding: '12px 16px', background: '#dbeafe', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 'bold', color: '#1e40af' }}>
                                        {selectedUsersForBulk.length} user(s) selected
                                    </span>
                                    {selectedUsersForBulk.length < filteredUsers.length ? (
                                        <button type="button" onClick={selectEveryFilteredUser} className="btn btn-secondary cp-bulk-btn" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                            Select all {filteredUsers.length} users
                                        </button>
                                    ) : filteredUsers.length > paginatedUsers.length && (
                                        <span style={{ color: '#b91c1c', fontWeight: 600, fontSize: '13px' }}>
                                            All {filteredUsers.length} users on every page are selected
                                        </span>
                                    )}
                                    <button type="button" onClick={() => setSelectedUsersForBulk([])} className="btn btn-secondary cp-bulk-btn" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                        Clear selection
                                    </button>
                                    <button onClick={() => setShowBulkDurationModal(true)} className="btn btn-primary cp-bulk-btn" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                        Modify Duration
                                    </button>
                                    <button onClick={handleBulkDeleteSelected} disabled={loading} className="btn btn-danger cp-bulk-btn" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                        Delete Selected
                                    </button>
                                    {canUseCPTransfer && (
                                        <button onClick={() => {
                                            if (!canUseCPBulkTransfer) {
                                                setError('Bulk transfer is disabled for this account.');
                                                return;
                                            }
                                            setShowBulkTransferModal(true);
                                            setSelectedTransferOrgForBulk('');
                                            setSelectedTransferProductsForBulk([]);
                                        }} className="btn btn-info cp-bulk-btn" style={{ padding: '6px 12px', fontSize: '13px', background: '#3b82f6', color: 'white', border: 'none' }}>
                                            Bulk Transfer
                                        </button>
                                    )}
                                    {canUseCPReinvite && (
                                        <button onClick={handleBulkReinvite} disabled={loading} className="btn btn-primary cp-bulk-btn" style={{ padding: '6px 12px', fontSize: '13px', background: '#0b7da6', color: 'white', border: 'none' }}>
                                            Bulk Reinvite
                                        </button>
                                    )}
                                    <button onClick={() => setSelectedUsersForBulk([])} className="btn btn-secondary cp-bulk-btn" style={{ padding: '6px 12px', fontSize: '13px' }}>
                                        Clear Selection
                                    </button>
                                </div>
                            )}

                            {loadingUsers ? (
                                <div className="loading">Loading users...</div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon"><Icon name="users" size={16} /></div>
                                    <div className="empty-text">No users found</div>
                                </div>
                            ) : (
                                <>
                                    {/* Mobile Select All Bar - visible only on mobile where thead is hidden */}
                                    <div className="cp-mobile-select-bar">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: '1' }}>
                                            <input type="checkbox"
                                                checked={paginatedUsers.length > 0 && paginatedUsers.every(u => selectedUsersForBulk.includes(u.email))}
                                                onChange={selectAllUsers}
                                                style={{ width: '20px', height: '20px' }}
                                            />
                                            <span style={{ fontWeight: '600', fontSize: '14px', color: '#334155' }}>
                                                Select this page ({paginatedUsers.length})
                                            </span>
                                        </label>
                                        {selectedUsersForBulk.length > 0 && (
                                            <span style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600' }}>
                                                {selectedUsersForBulk.length} selected
                                            </span>
                                        )}
                                    </div>
                                    <table className="data-table cp-users-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}>
                                                    <input type="checkbox"
                                                        checked={paginatedUsers.length > 0 && paginatedUsers.every(u => selectedUsersForBulk.includes(u.email))}
                                                        onChange={selectAllUsers}
                                                        title="Select all users on this page" />
                                                </th>
                                                <th>Email</th>
                                                <th>Organization</th>
                                                <th>Products</th>
                                                <th>Duration</th>
                                                <th>Days Left</th> {/* Changed from Expires */}
                                                <th>Refundable</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedUsers.map((user, idx) => {
                                                const now = new Date();
                                                const expires = new Date(user.expiresAt);
                                                const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
                                                const isExpired = daysLeft <= 0;

                                                return (
                                                    <tr key={idx}>
                                                        <td data-label="">
                                                            <input type="checkbox"
                                                                checked={selectedUsersForBulk.includes(user.email)}
                                                                onChange={() => toggleUserSelection(user.email)} />
                                                        </td>
                                                        <td data-label="Email" style={{ maxWidth: '180px', wordBreak: 'break-all' }}>
                                                            <strong>{user.email}</strong>
                                                            {user.adobeStatus === 'manual_pending' && (
                                                                <span title="Saved. Our team is adding it in Adobe, usually within 5-10 minutes." style={{ display: 'inline-block', marginLeft: 6, padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                                                    ⏳ Activating…
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td data-label="Organization" style={{ fontSize: '12px' }}>{user.organizationName || 'Unknown'}</td>
                                                        <td data-label="Products" style={{ fontSize: '12px', maxWidth: '200px' }}>
                                                            {(user.displayProducts || user.products) && (user.displayProducts || user.products).length > 0 ? (
                                                                (user.displayProducts || user.products).length > 2 ? (
                                                                    <span title={(user.displayProducts || user.products).join(', ')}>
                                                                        {(user.displayProducts || user.products)[0]}, {(user.displayProducts || user.products)[1]} +{(user.displayProducts || user.products).length - 2} more
                                                                    </span>
                                                                ) : (user.displayProducts || user.products).join(', ')
                                                            ) : '-'}
                                                        </td>
                                                        <td data-label="Duration">{getDurationLabel(user.durationKey, user)}</td>
                                                        <td data-label="Days Left" style={{ color: isExpired ? '#dc2626' : daysLeft <= 7 ? '#f59e0b' : 'inherit' }}>
                                                            {isExpired ? 'Expired' : `${daysLeft} days`}
                                                        </td>
                                                        <td data-label="Refundable">
                                                            {(() => {
                                                                const outcome = getDeleteOutcome(user);
                                                                if (outcome.kind === 'credits') {
                                                                    const left = Math.max(0, Math.round(outcome.graceHours - user.hoursSinceActivation));
                                                                    return <span style={{ color: '#059669' }}>Yes ({left}h left)</span>;
                                                                }
                                                                if (outcome.kind === 'pending') {
                                                                    return <span style={{ color: '#d97706' }}>Pro-rata ({outcome.credits} cr)</span>;
                                                                }
                                                                if (outcome.kind === 'days') {
                                                                    return <span style={{ color: '#d97706' }}>{outcome.days} day(s)</span>;
                                                                }
                                                                return <span style={{ color: '#dc2626' }}>No</span>;
                                                            })()}
                                                        </td>
                                                        <td data-label="Actions" style={{ minWidth: '160px' }}>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                <button onClick={() => {
                                                                    setTransferringUser(user);
                                                                    setNewEmailForTransfer('');
                                                                    if (organizationAccess.length === 0) fetchProducts();
                                                                    const ap = getAllowedProductsForOrg(user.organizationId);
                                                                    const up = user.products || [];
                                                                    const autoMapping = up.map(p => {
                                                                        if (ap.includes(p)) return p;
                                                                        const ciMatch = ap.find(a => a.toLowerCase() === p.toLowerCase());
                                                                        if (ciMatch) return ciMatch;
                                                                        const pLower = p.toLowerCase();
                                                                        if (pLower.includes('acrobat')) return ap.find(a => a.toLowerCase().includes('acrobat')) || '';
                                                                        if (pLower.includes('all apps')) return ap.find(a => a.toLowerCase().includes('all apps')) || '';
                                                                        if (pLower.includes('creative cloud pro')) return ap.find(a => a.toLowerCase().includes('creative cloud pro')) || '';
                                                                        if (ap.length === 1) return ap[0];
                                                                        return '';
                                                                    });
                                                                    setTransferProductMapping(autoMapping);
                                                                    setTransferRemovedIndices(new Set());
                                                                }}
                                                                    className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                                    Edit
                                                                </button>
                                                                {canUseCPTransfer && (
                                                                    <button onClick={() => {
                                                                        setTransferringOrgUser(user);
                                                                        setSelectedTransferOrg('');
                                                                        setSelectedTransferProducts([]);
                                                                    }}
                                                                        className="btn btn-info" style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', background: '#3b82f6', color: 'white', border: 'none' }}>
                                                                        Transfer
                                                                    </button>
                                                                )}
                                                                <button onClick={() => {
                                                                    setExtendingUser(user);
                                                                    setExtensionMonths(1);
                                                                }}
                                                                    className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                                    Extend
                                                                </button>
                                                                {canUseCPReinvite && (
                                                                    <button onClick={() => handleReinviteUser(user)}
                                                                        disabled={loading}
                                                                        className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', background: '#0b7da6', color: 'white', border: 'none' }}>
                                                                        Reinvite
                                                                    </button>
                                                                )}
                                                                <button onClick={() => {
                                                                    const statusUrl = `${window.location.origin}/status/${encodeURIComponent(user.email)}`;
                                                                    copyToClipboard(statusUrl);
                                                                    setResult(`Copied status link for ${user.email}`);
                                                                }}
                                                                    className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                                    Copy Link
                                                                </button>
                                                                <button onClick={() => handleRemoveUser(user.email)}
                                                                    className="btn btn-danger" style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {/* Pagination Controls */}
                                    {totalPages > 1 && (
                                        <div className="cp-pagination" style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginTop: '16px',
                                            padding: '12px 16px',
                                            background: '#f8fafc',
                                            borderRadius: '8px',
                                            flexWrap: 'wrap',
                                            gap: '8px'
                                        }}>
                                            <span style={{ fontSize: '14px', color: '#64748b' }}>
                                                Showing {((currentPage - 1) * USERS_PER_PAGE) + 1} - {Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
                                            </span>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                <button
                                                    onClick={() => setCurrentPage(1)}
                                                    disabled={currentPage === 1}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '6px 10px', fontSize: '12px' }}
                                                >
                                                    First
                                                </button>
                                                <button
                                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                    disabled={currentPage === 1}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '6px 10px', fontSize: '12px' }}
                                                >
                                                    ‹ Prev
                                                </button>
                                                <span style={{ padding: '0 12px', fontWeight: '600' }}>
                                                    Page {currentPage} of {totalPages}
                                                </span>
                                                <button
                                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                    disabled={currentPage === totalPages}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '6px 10px', fontSize: '12px' }}
                                                >
                                                    Next ›
                                                </button>
                                                <button
                                                    onClick={() => setCurrentPage(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '6px 10px', fontSize: '12px' }}
                                                >
                                                    Last </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Bulk Delete Tab */}
                    {activeTab === 'bulkDelete' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Bulk Delete Users</h3>
                            </div>

                            <div style={{ padding: '16px', background: '#fef2f2', borderRadius: '6px', marginBottom: '16px' }}>
                                <strong style={{ color: '#991b1b' }}>Important: refund rule</strong>
                                <p style={{ fontSize: '13px', color: '#991b1b', marginTop: '8px' }}>
                                    {accountGraceHours === 0
                                        ? 'Credits are not refunded on deletion for this account. Deleting a user returns its unused value as pro-rata refund credits or refund days instead, per your administrator\'s settings.'
                                        : `Credits are only refunded for users deleted within ${accountGraceHours} hour(s) of activation. After that, the unused value returns as pro-rata refund credits or refund days.`}
                                </p>
                            </div>

                            <form onSubmit={handleBulkDelete}>
                                <div className="form-group">
                                    <label className="form-label">Email Addresses</label>
                                    <textarea className="form-input" rows={8} value={bulkDeleteEmails}
                                        onChange={(e) => setBulkDeleteEmails(e.target.value)}
                                        placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                                        style={{ fontFamily: 'monospace' }} required />
                                </div>

                                {bulkDeleteProgress ? (
                                    <div style={{ width: '100%' }}>
                                        <div style={{ padding: '16px', background: '#fef2f2', borderRadius: '8px', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <span style={{ fontWeight: '600', color: '#991b1b' }}>
                                                    Deleting {bulkDeleteProgress.totalUsers} users...
                                                </span>
                                            </div>
                                            <div style={{ height: '8px', background: '#fecaca', borderRadius: '4px', overflow: 'hidden' }}>
                                                <div style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    background: '#ff5656',
                                                    animation: 'progress-pulse 1.5s ease-in-out infinite'
                                                }}></div>
                                            </div>
                                            <p style={{ fontSize: '12px', color: '#991b1b', marginTop: '8px', marginBottom: 0 }}>
                                                Processing... Please wait.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <button type="submit" disabled={loading || !bulkDeleteEmails.trim()}
                                        className="btn btn-danger" style={{ width: '100%' }}>
                                        Delete All Listed Users
                                    </button>
                                )}
                            </form>
                        </div>
                    )}

                    {/* Bulk Extend Tab */}
                    {activeTab === 'bulkExtend' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Bulk Extend Users</h3>
                            </div>

                            <div style={{ padding: '16px', background: '#ecfdf5', borderRadius: '6px', marginBottom: '16px' }}>
                                <strong style={{ color: 'var(--app-success-text)' }}>Extend Expiration Dates</strong>
                                <p style={{ fontSize: '13px', color: 'var(--app-success-text)', marginTop: '8px' }}>
                                    Add months to multiple users' expiration dates. Extension time is added to remaining time.
                                </p>
                            </div>

                            <form onSubmit={handleBulkExtend}>
                                <div className="form-group">
                                    <label className="form-label">Extension Duration</label>
                                    <select className="form-input" value={bulkExtendMonths}
                                        onChange={(e) => setBulkExtendMonths(parseInt(e.target.value))}>
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
                                            .filter(m => !(stats.hiddenMonths || []).map(String).includes(String(m)))
                                            .map(m => {
                                                // Price from the reseller's activation pricing — NOT pre-made-key
                                                // pricing, which is a different product and made this dropdown
                                                // disagree with both the preview below and the actual charge.
                                                const credits = getDurationCredits(stats.creditsPerMonth, m);
                                                return (
                                                    <option key={m} value={m}>
                                                        {m} {m === 1 ? 'Month' : 'Months'} ({formatCredits(credits)} credits/user)
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Email Addresses</label>
                                    <textarea className="form-input" rows={8} value={bulkExtendEmails}
                                        onChange={(e) => setBulkExtendEmails(e.target.value)}
                                        placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                                        style={{ fontFamily: 'monospace' }} required />
                                </div>

                                {bulkExtendEmails.trim() && (() => {
                                    const emailList = bulkExtendEmails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e && e.includes('@'));
                                    const count = emailList.length;
                                    // Priced per user (a batch can mix K12 and Pro teams), matching the server.
                                    const totalCredits = estimateBulkExtendCredits(emailList, bulkExtendMonths);
                                    const k12Count = (() => {
                                        const byEmail = new Map((users || []).map(u => [String(u.email || '').toLowerCase(), u]));
                                        return emailList.filter(e => byEmail.get(e.toLowerCase())?.k12).length;
                                    })();
                                    return (
                                        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' }}>
                                            <div><Icon name="mail" size={16} /><strong>{count}</strong> email(s) detected</div>
                                            <div><Icon name="card" size={16} /><strong>{formatCredits(totalCredits)}</strong> credits will be deducted</div>
                                            {k12Count > 0 && (
                                                <div style={{ color: 'var(--app-link)', marginTop: '4px' }}>
                                                    Includes {k12Count} K12 user(s) at {formatCredits(Number(stats.k12Cost ?? 1))} credit(s) per month.
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                <button type="submit" disabled={loading || !bulkExtendEmails.trim() || stats.creditsBalance < 1}
                                    className="btn btn-primary" style={{ width: '100%' }}>
                                    {loading ? 'Extending...' : 'Extend All Listed Users'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Transactions Tab */}
                    {activeTab === 'transactions' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Transaction History ({transactions.length})</h3>
                                <button onClick={fetchTransactions} disabled={loadingTransactions} className="btn btn-secondary">
                                    Refresh
                                </button>
                            </div>

                            {/* Email search */}
                            <div style={{ marginBottom: '16px' }}>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Search by email address..."
                                    value={txSearchEmail}
                                    onChange={e => setTxSearchEmail(e.target.value)}
                                    style={{ maxWidth: '400px' }}
                                />
                                {txSearchEmail && (
                                    <button
                                        onClick={() => setTxSearchEmail('')}
                                        style={{ marginLeft: '8px', background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {loadingTransactions ? (
                                <div className="loading">Loading transactions...</div>
                            ) : transactions.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon"><Icon name="history" size={16} /></div>
                                    <div className="empty-text">No transactions yet</div>
                                </div>
                            ) : (() => {
                                const filteredTx = transactions.filter(tx => {
                                    if (!txSearchEmail.trim()) return true;
                                    const q = txSearchEmail.trim().toLowerCase();
                                    if (tx.reason && tx.reason.toLowerCase().includes(q)) return true;
                                    if (tx.emails && tx.emails.some(e => e.toLowerCase().includes(q))) return true;
                                    return false;
                                });
                                const txTotalPages = Math.ceil(filteredTx.length / TX_PER_PAGE);
                                const paginatedTx = filteredTx.slice(
                                    (txCurrentPage - 1) * TX_PER_PAGE,
                                    txCurrentPage * TX_PER_PAGE
                                );

                                return (
                                    <>
                                        {txSearchEmail && (
                                            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#475569' }}>
                                                Showing {filteredTx.length} result{filteredTx.length !== 1 ? 's' : ''} for <strong>"{txSearchEmail}"</strong>
                                            </div>
                                        )}
                                        {filteredTx.length === 0 ? (
                                            <div className="empty-state">
                                                <div className="empty-icon"><Icon name="search" size={16} /></div>
                                                <div className="empty-text">No transactions found for this email</div>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Type</th>
                                                            <th>Credits / Days</th>
                                                            <th>Reason</th>
                                                            <th>Balance</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedTx.map((tx, idx) => {
                                                            const amountDisplay = getTransactionAmountDisplay(tx);
                                                            return (
                                                            <tr key={idx}>
                                                                <td style={{ fontSize: '12px' }}>{new Date(tx.timestamp).toLocaleString()}</td>
                                                                <td>
                                                                    <span className={`badge ${getTransactionBadgeClass(tx)}`}>
                                                                        {transactionLabel(tx.type)}
                                                                    </span>
                                                                </td>
                                                                <td style={{ color: amountDisplay.color, fontWeight: 600 }}>
                                                                    {amountDisplay.text}
                                                                </td>
                                                                <td style={{ fontSize: '12px' }}>
                                                                    {tx.reason}
                                                                    {tx.emails && tx.emails.length > 0 && (
                                                                        <div style={{ marginTop: '4px' }}>
                                                                            <details>
                                                                                <summary style={{ cursor: 'pointer', color: '#3b82f6', fontSize: '11px', fontWeight: 600 }}>
                                                                                    Show {tx.emails.length} email(s)
                                                                                </summary>
                                                                                <div style={{ marginTop: '4px', padding: '6px 8px', background: '#f1f5f9', borderRadius: '4px', maxHeight: '150px', overflowY: 'auto', fontSize: '11px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                                                                                    {tx.emails.map((email, i) => (
                                                                                        <div key={i}>{email}</div>
                                                                                    ))}
                                                                                </div>
                                                                            </details>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>{isGoldTransaction(tx.type) ? '' : ''}{formatCredits(displayBalanceBefore(tx))} → {formatCredits(tx.balanceAfter)}</td>
                                                            </tr>
                                                        )})}
                                                    </tbody>
                                                </table>

                                                {/* Pagination */}
                                                {txTotalPages > 1 && (
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginTop: '16px',
                                                        padding: '12px 16px',
                                                        background: '#f8fafc',
                                                        borderRadius: '8px',
                                                        flexWrap: 'wrap',
                                                        gap: '8px'
                                                    }}>
                                                        <span style={{ fontSize: '14px', color: '#64748b' }}>
                                                            Showing {((txCurrentPage - 1) * TX_PER_PAGE) + 1}–{Math.min(txCurrentPage * TX_PER_PAGE, filteredTx.length)} of {filteredTx.length} records
                                                        </span>
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                            <button onClick={() => setTxCurrentPage(1)} disabled={txCurrentPage === 1} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>First</button>
                                                            <button onClick={() => setTxCurrentPage(p => Math.max(1, p - 1))} disabled={txCurrentPage === 1} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>‹ Prev</button>
                                                            <span style={{ padding: '0 12px', fontWeight: '600' }}>Page {txCurrentPage} of {txTotalPages}</span>
                                                            <button onClick={() => setTxCurrentPage(p => Math.min(txTotalPages, p + 1))} disabled={txCurrentPage === txTotalPages} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>Next ›</button>
                                                            <button onClick={() => setTxCurrentPage(txTotalPages)} disabled={txCurrentPage === txTotalPages} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>Last</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {/* Operations Tab */}
                    {activeTab === 'operations' && (
                        <>
                            {/* Migration History Section */}
                            <div className="card" style={{ marginBottom: '24px' }}>
                                <div className="card-header">
                                    <h3 className="card-title">Migration History</h3>
                                    <button onClick={fetchMigrationHistory} className="btn btn-secondary">
                                        Refresh
                                    </button>
                                </div>

                                {migrationHistory.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon"><Icon name="package" size={16} /></div>
                                        <div className="empty-text">No migration history found</div>
                                        <p style={{ color: '#64748b', marginTop: '8px' }}>Migrations affecting your users will appear here.</p>
                                    </div>
                                ) : (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Organization</th>
                                                <th>Users (Total/Migrated)</th>
                                                <th>Status</th>
                                                <th>Started</th>
                                                <th>Duration</th>
                                                <th>Affected Users</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {migrationHistory.map((mig) => (
                                                <tr key={mig.id}>
                                                    <td>
                                                        <strong>{mig.orgName}</strong>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>ID: {mig.orgId}</div>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span>{mig.migratedCount} / {mig.totalUsers}</span>
                                                            {mig.status === 'in_progress' && (
                                                                <div style={{ width: '50px', height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                                                                    <div style={{
                                                                        width: `${Math.round((mig.migratedCount / mig.totalUsers) * 100)}%`,
                                                                        height: '100%',
                                                                        background: '#f59e0b'
                                                                    }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${mig.status === 'completed' ? 'badge-success' :
                                                            mig.status === 'in_progress' ? 'badge-warning' :
                                                                mig.status === 'paused' ? 'badge-warning' :
                                                                    'badge-danger'
                                                            }`}>
                                                            {mig.status === 'in_progress' ? 'In Progress' :
                                                                mig.status === 'completed' ? 'Completed' :
                                                                    mig.status.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '12px' }}>
                                                        {new Date(mig.startedAt).toLocaleString()}
                                                    </td>
                                                    <td style={{ fontSize: '12px' }}>
                                                        {mig.durationSeconds ? `${Math.floor(mig.durationSeconds / 60)}m ${mig.durationSeconds % 60}s` : '-'}
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-secondary">{mig.affectedUserCount} users</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Bulk Operations Section */}
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Bulk Operations History</h3>
                                    <button onClick={fetchBulkOperations} disabled={loadingOperations} className="btn btn-secondary">
                                        Refresh
                                    </button>
                                </div>

                                {loadingOperations ? (
                                    <div className="loading-spinner">Loading operations...</div>
                                ) : bulkOperations.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon"><Icon name="clipboard" size={16} /></div>
                                        <div className="empty-text">No bulk operations yet</div>
                                        <p style={{ color: '#64748b', marginTop: '8px' }}>Start a bulk import to see operations here</p>
                                    </div>
                                ) : (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                                <th>Progress</th>
                                                <th>Organization</th>
                                                <th>Started</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkOperations.map((op) => (
                                                <tr key={op.id}>
                                                    <td>#{op.id}</td>
                                                    <td>
                                                        <span className="badge badge-primary">{op.type.replace('_', ' ').toUpperCase()}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${op.status === 'completed' ? 'badge-success' :
                                                            op.status === 'in_progress' ? 'badge-warning' :
                                                                op.status === 'paused' ? 'badge-warning' :
                                                                    op.status === 'cancelled' ? 'badge-danger' :
                                                                        'badge-secondary'
                                                            }`}>
                                                            {op.status === 'in_progress' ? 'In Progress' :
                                                                op.status === 'completed' ? 'Completed' :
                                                                    op.status === 'paused' ? 'Paused' :
                                                                        op.status === 'cancelled' ? 'Cancelled' :
                                                                            op.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span style={{ fontWeight: 600 }}>
                                                            {op.type === 'bulk_delete'
                                                                ? `${op.deletedCount || 0}/${op.totalEmails || 0}`
                                                                : `${op.successCount || 0}/${op.emailsToProcess || 0}`
                                                            }
                                                        </span>
                                                        {(op.failCount > 0 || op.notFoundCount > 0) && (
                                                            <span style={{ color: '#dc2626', marginLeft: '8px' }}>
                                                                ({op.failCount || op.notFoundCount || 0} {op.type === 'bulk_delete' ? 'not found' : 'failed'})
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ fontSize: '12px' }}>
                                                        {op.organizationName || '-'}
                                                    </td>
                                                    <td style={{ fontSize: '12px' }}>
                                                        {new Date(op.createdAt).toLocaleString()}
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                            {/* Pause button for in-progress */}
                                                            {op.status === 'in_progress' && (
                                                                <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }}
                                                                    onClick={() => handlePauseOperation(op.id)}>
                                                                    Pause
                                                                </button>
                                                            )}

                                                            {/* Resume button for paused/failed with remaining emails */}
                                                            {(op.status === 'paused' || op.status === 'failed') && op.remainingEmails?.length > 0 && (
                                                                <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '11px' }}
                                                                    onClick={() => handleResumeOperation(op.id)}>
                                                                    Resume ({op.remainingEmails.length})
                                                                </button>
                                                            )}

                                                            {/* View failures for completed with failures */}
                                                            {op.status === 'completed' && ((op.failCount > 0) || (op.failures && op.failures.length > 0)) && (
                                                                <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }}
                                                                    onClick={() => setViewingFailures({
                                                                        operationType: op.type,
                                                                        operationId: op.id,
                                                                        failures: op.failures || []
                                                                    })}>
                                                                    View Failures
                                                                </button>
                                                            )}

                                                            {/* Cancel button for in-progress or paused */}
                                                            {(op.status === 'in_progress' || op.status === 'paused') && (
                                                                <button className="btn" style={{ padding: '4px 8px', fontSize: '11px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}
                                                                    onClick={() => handleCancelOperation(op.id, op.organizationName)}>
                                                                    Cancel
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    )}

                    {/* My Keys Tab - History only */}
                    {activeTab === 'backupKeys' && stats.backupKeys?.enabled && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">My Pre-Made Keys</h3>
                                <button onClick={fetchBackupKeyInventory} className="btn btn-secondary">Refresh</button>
                            </div>

                            {/* My Redeemed Keys */}
                            {backupKeyInventory?.myRedeemedKeys && backupKeyInventory.myRedeemedKeys.length > 0 ? (
                                <div>
                                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Code</th>
                                                    <th>Duration</th>
                                                    <th>Date</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {backupKeyInventory.myRedeemedKeys.map((key, idx) => (
                                                    <tr key={idx}>
                                                        <td><code style={{ fontSize: '12px', userSelect: 'all' }}>{key.code}</code></td>
                                                        <td>{key.durationMonths} month{key.durationMonths > 1 ? 's' : ''}</td>
                                                        <td style={{ fontSize: '12px', color: '#64748b' }}>{new Date(key.assignedAt).toLocaleDateString()}</td>
                                                        <td>
                                                            <button onClick={() => {
                                                                navigator.clipboard.writeText(key.code);
                                                                setResult('Code copied to clipboard!');
                                                            }} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }}>
                                                                Copy
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                                    <div style={{ fontSize: '40px', marginBottom: '12px' }}><Icon name="key" size={16} /></div>
                                    <p>No keys redeemed yet. Go to <strong>Add User → Get Pre-Made Key</strong> to purchase keys.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Shop / Marketplace Tab */}
                    {activeTab === 'shop' && marketplaceEnabled && (() => {
                        const categoryOptions = [
                            { name: 'all', label: 'All', count: marketplaceProducts.length },
                            // Only offer the New chip when there is something new to see.
                            ...(newArrivalProducts.length > 0
                                ? [{ name: NEW_FILTER, label: 'New', count: newArrivalProducts.length }]
                                : []),
                            ...marketplaceCategories.map(category => ({ ...category, label: category.name }))
                        ];
                        const filteredProducts = filteredMarketplaceProducts;

                        return (
                        <>
                            {/* Purchase success modal - supports multiple codes */}
                            {purchasedOrders.length > 0 && (
                                <div className="purchase-success-overlay"
                                    onClick={() => { setPurchasedOrders([]); setPurchasedOrder(null); }}>
                                    <div className="purchase-success-modal" onClick={e => e.stopPropagation()}>
                                        <div className="purchase-success-header">
                                            <div className="purchase-success-icon"><Icon name="check" size={16} /></div>
                                            <h3>Purchase Complete!</h3>
                                            <p>
                                                {purchasedOrders.every(o => o.fulfillmentStatus === 'pending' && !o.supplierDelivery)
                                                    ? (purchasedOrders.length === 1
                                                        ? `Order placed for ${purchasedOrders[0].productName}`
                                                        : `${purchasedOrders.length} orders placed for ${purchasedOrders[0].productName}`)
                                                    : (purchasedOrders.length === 1
                                                        ? `Your code for ${purchasedOrders[0].productName}`
                                                        : `${purchasedOrders.length} codes for ${purchasedOrders[0].productName}`)}
                                                {' '}({purchasedOrders[0].retentionMonths === 0 ? 'Lifetime' : `${purchasedOrders[0].retentionMonths} month`})
                                            </p>
                                            {purchasedOrders[0].buyerEmail && (
                                                <div className="purchase-success-email">
                                                    Activation or receive message email: <strong>{purchasedOrders[0].buyerEmail}</strong>
                                                </div>
                                            )}
                                        </div>
                                        {purchasedOrders.every(o => o.fulfillmentStatus === 'pending' && !o.supplierDelivery) ? (
                                            <div className="mp-awaiting">
                                                This product is shipped by hand. Your order{purchasedOrders.length > 1 ? 's are' : ' is'}{' '}
                                                <strong>Pending Processing</strong> — the details will appear under <strong>My Orders</strong> once we have processed{' '}
                                                {purchasedOrders.length > 1 ? 'them' : 'it'}.
                                            </div>
                                        ) : (
                                        <div className="purchase-success-codes">
                                            {purchasedOrders.map((o, idx) => (
                                                <div key={o.id} className="purchase-success-code-row">
                                                    {purchasedOrders.length > 1 && (
                                                        <span className="purchase-success-code-index">#{idx + 1}</span>
                                                    )}
                                                    {o.code ? (<>
                                                    <code className="purchase-success-code">{o.code}</code>
                                                    <button
                                                        onClick={() => { navigator.clipboard.writeText(o.code); setResult('Code copied!'); }}
                                                        className="purchase-success-copy-btn"
                                                        type="button"
                                                    ><Icon name="clipboard" size={16} /></button>
                                                    </>) : o.fulfillmentStatus === 'refunded' ? (
                                                        <span style={{ color: '#b91c1c', fontSize: '13px' }}>Could not be delivered — refunded to your balance</span>
                                                    ) : (
                                                        <span style={{ color: '#b45309', fontSize: '13px' }}>⏳ Being delivered — it will appear in My Orders in a few seconds</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        )}
                                        {purchasedOrders.filter(o => o.code).length > 1 && (
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(purchasedOrders.filter(o => o.code).map(o => o.code).join('\n'));
                                                    setResult('All codes copied!');
                                                }}
                                                className="purchase-success-copy-all"
                                                type="button"
                                            >Copy All {purchasedOrders.filter(o => o.code).length} Codes</button>
                                        )}
                                        <p className="purchase-success-note">
                                            All orders saved in <strong>My Orders</strong> below.
                                        </p>
                                        <button onClick={() => { setPurchasedOrders([]); setPurchasedOrder(null); }} className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>Close</button>
                                    </div>
                                </div>
                            )}

                            {/* Product detail view */}
                            {marketplaceProducts.some(p => p.id === selectedMarketplaceProductId) ? (() => {
                                const p = marketplaceProducts.find(prod => prod.id === selectedMarketplaceProductId);
                                const options = getMarketplaceOptions(p);
                                const selectedRetention = parseInt(selectedRetentionByProduct[p.id], 10);
                                const selectedOption = options.find(opt => opt.month === selectedRetention) || options[0];
                                const retention = selectedOption ? selectedOption.month : 1;
                                const price = selectedOption?.priceGoldCoins || 0;
                                const selectedStock = selectedOption?.availableCount || 0;
                                const qty = Math.max(1, Math.min(selectedStock || 1, parseInt(shopQuantity) || 1));
                                const totalPrice = price * qty;
                                const cantAfford = (Number(stats.goldCoinsBalance) || 0) < totalPrice;
                                const notEnoughStock = selectedStock < qty;

                                return (
                                <div className="card">
                                    {/* Breadcrumb */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontSize: '13px' }}>
                                        <button type="button" onClick={() => { setSelectedMarketplaceProductId(null); setShopQuantity(1); setShopBuyerEmail(''); }}
                                            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: '13px' }}>
                                            ← Shop
                                        </button>
                                        {p.category && <><span style={{ color: 'var(--app-muted)' }}>/</span><span style={{ color: '#64748b' }}>{p.category}</span></>}
                                        <span style={{ color: 'var(--app-muted)' }}>/</span>
                                        <span style={{ color: '#334155', fontWeight: 600 }}>{p.name}</span>
                                    </div>

                                    <div className="marketplace-detail-layout">
                                        {p.imageUrl ? (
                                            <img src={productThumb(p, 800)} alt={p.name} className="marketplace-detail-media" decoding="async" onError={e => onThumbError(e, p.name)} />
                                        ) : (
                                            <div className="marketplace-detail-media marketplace-card-placeholder" style={{ fontSize: '40px' }}><Icon name="bag" size={16} /></div>
                                        )}
                                        <div className="marketplace-detail-content">
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '4px' }}>
                                                {p.promo && p.promo.discountPercent > 0 && (
                                                    <span className="cp-promo-badge" style={p.promo.badgeColor ? { background: p.promo.badgeColor } : undefined}>
                                                        {p.promo.label} −{p.promo.discountPercent}%
                                                    </span>
                                                )}
                                                {p.category && (
                                                    <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700 }}>{p.category}</span>
                                                )}
                                                {p.requiresEmail && (
                                                    <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700 }}>Email Required</span>
                                                )}
                                                {p.manualFulfill && (
                                                    <span className="mp-status mp-status-pending" title="Shipped by hand. The details arrive in My Orders once processed.">Manual delivery</span>
                                                )}
                                                {p.promo && formatPromoCountdown(p.promo.endsAt) && (
                                                    <span className="cp-onsale-countdown">{formatPromoCountdown(p.promo.endsAt)}</span>
                                                )}
                                            </div>
                                            <h4 style={{ margin: '4px 0 12px', fontSize: '20px' }}>{p.name}</h4>
                                            <div className="marketplace-detail-copy">
                                                {p.description
                                                    ? <CollapsibleDescription text={p.description} />
                                                    : <p style={{ color: '#94a3b8' }}>No description provided.</p>}
                                            </div>

                                            {/* Duration selector */}
                                            <div className="marketplace-retention-section">
                                                <label style={{ fontWeight: 700, fontSize: '13px', color: '#334155' }}>Duration</label>
                                                <div className="marketplace-retention-grid marketplace-retention-grid-wide">
                                                    {options.map(option => (
                                                        <button key={option.month} type="button"
                                                            onClick={() => setSelectedRetentionByProduct({ ...selectedRetentionByProduct, [p.id]: option.month })}
                                                            className={`marketplace-retention-option ${retention === option.month ? 'is-selected' : ''}`}
                                                            style={{ opacity: option.availableCount === 0 ? 0.5 : 1 }}
                                                        >
                                                            <span>{option.label || formatMarketplaceMonth(option.month)}</span>
                                                            {option.originalPriceGoldCoins > option.priceGoldCoins && (
                                                                <span className="cp-price-original" style={{ fontSize: '11px' }}>{option.originalPriceGoldCoins} GC</span>
                                                            )}
                                                            <strong>{option.priceGoldCoins} GC</strong>
                                                            {option.availableCount === 0 && <span style={{ fontSize: '10px', color: '#94a3b8' }}>Out</span>}
                                                        </button>
                                                    ))}
                                                </div>

                                                {/* Quantity selector — manual entry + up to available stock */}
                                                <div style={{ marginTop: '12px' }}>
                                                    <label style={{ fontWeight: 700, fontSize: '13px', color: '#334155', display: 'block', marginBottom: '8px' }}>
                                                        Quantity <span style={{ fontWeight: 400, color: '#64748b', fontSize: '12px' }}>({selectedStock} in stock)</span>
                                                    </label>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0', border: '1px solid #cbd5e1', borderRadius: '8px', width: 'fit-content', overflow: 'hidden' }}>
                                                            <button type="button"
                                                                onClick={() => setShopQuantity(q => Math.max(1, (parseInt(q) || 1) - 1))}
                                                                style={{ width: '40px', height: '40px', border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: '18px', fontWeight: 700, color: '#334155' }}>−</button>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max={selectedStock}
                                                                value={shopQuantity}
                                                                className="shop-qty-input"
                                                                onChange={e => setShopQuantity(e.target.value.replace(/[^0-9]/g, ''))}
                                                                onBlur={() => setShopQuantity(q => String(Math.max(1, Math.min(selectedStock || 1, parseInt(q) || 1))))}
                                                                style={{ width: '72px', height: '40px', textAlign: 'center', border: 'none', borderLeft: '1px solid #cbd5e1', borderRight: '1px solid #cbd5e1', fontWeight: 700, fontSize: '16px', color: '#0f172a', outline: 'none' }}
                                                            />
                                                            <button type="button"
                                                                onClick={() => setShopQuantity(q => Math.min(selectedStock || 1, (parseInt(q) || 1) + 1))}
                                                                style={{ width: '40px', height: '40px', border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: '18px', fontWeight: 700, color: '#334155' }}>+</button>
                                                        </div>
                                                        {selectedStock > 1 && (
                                                            <button type="button"
                                                                onClick={() => setShopQuantity(selectedStock)}
                                                                className="btn btn-secondary"
                                                                style={{ height: '40px', padding: '0 14px', fontSize: '13px' }}>
                                                                Max ({selectedStock})
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Warranty, as set by the admin on the product */}
                                                {p.warrantyType && (() => {
                                                    const days = Number(p.warrantyDays) > 0 ? Number(p.warrantyDays) : null;
                                                    const w = {
                                                        full: { icon: '🛡️', label: 'Full Warranty', bg: '#ecfdf5', border: '#a7f3d0', color: '#047857' },
                                                        first_login: { icon: '🔑', label: 'Covers initial login or activation only', bg: '#fffbeb', border: '#fde68a', color: '#b45309' },
                                                        none: { icon: '⚠️', label: 'No warranty', bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' }
                                                    }[p.warrantyType];
                                                    if (!w) return null;
                                                    return (
                                                        <div style={{ marginTop: '12px', display: 'inline-flex', flexDirection: 'column', gap: '2px', padding: '8px 12px', borderRadius: '10px', background: w.bg, border: `1px solid ${w.border}`, color: w.color, fontSize: '13px' }}>
                                                            <span style={{ fontWeight: 700 }}>{w.icon} {w.label}</span>
                                                            {days && p.warrantyType !== 'none' && (
                                                                <span style={{ fontWeight: 600 }}>Warranty period: {days} day{days === 1 ? '' : 's'}</span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {p.manualFulfill && (
                                                    <div className="mp-awaiting" style={{ marginTop: '12px' }}>
                                                        This product is delivered by hand. Your order will show as <strong>Pending Processing</strong> and the details appear in <strong>My Orders</strong> once we have completed it.
                                                    </div>
                                                )}

                                                {/* Email input for activation-required products */}
                                                {p.requiresEmail && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <label style={{ fontWeight: 700, fontSize: '13px', color: '#334155', display: 'block', marginBottom: '6px' }}>
                                                            Activation or Receive Message Email <span style={{ color: '#ef4444' }}>*</span>
                                                        </label>
                                                        <input
                                                            type="email"
                                                            className="form-input"
                                                            placeholder="customer@example.com"
                                                            value={shopBuyerEmail}
                                                            onChange={e => setShopBuyerEmail(e.target.value)}
                                                            style={{ maxWidth: '320px' }}
                                                        />
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>This email will be used to activate the product.</div>
                                                    </div>
                                                )}

                                                {/* Price summary */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #e2e8f0', marginTop: '12px' }}>
                                                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                                                        <span>{selectedStock} in stock · </span>
                                                        <span style={{ color: cantAfford ? '#ef4444' : '#64748b' }}>Your balance: {formatCredits(stats.goldCoinsBalance)} GC</span>
                                                    </div>
                                                    <div style={{ fontWeight: 700, fontSize: '16px', color: cantAfford ? '#ef4444' : '#059669', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                                        {selectedOption && selectedOption.originalPriceGoldCoins > price && (
                                                            <span className="cp-price-original" style={{ fontSize: '13px' }}>{formatCredits(selectedOption.originalPriceGoldCoins * qty)} GC</span>
                                                        )}
                                                        <span>{qty > 1 ? `${formatCredits(price)} × ${qty} = ` : ''}{formatCredits(totalPrice)} GC</span>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => handleBuyProduct(p.id)}
                                                    disabled={!p.available || selectedStock <= 0 || cantAfford || notEnoughStock || buyingProductId === p.id}
                                                    className="btn btn-primary"
                                                    style={{ width: '100%', padding: '14px', justifyContent: 'center', fontSize: '15px', fontWeight: 700, borderRadius: '10px' }}
                                                >
                                                    {buyingProductId === p.id ? 'Processing...'
                                                        : selectedStock <= 0 ? 'Out of Stock'
                                                        : notEnoughStock ? `Only ${selectedStock} available`
                                                        : cantAfford ? `Need ${formatCredits(totalPrice - Number(stats.goldCoinsBalance || 0))} more GC`
                                                        : `Buy ${qty > 1 ? `${qty}×` : ''} for ${formatCredits(totalPrice)} GC`}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                );
                            })() : (
                            <>
                                {/* Browse vs My Orders sub-tabs — keeps order history one click away
                                    instead of below however many products are in the store. */}
                                <div className="shop-category-tabs" aria-label="Shop view" style={{ marginBottom: '12px' }}>
                                    <button type="button"
                                        onClick={() => setShopViewTab('browse')}
                                        className={`shop-category-tab ${shopViewTab === 'browse' ? 'is-active' : ''}`}
                                    >
                                        <span>Browse Shop</span>
                                        <span className="shop-category-count">{filteredProducts.length}</span>
                                    </button>
                                    <button type="button"
                                        onClick={() => setShopViewTab('orders')}
                                        className={`shop-category-tab ${shopViewTab === 'orders' ? 'is-active' : ''}`}
                                    >
                                        <span>My Orders</span>
                                        <span className="shop-category-count">{myMarketplaceOrders.length}</span>
                                    </button>
                                </div>

                                {shopViewTab === 'browse' && (
                                <>
                                {/* Shop header + category tabs */}
                                <div className="card" style={{ marginBottom: '0', borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: marketplaceCategories.length > 0 ? '16px' : '0' }}>
                                        <div>
                                            <h3 className="card-title" style={{ margin: 0 }}>Shop</h3>
                                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                                                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                                                {shopCategoryFilter === NEW_FILTER
                                                    ? ' added recently'
                                                    : shopCategoryFilter !== 'all' ? ` in "${shopCategoryFilter}"` : ' available'}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="search"
                                                    value={shopSearchQuery}
                                                    onChange={(e) => setShopSearchQuery(e.target.value)}
                                                    placeholder="Search products…"
                                                    className="form-input"
                                                    style={{ width: '220px', paddingLeft: '32px' }}
                                                    aria-label="Search products"
                                                />
                                                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}>
                                                    <Icon name="search" size={14} />
                                                </span>
                                            </div>
                                            <button onClick={() => { fetchMarketplace(); fetchMyMarketplaceOrders(); }} disabled={loadingMarketplace} className="btn btn-secondary">
                                                {loadingMarketplace ? 'Refreshing…' : 'Refresh'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Category filter tabs */}
                                    {marketplaceCategories.length > 0 && (
                                        <div className="shop-category-tabs" aria-label="Shop categories">
                                            {categoryOptions.map(category => (
                                                <button key={category.name} type="button"
                                                    onClick={() => setShopCategoryFilter(category.name)}
                                                    className={`shop-category-tab ${shopCategoryFilter === category.name ? 'is-active' : ''}`}
                                                >
                                                    <span>{category.label}</span>
                                                    <span className="shop-category-count">{category.count}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Just dropped — hidden while searching or filtering, where it
                                    would only repeat what the grid below already shows. */}
                                {newArrivalProducts.length > 0 && shopCategoryFilter === 'all' && !shopSearchQuery.trim() && (
                                    <div className="shop-new-strip">
                                        <div className="shop-new-strip__head">
                                            <div>
                                                <h4 className="shop-new-strip__title">
                                                    <span className="shop-new-flag">NEW</span> Just dropped
                                                </h4>
                                                <p className="shop-new-strip__sub">
                                                    {newArrivalProducts.length} product{newArrivalProducts.length !== 1 ? 's' : ''} added recently.
                                                </p>
                                            </div>
                                            {newArrivalProducts.length > 6 && (
                                                <button type="button" className="btn btn-secondary"
                                                    style={{ padding: '6px 12px', fontSize: '13px' }}
                                                    onClick={() => setShopCategoryFilter(NEW_FILTER)}>
                                                    See all {newArrivalProducts.length}
                                                </button>
                                            )}
                                        </div>
                                        <div className="shop-new-rail">
                                            {newArrivalProducts.slice(0, 6).map(p => {
                                                const opts = getMarketplaceOptions(p);
                                                const prices = opts.map(o => o.priceGoldCoins).filter(v => Number.isFinite(v));
                                                const min = prices.length ? Math.min(...prices) : null;
                                                const max = prices.length ? Math.max(...prices) : null;
                                                return (
                                                    <button key={p.id} type="button" className="shop-new-item"
                                                        title={p.name}
                                                        onClick={() => { setSelectedMarketplaceProductId(p.id); setShopQuantity(1); setShopBuyerEmail(''); }}>
                                                        <span className="shop-new-item__media">
                                                            {p.imageUrl
                                                                ? <img src={productThumb(p, 320)} alt="" loading="lazy" decoding="async" onError={e => onThumbError(e, p.name)} />
                                                                : <Icon name="bag" size={22} />}
                                                        </span>
                                                        <span className="shop-new-item__name">{p.name}</span>
                                                        <span className="shop-new-item__price">
                                                            {min == null ? '\u2014'
                                                                : min === max ? `${formatCredits(min)} GC`
                                                                : `${formatCredits(min)}\u2013${formatCredits(max)} GC`}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Product grid */}
                                <div className="marketplace-shop-surface" style={{ background: 'var(--surface-primary)', borderRadius: '0 0 8px 8px', border: '1px solid #e2e8f0', borderTop: 'none', padding: '20px', marginBottom: '20px' }}>
                                    {loadingMarketplace && marketplaceProducts.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading products...</div>
                                    ) : filteredProducts.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                            <div style={{ fontSize: '40px', marginBottom: '12px' }}><Icon name="bag" size={16} /></div>
                                            <p>{shopSearchQuery.trim() ? `No products match "${shopSearchQuery.trim()}".` : shopCategoryFilter === 'all' ? 'No products available right now.' : `No products in "${shopCategoryFilter}".`}</p>
                                        </div>
                                    ) : (
                                        <div className="marketplace-shop-grid">
                                            {[...filteredProducts]
                                                .sort((a, b) => {
                                                    // Admin-pinned products win outright; the newest pin leads.
                                                    // Best sellers keep their existing precedence below that.
                                                    const pin = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
                                                    if (pin !== 0) return pin;
                                                    if (a.pinned && b.pinned) return new Date(b.pinnedAt || 0) - new Date(a.pinnedAt || 0);
                                                    return (bestSellerIds.has(b.id) ? 1 : 0) - (bestSellerIds.has(a.id) ? 1 : 0);
                                                })
                                                .map(p => {
                                                const options = getMarketplaceOptions(p);
                                                const totalStock = options.reduce((sum, opt) => sum + (opt.availableCount || 0), 0);
                                                const minPrice = options.length > 0 ? Math.min(...options.map(o => o.priceGoldCoins)) : 0;
                                                const maxPrice = options.length > 0 ? Math.max(...options.map(o => o.priceGoldCoins)) : 0;
                                                const priceSummary = options.length === 0 ? '—' : minPrice === maxPrice ? `${minPrice} GC` : `${minPrice}–${maxPrice} GC`;
                                                const isOutOfStock = totalStock === 0;
                                                const isBestSeller = bestSellerIds.has(p.id);
                                                const promo = p.promo && p.promo.discountPercent > 0 ? p.promo : null;
                                                const minOriginal = options.length > 0 ? Math.min(...options.map(o => o.originalPriceGoldCoins)) : 0;

                                                return (
                                                    <div key={p.id}
                                                        className={`marketplace-card marketplace-card-clickable${promo ? ' marketplace-card-onsale' : ''}`}
                                                        role="button" tabIndex={0}
                                                        onClick={() => { setSelectedMarketplaceProductId(p.id); setShopQuantity(1); setShopBuyerEmail(''); }}
                                                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedMarketplaceProductId(p.id); setShopQuantity(1); setShopBuyerEmail(''); } }}
                                                        style={{ opacity: isOutOfStock ? 0.6 : 1, ...(isBestSeller && !promo ? { border: '2px solid #f59e0b', boxShadow: '0 0 0 3px rgba(245,158,11,0.15)' } : {}) }}
                                                    >
                                                        {p.imageUrl ? (
                                                            <img src={productThumb(p, 480)} alt={p.name} className="marketplace-card-media" loading="lazy" decoding="async" onError={e => onThumbError(e, p.name)} />
                                                        ) : (
                                                            <div className="marketplace-card-media marketplace-card-placeholder" style={{ fontSize: '32px' }}><Icon name="bag" size={16} /></div>
                                                        )}
                                                        <div className="marketplace-card-body">
                                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                                                {promo && (
                                                                    <span className="cp-promo-badge" style={promo.badgeColor ? { background: promo.badgeColor } : undefined}>
                                                                        {promo.label} −{promo.discountPercent}%
                                                                    </span>
                                                                )}
                                                                {p.pinned && <span className="shop-pin-flag">FEATURED</span>}
                                                                {p.isNew && <span className="shop-new-flag">NEW</span>}
                                                                {isBestSeller && <span style={{ background: '#f59e0b', color: 'white', padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700 }}>Best Seller</span>}
                                                                {p.category && <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700 }}>{p.category}</span>}
                                                                {p.requiresEmail && <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700 }}>Email</span>}
                                                                {p.manualFulfill && <span className="mp-status mp-status-pending" title="Shipped by hand">Manual</span>}
                                                                {isOutOfStock && <span style={{ background: '#fee2e2', color: '#991b1b', padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700 }}>Out of Stock</span>}
                                                            </div>
                                                            <div className="marketplace-card-title-row">
                                                                <strong>{p.name}</strong>
                                                                <span className="marketplace-price-badge">
                                                                    {promo && minOriginal > minPrice && (
                                                                        <span className="cp-price-original" style={{ marginRight: '5px' }}>{minOriginal} GC</span>
                                                                    )}
                                                                    {priceSummary}
                                                                </span>
                                                            </div>
                                                            {promo && formatPromoCountdown(promo.endsAt) && (
                                                                <div className="cp-onsale-countdown" style={{ marginTop: '4px' }}>{formatPromoCountdown(promo.endsAt)}</div>
                                                            )}
                                                            {p.description && <p className="marketplace-card-summary" style={{ marginTop: '6px' }}>{p.description}</p>}
                                                            <div className="marketplace-card-footer" style={{ marginTop: 'auto' }}>
                                                                <span className={`marketplace-stock-text ${isOutOfStock ? 'is-out' : ''}`}>{totalStock} in stock</span>
                                                                <span className="marketplace-card-action-hint">View options</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                </>
                                )}

                                {/* My Orders */}
                                {shopViewTab === 'orders' && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">My Orders ({myMarketplaceOrders.length})</h3>
                                        <button onClick={fetchMyMarketplaceOrders} disabled={loadingMyOrders} className="btn btn-secondary">
                                            {loadingMyOrders ? 'Refreshing…' : 'Refresh'}
                                        </button>
                                    </div>
                                    {loadingMyOrders && myMarketplaceOrders.length === 0 ? (
                                        <p style={{ color: '#94a3b8', padding: '20px 0' }}>Loading orders...</p>
                                    ) : myMarketplaceOrders.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94a3b8' }}>
                                            <p>You haven't bought anything yet.</p>
                                        </div>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Product</th>
                                                        <th>Code / Status</th>
                                                        <th>Email</th>
                                                        <th>Duration</th>
                                                        <th>GC</th>
                                                        <th>Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {myMarketplaceOrders.map(o => (
                                                        <tr key={o.id}>
                                                            <td style={{ color: '#94a3b8', fontSize: '12px' }}>#{o.id}</td>
                                                            <td style={{ fontWeight: 600 }}>{o.productName}</td>
                                                            <td style={{ minWidth: '220px' }}>
                                                                {o.fulfillmentStatus === 'pending' ? (
                                                                    <>
                                                                        <span className="mp-status mp-status-pending">Pending Processing</span>
                                                                        {/* A hand-shipped product can still carry a code (often the
                                                                            instructions). Buyers used to receive it immediately, so
                                                                            keep showing it while the rest is being prepared. */}
                                                                        {o.code && (
                                                                            <div className="mp-delivered">
                                                                                <pre>{o.code}</pre>
                                                                            </div>
                                                                        )}
                                                                        <div className="mp-awaiting">We are preparing this order. The remaining details will appear here once it is completed.</div>
                                                                    </>
                                                                ) : o.fulfillmentStatus === 'completed' ? (
                                                                    <>
                                                                        <span className="mp-status mp-status-done">Completed</span>
                                                                        {o.code && (
                                                                            <div className="mp-delivered">
                                                                                <pre>{o.code}</pre>
                                                                            </div>
                                                                        )}
                                                                        <div className="mp-delivered">
                                                                            <span className="mp-delivered-label">Product information</span>
                                                                            <pre>{o.deliveredInfo}</pre>
                                                                            <button onClick={() => { navigator.clipboard.writeText(o.deliveredInfo || ''); setResult('Details copied!'); }}
                                                                                style={{ marginTop: '6px', background: 'none', border: '1px solid var(--app-border)', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '12px', color: 'var(--app-text)' }}>
                                                                                <Icon name="clipboard" size={14} /> Copy
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        <code style={{ fontSize: '11px', userSelect: 'all', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{o.code}</code>
                                                                        <button onClick={() => { navigator.clipboard.writeText(o.code); setResult('Code copied!'); }}
                                                                            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '12px' }}><Icon name="clipboard" size={16} /></button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td style={{ fontSize: '12px', color: o.buyerEmail ? '#334155' : '#94a3b8' }}>{o.buyerEmail || '—'}</td>
                                                            <td>{o.retentionMonths === 0 ? 'Lifetime' : `${o.retentionMonths}mo`}</td>
                                                            <td style={{ fontWeight: 600, color: '#d97706' }}>{o.priceGoldCoins ?? o.priceCredits}</td>
                                                            <td style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(o.purchasedAt).toLocaleDateString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                                )}
                            </>
                            )}
                        </>
                        );
                    })()}

                    {/* API Documentation Tab */}
                    {activeTab === 'apiDocs' && apiKeyInfo?.apiAccessEnabled && (
                        <>
                            {/* API Key Management Card */}
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">API Key Management</h3>
                                </div>

                                {newlyGeneratedKey && (
                                    <div style={{ padding: '16px', background: '#fef3c7', borderRadius: '8px', marginBottom: '16px', border: '2px solid #f59e0b' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '20px' }}><Icon name="key" size={16} /></span>
                                            <strong style={{ color: '#92400e' }}>Your new API key is ready! You can also view it later using the "View Key" button.</strong>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <code style={{ flex: 1, padding: '10px', background: '#1e293b', color: '#22d3ee', borderRadius: '6px', fontSize: '13px', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                                {newlyGeneratedKey}
                                            </code>
                                            <button onClick={() => copyToClipboard(newlyGeneratedKey)}
                                                className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                                                {apiKeyCopied ? 'Copied!' : 'Copy'}
                                            </button>
                                        </div>
                                        <button onClick={() => setNewlyGeneratedKey(null)}
                                            style={{ marginTop: '8px', background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>
                                            I've saved my key, dismiss this
                                        </button>
                                    </div>
                                )}

                                {/* Reveal Key Modal */}
                                {showRevealKeyModal && (
                                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                                        onClick={() => { setShowRevealKeyModal(false); setRevealedKey(null); }}>
                                        <div onClick={e => e.stopPropagation()}
                                            style={{ maxWidth: '500px', width: '90%', background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px' }}>
                                            <h3 style={{ marginBottom: '16px' }}>View API Key</h3>
                                            {!revealedKey ? (
                                                <>
                                                    <p style={{ color: '#475569', fontSize: '14px', marginBottom: '12px' }}>Enter your account password to view your API key.</p>
                                                    {revealError && (
                                                        <div style={{ padding: '10px', background: '#fef2f2', borderRadius: '6px', marginBottom: '12px', color: '#dc2626', fontSize: '13px' }}>
                                                            {revealError}
                                                        </div>
                                                    )}
                                                    <input
                                                        type="password"
                                                        className="form-input"
                                                        value={revealPassword}
                                                        onChange={e => setRevealPassword(e.target.value)}
                                                        placeholder="Enter your password"
                                                        onKeyDown={e => e.key === 'Enter' && revealApiKey()}
                                                        autoFocus
                                                    />
                                                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                                                        <button onClick={revealApiKey} disabled={loadingApiKey || !revealPassword}
                                                            className="btn btn-primary">
                                                            {loadingApiKey ? 'Verifying...' : 'Reveal Key'}
                                                        </button>
                                                        <button onClick={() => { setShowRevealKeyModal(false); setRevealedKey(null); }}
                                                            className="btn btn-secondary">Cancel</button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <p style={{ color: 'var(--app-success-text)', fontSize: '14px', marginBottom: '12px' }}>Your API key:</p>
                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                        <code style={{ flex: 1, padding: '10px', background: '#1e293b', color: '#22d3ee', borderRadius: '6px', fontSize: '13px', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                                            {revealedKey}
                                                        </code>
                                                        <button onClick={() => copyToClipboard(revealedKey)}
                                                            className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                                                            {apiKeyCopied ? 'Copied!' : 'Copy'}
                                                        </button>
                                                    </div>
                                                    <button onClick={() => { setShowRevealKeyModal(false); setRevealedKey(null); }}
                                                        className="btn btn-secondary" style={{ marginTop: '12px' }}>Close</button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {apiKeyInfo.hasKey ? (
                                    <div style={{ padding: '16px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                            <div>
                                                <p style={{ margin: 0, fontWeight: 600, color: 'var(--app-success-text)' }}>API Key Active</p>
                                                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#047857' }}>
                                                    Prefix: <code style={{ background: '#d1fae5', padding: '2px 6px', borderRadius: '4px' }}>{apiKeyInfo.keyInfo?.keyPrefix}</code>
                                                </p>
                                                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                                                    Created: {new Date(apiKeyInfo.keyInfo?.createdAt).toLocaleDateString()} •
                                                    Last used: {apiKeyInfo.keyInfo?.lastUsedAt ? new Date(apiKeyInfo.keyInfo.lastUsedAt).toLocaleString() : 'Never'} •
                                                    Total requests: {apiKeyInfo.keyInfo?.totalRequests || 0}
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                <button onClick={() => { setShowRevealKeyModal(true); setRevealedKey(null); setRevealError(null); setRevealPassword(''); }} disabled={loadingApiKey}
                                                    className="btn btn-primary" style={{ fontSize: '13px' }}>
                                                    View Key
                                                </button>
                                                <button onClick={revokeApiKey} disabled={loadingApiKey}
                                                    className="btn btn-danger" style={{ fontSize: '13px' }}>
                                                    {loadingApiKey ? 'Revoking…' : 'Revoke Key'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                                        <p style={{ color: '#475569', marginBottom: '12px' }}>No active API key. Generate one to start using the API.</p>
                                        <button onClick={generateApiKey} disabled={loadingApiKey}
                                            className="btn btn-primary">
                                            {loadingApiKey ? 'Generating...' : 'Generate API Key'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* API Usage Card */}
                            <div className="card" style={{ marginTop: '20px' }}>
                                <div className="card-header">
                                    <h3 className="card-title">API Usage</h3>
                                    <button onClick={fetchApiUsage} disabled={loadingApiUsage} className="btn btn-secondary">
                                        Refresh
                                    </button>
                                </div>

                                {loadingApiUsage ? (
                                    <div className="loading">Loading API usage...</div>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' }}>
                                            <div className="api-usage-stat api-usage-stat-blue" style={{ flex: '1 1 180px', padding: '12px 16px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                                                <div className="api-usage-stat-label" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Total API Requests</div>
                                                <div className="api-usage-stat-value" style={{ fontSize: '22px', fontWeight: 700 }}>{(apiKeyInfo?.keyInfo?.totalRequests || 0).toLocaleString()}</div>
                                                <div className="api-usage-stat-label" style={{ fontSize: '11px', marginTop: '2px' }}>
                                                    Last used: {apiKeyInfo?.keyInfo?.lastUsedAt ? new Date(apiKeyInfo.keyInfo.lastUsedAt).toLocaleString() : 'Never'}
                                                </div>
                                            </div>
                                            <div className="api-usage-stat api-usage-stat-green" style={{ flex: '1 1 180px', padding: '12px 16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                                <div className="api-usage-stat-label" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Customers Activated via API</div>
                                                <div className="api-usage-stat-value" style={{ fontSize: '22px', fontWeight: 700 }}>{(apiUsage?.totalActivatedViaApi || 0).toLocaleString()}</div>
                                                <div className="api-usage-stat-label" style={{ fontSize: '11px', marginTop: '2px' }}>
                                                    All-time, since you started using the API
                                                </div>
                                            </div>
                                            <div className="api-usage-stat api-usage-stat-amber" style={{ flex: '1 1 180px', padding: '12px 16px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                                                <div className="api-usage-stat-label" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>Currently Active</div>
                                                <div className="api-usage-stat-value" style={{ fontSize: '22px', fontWeight: 700 }}>{(apiUsage?.currentlyActiveViaApi || 0).toLocaleString()}</div>
                                                <div className="api-usage-stat-label" style={{ fontSize: '11px', marginTop: '2px' }}>
                                                    Still active right now
                                                </div>
                                            </div>
                                        </div>

                                        <h4 style={{ marginBottom: '12px' }}>Customers Activated via API ({(apiUsage?.users || []).length}{(apiUsage?.users || []).length === 200 ? '+' : ''})</h4>
                                        {(apiUsage?.users || []).length === 0 ? (
                                            <div className="empty-state">
                                                <div className="empty-text">No customers have been activated through the API yet</div>
                                            </div>
                                        ) : (
                                            <div style={{ overflowX: 'auto' }}>
                                                <table className="data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Email</th>
                                                            <th>Organization</th>
                                                            <th>Products</th>
                                                            <th>Duration</th>
                                                            <th>Activated</th>
                                                            <th>Expires</th>
                                                            <th>Credits</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(apiUsage?.users || []).map((u, idx) => (
                                                            <tr key={idx}>
                                                                <td style={{ fontSize: '12px', wordBreak: 'break-all' }}>{u.email}</td>
                                                                <td style={{ fontSize: '12px' }}>{u.organizationName || '—'}</td>
                                                                <td style={{ fontSize: '12px' }}>{(u.products || []).join(', ') || '—'}</td>
                                                                <td style={{ fontSize: '12px' }}>{u.durationDays ? `${u.durationDays}d` : u.durationMonths ? `${u.durationMonths}mo` : '—'}</td>
                                                                <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{u.activatedAt ? new Date(u.activatedAt).toLocaleDateString() : '—'}</td>
                                                                <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{u.expiresAt ? new Date(u.expiresAt).toLocaleDateString() : '—'}</td>
                                                                <td style={{ fontSize: '12px' }}>{formatCredits(u.creditsCost || 0)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* API Documentation Card */}
                            <div className="card" style={{ marginTop: '20px' }}>
                                <div className="card-header">
                                    <h3 className="card-title">API Documentation</h3>
                                    <button onClick={handleDownloadApiDocs} className="btn btn-secondary">
                                        ⬇ Download Docs
                                    </button>
                                </div>

                                <div style={{ padding: '4px 0' }}>
                                    {apiKeyInfo?.defaultOrganizationId && (
                                        <div style={{ padding: '12px', background: '#ecfdf5', borderRadius: '6px', fontSize: '13px', color: 'var(--app-success-text)', marginBottom: '18px' }}>
                                            <strong>Current API organization:</strong>{' '}
                                            {apiKeyInfo.defaultOrganizationName || `Organization #${apiKeyInfo.defaultOrganizationId}`}
                                            <span style={{ marginLeft: '6px', color: '#047857' }}>
                                                Omit organizationId in your API code and this target will update automatically when admin changes your access.
                                            </span>
                                        </div>
                                    )}

                                    {/* Authentication */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <h4 style={{ color: '#1e40af', borderBottom: '2px solid #dbeafe', paddingBottom: '8px', marginBottom: '12px' }}>Authentication</h4>
                                        <p style={{ color: '#475569', fontSize: '14px', marginBottom: '12px' }}>
                                            All API requests require your API key in the <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>x-api-key</code> header.
                                        </p>
                                        <p style={{ color: '#475569', fontSize: '14px', marginBottom: '12px' }}>
                                            For enhanced security, include a timestamp and HMAC signature:
                                        </p>
                                        <div style={{ background: '#1e293b', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                                            <pre style={{ color: '#e2e8f0', fontSize: '13px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`Headers:
  x-api-key: your_api_key_here
  x-timestamp: ${Date.now()} (Unix ms)
  x-signature: HMAC-SHA256(timestamp + body, api_key)
  Content-Type: application/json`}</pre>
                                        </div>
                                        <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '6px', fontSize: '13px', color: '#92400e' }}>
                                            <strong>Security Notes:</strong>
                                            <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                                                <li>Never expose your API key in client-side code or public repositories</li>
                                                <li>Timestamps must be within 5 minutes of server time</li>
                                                <li>Rate limit: {30} requests per minute</li>
                                                <li>API key is shown only once on generation - store it securely</li>
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Base URL */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <h4 style={{ color: '#1e40af', borderBottom: '2px solid #dbeafe', paddingBottom: '8px', marginBottom: '12px' }}>Base URL</h4>
                                        <div style={{ background: '#1e293b', borderRadius: '8px', padding: '12px' }}>
                                            <code style={{ color: '#22d3ee', fontSize: '14px' }}>{window.location.origin}/api/credits-pool/external</code>
                                        </div>
                                    </div>

                                    {/* Endpoints */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <h4 style={{ color: '#1e40af', borderBottom: '2px solid #dbeafe', paddingBottom: '8px', marginBottom: '12px' }}>Endpoints</h4>

                                        {/* POST /add-user */}
                                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                <span style={{ background: '#059669', color: 'white', padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}>POST</span>
                                                <code style={{ fontSize: '14px', fontWeight: 600 }}>/add-user</code>
                                            </div>
                                            <p style={{ color: '#475569', fontSize: '13px', marginBottom: '12px' }}>Add a user with duration. Use 1-12 for month plans, or "7d" / "14d" for short plans. Credits are deducted automatically. Products and organizationId are optional. Recommended: omit organizationId so admin can change your API organization without requiring code changes.</p>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Request Body:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "email": "user@example.com",
  "durationMonths": "14d",
  "products": [${organizationAccess[0]?.products?.[0] ? `"${organizationAccess[0].products[0]}"` : '"Product Name"'}]  // OPTIONAL - omit to assign all
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Success Response (200):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "success": true,
  "message": "User user@example.com activated for 14 days...",
  "user": {
    "email": "user@example.com",
    "organizationId": 1,
    "products": ["Product Name"],
    "durationMonths": null,
    "durationDays": 14,
    "durationKey": "14d",
    "activatedAt": "2026-03-06T...",
    "expiresAt": "2026-04-06T..."
  },
  "creditsRemaining": 999
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Error Response (400/401/429):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#fca5a5', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "error": "Insufficient credits. Required: 1, Available: 0"
}`}</pre>
                                            </div>
                                        </div>

                                        {/* GET /team-name */}
                                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                <span style={{ background: '#2563eb', color: 'white', padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}>GET</span>
                                                <code style={{ fontSize: '14px', fontWeight: 600 }}>/team-name?email=user@example.com</code>
                                            </div>
                                            <p style={{ color: '#475569', fontSize: '13px', marginBottom: '12px' }}>
                                                Look up the team/organization name for a customer email — the same name shown on the status page. Useful for displaying team info in your own portal.
                                            </p>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Query Parameter:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`email  (string, required)  —  The customer email to look up`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Success Response (200):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "found": true,
  "email": "user@example.com",
  "teamName": "Art Grid Dynamics Amelia Engineers",
  "organizationId": 12
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Not Found Response (404):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#fca5a5', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "found": false,
  "error": "No active subscription found for this email"
}`}</pre>
                                            </div>
                                        </div>

                                        {/* GET /org-status */}
                                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                <span style={{ background: '#2563eb', color: 'white', padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}>GET</span>
                                                <code style={{ fontSize: '14px', fontWeight: 600 }}>/org-status?email=user@example.com</code>
                                            </div>
                                            <p style={{ color: '#475569', fontSize: '13px', marginBottom: '12px' }}>
                                                Check whether a customer's organization is active or disabled, and whether a self-switch to a working organization is currently available — the same check that drives the "Switch organization" button on our status page. Use this to build the same flow into your own portal.
                                            </p>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Query Parameter:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`email  (string, required)  —  The customer email to look up`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Success Response (200) — organization disabled, switch available:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "found": true,
  "email": "user@example.com",
  "teamName": "Art Grid Dynamics Amelia Engineers",
  "organizationId": 12,
  "status": "inactive",
  "switchAvailable": true,
  "switchTargetTeamName": "Blue Sky Creative Group"
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Success Response (200) — organization active:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "found": true,
  "email": "user@example.com",
  "teamName": "Art Grid Dynamics Amelia Engineers",
  "organizationId": 12,
  "status": "active",
  "switchAvailable": false,
  "switchTargetTeamName": null
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Not Found Response (404):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#fca5a5', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "found": false,
  "error": "No active subscription found for this email"
}`}</pre>
                                            </div>
                                        </div>

                                        {/* POST /org-switch */}
                                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                <span style={{ background: '#dc2626', color: 'white', padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}>POST</span>
                                                <code style={{ fontSize: '14px', fontWeight: 600 }}>/org-switch</code>
                                            </div>
                                            <p style={{ color: '#475569', fontSize: '13px', marginBottom: '12px' }}>
                                                Move a customer into an available organization once <code>/org-status</code> reports <code>switchAvailable: true</code>. Only works while the customer's current organization is disabled. The customer keeps their remaining time and products — they just need to sign out of all Adobe apps and sign back in afterward to refresh their access. Limited to one switch per customer every 10 minutes.
                                            </p>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Request Body:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "email": "user@example.com"
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Success Response (200):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "success": true,
  "message": "Moved to Blue Sky Creative Group. Sign out of all Adobe apps and sign back in to refresh access.",
  "newOrganizationName": "Blue Sky Creative Group"
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Error Response (400) — nothing available right now:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#fca5a5', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "error": "No organization with available capacity right now. Please try again in a few minutes."
}`}</pre>
                                            </div>
                                        </div>

                                        {/* POST /delete-user */}
                                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                <span style={{ background: '#dc2626', color: 'white', padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}>POST</span>
                                                <code style={{ fontSize: '14px', fontWeight: 600 }}>/delete-user</code>
                                            </div>
                                            <p style={{ color: '#475569', fontSize: '13px', marginBottom: '12px' }}>
                                                Delete an active user from your account. Applies the same refund policy as the panel: inside your account's grace period (2h by default — your administrator can change it, or switch it off entirely) the credits paid are refunded in full. Deleting an email you already deleted inside the grace period and re-added within 30 days refunds nothing if it is deleted again within that period; after it, it is refunded like any other user. Outside it you receive either pro-rata refund credits into your pending balance or refund days, depending on how your administrator has configured refunds. The user is also removed from Adobe automatically.
                                            </p>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Request Body:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "email": "user@example.com"
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Success Response (200) — deleted inside the grace period:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "success": true,
  "message": "User user@example.com deleted. 1 credit(s) refunded (deleted within the 2h grace period).",
  "refunded": true,
  "creditsRefunded": 1,
  "refundDaysAccumulated": 0,
  "refundDaysBalance": 0,
  "creditsRemaining": 18.4
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Success Response (200) — deleted after 24 h:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "success": true,
  "message": "User user@example.com deleted. 45 refund day(s) added to your balance.",
  "refunded": false,
  "creditsRefunded": 0,
  "refundDaysAccumulated": 45,
  "refundDaysBalance": 45,
  "creditsRemaining": 18.4
}`}</pre>
                                            </div>

                                            <strong style={{ fontSize: '13px', color: '#374151' }}>Error Response (404):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '6px', padding: '12px', margin: '8px 0' }}>
                                                <pre style={{ color: '#fca5a5', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`{
  "error": "User not found or already deleted."
}`}</pre>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Code Examples */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <h4 style={{ color: '#1e40af', borderBottom: '2px solid #dbeafe', paddingBottom: '8px', marginBottom: '12px' }}>Code Examples</h4>

                                        {/* cURL */}
                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — Add User:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl -X POST "${window.location.origin}/api/credits-pool/external/add-user" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "user@example.com",
    "durationMonths": "7d"
  }'`}</pre>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — Get Team Name:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl "${window.location.origin}/api/credits-pool/external/team-name?email=user@example.com" \\
  -H "x-api-key: YOUR_API_KEY"`}</pre>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — Check Org Status / Switch Availability:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl "${window.location.origin}/api/credits-pool/external/org-status?email=user@example.com" \\
  -H "x-api-key: YOUR_API_KEY"`}</pre>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — Switch Organization:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl -X POST "${window.location.origin}/api/credits-pool/external/org-switch" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "user@example.com"
  }'`}</pre>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — Extend a Licence:</strong>
                                            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px', lineHeight: 1.6 }}>
                                                Adds time to an existing user. <code>extensionMonths</code> accepts <code>1</code>–<code>12</code>,
                                                or <code>"7d"</code> / <code>"14d"</code>. Credits are charged at the same rate as the dashboard.
                                                An already-expired licence restarts from today rather than back-dating the time.
                                            </p>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl -X POST "${window.location.origin}/api/credits-pool/external/extend-user" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "user@example.com",
    "extensionMonths": 2
  }'`}</pre>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — Change Email Address:</strong>
                                            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px', lineHeight: 1.6 }}>
                                                Moves a subscription onto a new address — for when Adobe deactivates a customer's account.
                                                Omit <code>organizationId</code> to keep the current team when it still has room, otherwise the
                                                team with the most free seats is chosen. Destinations always match the plan's tier (K12 stays on
                                                K12, Pro stays on Pro).
                                            </p>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl -X POST "${window.location.origin}/api/credits-pool/external/change-email" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "old@example.com",
    "newEmail": "new@example.com"
  }'`}</pre>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — List Teams a Subscription Can Move To:</strong>
                                            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px', lineHeight: 1.6 }}>
                                                Optional. Call this first if you want to pick the destination yourself, then pass the chosen
                                                <code> id</code> as <code>organizationId</code> to change-email.
                                            </p>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl "${window.location.origin}/api/credits-pool/external/email-change-options?email=old@example.com" \\
  -H "x-api-key: YOUR_API_KEY"`}</pre>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>cURL — Delete User:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px', position: 'relative' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`curl -X POST "${window.location.origin}/api/credits-pool/external/delete-user" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{
    "email": "user@example.com"
  }'`}</pre>
                                            </div>
                                        </div>

                                        {/* Python */}
                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>Python:</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`import requests, time, hmac, hashlib, json

API_KEY = "YOUR_API_KEY"
BASE_URL = "${window.location.origin}/api/credits-pool/external"

def add_user(email, months):
    timestamp = str(int(time.time() * 1000))
    body = {
        "email": email,
        "durationMonths": months
    }
    payload = timestamp + json.dumps(body)
    signature = hmac.new(
        API_KEY.encode(), payload.encode(), hashlib.sha256
    ).hexdigest()
    
    headers = {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
        "x-timestamp": timestamp,
        "x-signature": signature
    }
    resp = requests.post(f"{BASE_URL}/add-user",
                         json=body, headers=headers)
    return resp.json()

# Example usage
result = add_user("user@example.com", 1)
print(result)`}</pre>
                                            </div>
                                        </div>

                                        {/* JavaScript/Node.js */}
                                        <div style={{ marginBottom: '16px' }}>
                                            <strong style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>JavaScript (Node.js):</strong>
                                            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '14px' }}>
                                                <pre style={{ color: '#e2e8f0', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{`const crypto = require('crypto');

const API_KEY = 'YOUR_API_KEY';
const BASE = '${window.location.origin}/api/credits-pool/external';

async function addUser(email, months) {
  const timestamp = Date.now().toString();
  const body = { email,
                  durationMonths: months };
  const payload = timestamp + JSON.stringify(body);
  const signature = crypto.createHmac('sha256', API_KEY)
    .update(payload).digest('hex');

  const res = await fetch(BASE + '/add-user', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      'x-timestamp': timestamp,
      'x-signature': signature
    },
    body: JSON.stringify(body)
  });
  return res.json();
}

// Example
addUser('user@example.com', 1)
  .then(console.log);

async function getTeamName(email) {
  const res = await fetch(
    BASE + '/team-name?email=' + encodeURIComponent(email),
    { headers: { 'x-api-key': API_KEY } }
  );
  return res.json();
}

// Example
getTeamName('user@example.com')
  .then(console.log);`}</pre>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Error Codes */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <h4 style={{ color: '#1e40af', borderBottom: '2px solid #dbeafe', paddingBottom: '8px', marginBottom: '12px' }}>Error Codes</h4>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>HTTP Code</th>
                                                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Meaning</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[
                                                    ['200', 'OK – Request succeeded'],
                                                    ['400', 'Bad Request – Missing fields, invalid email, duplicate user, insufficient credits, expired pool'],
                                                    ['401', 'Unauthorized – Invalid or missing API key'],
                                                    ['403', 'Forbidden – API access disabled by admin'],
                                                    ['404', 'Not Found – No active subscription found for this email'],
                                                    ['409', 'Conflict – User is already being processed (duplicate request)'],
                                                    ['429', 'Too Many Requests – Rate limit exceeded (30 req/min)'],
                                                    ['500', 'Server Error – Adobe API failure (may be retryable)']
                                                ].map(([code, meaning]) => (
                                                    <tr key={code} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '8px 12px', fontWeight: 600, color: parseInt(code) >= 500 ? '#dc2626' : parseInt(code) >= 400 ? '#f59e0b' : '#059669' }}>{code}</td>
                                                        <td style={{ padding: '8px 12px', color: '#475569' }}>{meaning}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'feedback' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Feedback</h3>
                                <button onClick={fetchMyFeedback} disabled={loadingFeedback} className="btn btn-secondary">
                                    Refresh
                                </button>
                            </div>

                            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
                                <h4 style={{ marginTop: 0, marginBottom: '16px' }}>Report a bug or suggest an improvement</h4>
                                <form onSubmit={handleSubmitFeedback}>
                                    <div className="form-group">
                                        <label className="form-label">Type</label>
                                        <select
                                            className="form-input"
                                            value={feedbackType}
                                            onChange={e => setFeedbackType(e.target.value)}
                                        >
                                            <option value="suggestion">Suggestion</option>
                                            <option value="bug">Bug Report</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Message</label>
                                        <textarea
                                            className="form-input"
                                            value={feedbackMessage}
                                            onChange={e => setFeedbackMessage(e.target.value)}
                                            placeholder="Describe the bug or your suggestion in detail..."
                                            rows={4}
                                            maxLength={2000}
                                            required
                                        />
                                    </div>
                                    <div style={{ textAlign: 'right', marginTop: '16px' }}>
                                        <button type="submit" disabled={submittingFeedback || !feedbackMessage.trim()} className="btn btn-primary">
                                            {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            <h4 style={{ marginBottom: '12px' }}>Your Past Feedback ({myFeedback.length})</h4>
                            {loadingFeedback ? (
                                <div className="loading">Loading feedback...</div>
                            ) : myFeedback.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-text">You haven't submitted any feedback yet</div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {myFeedback.map((fb) => {
                                        const statusColors = {
                                            new: { bg: '#fef3c7', color: '#92400e' },
                                            reviewed: { bg: '#dbeafe', color: '#1e40af' },
                                            resolved: { bg: '#dcfce7', color: '#166534' },
                                            dismissed: { bg: '#f1f5f9', color: '#64748b' }
                                        };
                                        const sc = statusColors[fb.status] || statusColors.new;
                                        const isUnseenReply = !!(fb.adminNote && !fb.resellerSeenAt);
                                        return (
                                            <div key={fb.id} style={{
                                                padding: '14px',
                                                background: isUnseenReply ? '#fffbeb' : 'white',
                                                borderRadius: '8px',
                                                border: isUnseenReply ? '1px solid #fbbf24' : '1px solid #e2e8f0',
                                                borderLeft: `4px solid ${fb.type === 'bug' ? '#ef4444' : '#3b82f6'}`,
                                                boxShadow: isUnseenReply ? '0 0 0 1px #fbbf24' : 'none'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                                    <span className="badge" style={{ background: fb.type === 'bug' ? '#fee2e2' : '#dbeafe', color: fb.type === 'bug' ? '#991b1b' : '#1e40af', fontSize: '10px' }}>
                                                        {fb.type === 'bug' ? 'Bug' : 'Suggestion'}
                                                    </span>
                                                    <span className="badge" style={{ background: sc.bg, color: sc.color, fontSize: '10px', textTransform: 'capitalize' }}>
                                                        {fb.status}
                                                    </span>
                                                    {isUnseenReply && (
                                                        <span className="badge" style={{ background: '#f59e0b', color: 'white', fontSize: '10px', fontWeight: 700 }}>
                                                            New reply
                                                        </span>
                                                    )}
                                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                        {new Date(fb.createdAt).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div style={{ whiteSpace: 'pre-wrap', color: '#475569', fontSize: '14px' }}>
                                                    {fb.message}
                                                </div>
                                                {fb.adminNote && (
                                                    <div style={{ marginTop: '8px', padding: '8px 10px', background: '#f0f9ff', borderRadius: '6px', fontSize: '13px', color: '#0369a1' }}>
                                                        <strong>Admin response:</strong> {fb.adminNote}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'branding' && (
                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title">Status Page Branding</h3>
                                <button onClick={fetchBranding} disabled={loadingBranding} className="btn btn-secondary">
                                    Refresh
                                </button>
                            </div>

                            <p style={{ color: '#64748b', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
                                Customize what your customers see on the public subscription status page. Changes apply
                                once a customer looks up an email that belongs to your organization.
                            </p>

                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 320px', gap: '24px', alignItems: 'start' }}>
                                <form onSubmit={handleSaveBranding}>
                                    <div className="form-group">
                                        <label className="form-label">Header Title</label>
                                        <input
                                            className="form-input"
                                            type="text"
                                            value={brandingForm.title}
                                            onChange={e => setBrandingForm({ ...brandingForm, title: e.target.value })}
                                            placeholder="Check Your Subscription"
                                            maxLength={80}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Header Subtitle</label>
                                        <input
                                            className="form-input"
                                            type="text"
                                            value={brandingForm.subtitle}
                                            onChange={e => setBrandingForm({ ...brandingForm, subtitle: e.target.value })}
                                            placeholder="Enter your email to view your Adobe subscription status"
                                            maxLength={160}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Brand Color (buttons)</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <input
                                                type="color"
                                                value={brandingForm.brandColor || '#ffa239'}
                                                onChange={e => setBrandingForm({ ...brandingForm, brandColor: e.target.value })}
                                                style={{ width: '44px', height: '38px', padding: '2px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer' }}
                                            />
                                            <input
                                                className="form-input"
                                                type="text"
                                                value={brandingForm.brandColor}
                                                onChange={e => setBrandingForm({ ...brandingForm, brandColor: e.target.value })}
                                                placeholder="#ffa239 (default)"
                                                style={{ flex: 1 }}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Header Logo</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                {branding.logoUrl ? <img src={`${API_URL}${branding.logoUrl}`} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '24px' }}><Icon name="clipboard" size={16} /></span>}
                                            </div>
                                            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                                                {uploadingLogo ? 'Uploading...' : 'Upload'}
                                                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={uploadingLogo}
                                                    onChange={e => { if (e.target.files[0]) handleBrandingImageUpload('logo', e.target.files[0]); e.target.value = ''; }} />
                                            </label>
                                            {branding.logoUrl && (
                                                <button type="button" className="btn btn-secondary" onClick={() => handleRemoveBrandingImage('logo')}>Remove</button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Organization Icon</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                {branding.orgIconUrl ? <img src={`${API_URL}${branding.orgIconUrl}`} alt="Org icon" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '24px' }}><Icon name="building" size={16} /></span>}
                                            </div>
                                            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                                                {uploadingOrgIcon ? 'Uploading...' : 'Upload'}
                                                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={uploadingOrgIcon}
                                                    onChange={e => { if (e.target.files[0]) handleBrandingImageUpload('orgIcon', e.target.files[0]); e.target.value = ''; }} />
                                            </label>
                                            {branding.orgIconUrl && (
                                                <button type="button" className="btn btn-secondary" onClick={() => handleRemoveBrandingImage('orgIcon')}>Remove</button>
                                            )}
                                        </div>
                                    </div>

                                    {canEditCustomerStatusNotice && (
                                        <div className="form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
                                                <label className="form-label" style={{ margin: 0 }}>Customer Notices</label>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => setBrandingForm({ ...brandingForm, noticeRules: [...(brandingForm.noticeRules || []), createNoticeRule()] })}
                                                >
                                                    + Add Notice
                                                </button>
                                            </div>
                                            {(brandingForm.noticeRules || []).length === 0 && (
                                                <div style={{ padding: '12px', borderRadius: '8px', border: '1px dashed #334155', color: '#94a3b8', fontSize: '13px' }}>
                                                    No customer notices are active.
                                                </div>
                                            )}
                                            {(brandingForm.noticeRules || []).map((rule, index) => (
                                                <div key={rule.id || index} style={{ padding: '14px', border: '1px solid #334155', borderRadius: '8px', marginBottom: '12px', background: 'rgba(15,23,42,0.35)' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                                                        <select
                                                            className="form-input"
                                                            value={rule.scope}
                                                            onChange={e => {
                                                                const nextRules = (brandingForm.noticeRules || []).map(item =>
                                                                    item.id === rule.id ? { ...item, scope: e.target.value, organizationIds: e.target.value === 'all' ? [] : item.organizationIds } : item
                                                                );
                                                                setBrandingForm({ ...brandingForm, noticeRules: nextRules });
                                                            }}
                                                        >
                                                            <option value="all">All customers</option>
                                                            <option value="organizations">Selected organizations</option>
                                                        </select>
                                                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                                                            {rule.scope === 'all'
                                                                ? 'Shown to every customer.'
                                                                : `${(rule.organizationIds || []).length} organization(s) selected.`}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger"
                                                            onClick={() => setBrandingForm({ ...brandingForm, noticeRules: (brandingForm.noticeRules || []).filter(item => item.id !== rule.id) })}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                    {rule.scope === 'organizations' && (
                                                        <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #334155', borderRadius: '8px', padding: '8px', marginBottom: '10px' }}>
                                                            {organizationAccess.map(org => {
                                                                const orgId = getNoticeOrgId(org);
                                                                const checked = (rule.organizationIds || []).map(Number).includes(orgId);
                                                                return (
                                                                    <label key={orgId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 4px', color: 'var(--app-text-soft)', fontSize: '13px', cursor: 'pointer' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={checked}
                                                                            onChange={() => {
                                                                                const currentIds = new Set((rule.organizationIds || []).map(Number));
                                                                                if (currentIds.has(orgId)) currentIds.delete(orgId);
                                                                                else currentIds.add(orgId);
                                                                                const nextRules = (brandingForm.noticeRules || []).map(item =>
                                                                                    item.id === rule.id ? { ...item, organizationIds: [...currentIds] } : item
                                                                                );
                                                                                setBrandingForm({ ...brandingForm, noticeRules: nextRules });
                                                                            }}
                                                                        />
                                                                        <span>{getNoticeOrgName(org)}</span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    <textarea
                                                        className="form-input"
                                                        value={rule.message}
                                                        onChange={e => {
                                                            const nextRules = (brandingForm.noticeRules || []).map(item =>
                                                                item.id === rule.id ? { ...item, message: e.target.value } : item
                                                            );
                                                            setBrandingForm({ ...brandingForm, noticeRules: nextRules });
                                                        }}
                                                        placeholder="Write the notice for this target"
                                                        maxLength={4000}
                                                        rows={4}
                                                        style={{ resize: 'vertical', fontFamily: 'inherit' }}
                                                    />
                                                </div>
                                            ))}
                                            <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>
                                                Organization-specific notices override the all-customers notice on the customer status page.
                                            </p>
                                        </div>
                                    )}

                                    <div style={{ textAlign: 'right', marginTop: '16px' }}>
                                        <button type="submit" disabled={savingBranding} className="btn btn-primary">
                                            {savingBranding ? 'Saving...' : 'Save Branding'}
                                        </button>
                                    </div>
                                </form>

                                <div>
                                    <div className="form-label" style={{ marginBottom: '8px' }}>Preview</div>
                                    <div style={{ borderRadius: '12px', padding: '24px 16px', background: '#eef8fd', border: '1px solid #d9e6ed', textAlign: 'center' }}>
                                        <div style={{ width: '56px', height: '56px', background: '#ffffff', border: '1px solid #d9e6ed', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', overflow: 'hidden' }}>
                                            {branding.logoUrl ? <img src={`${API_URL}${branding.logoUrl}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '28px' }}><Icon name="clipboard" size={16} /></span>}
                                        </div>
                                        <div style={{ color: '#16232d', fontWeight: 700, fontSize: '18px', marginBottom: '6px' }}>{brandingForm.title || 'Check Your Subscription'}</div>
                                        <div style={{ color: '#5d6d79', fontSize: '12px', marginBottom: '16px' }}>{brandingForm.subtitle || 'Enter your email to view your Adobe subscription status'}</div>
                                        <div style={{ padding: '10px', borderRadius: '10px', background: brandingForm.brandColor || '#ffa239', color: brandingForm.brandColor ? 'white' : '#3d2200', fontSize: '13px', fontWeight: 600 }}>
                                            Check Status
                                        </div>
                                    </div>
                                    {canEditCustomerStatusNotice && (brandingForm.noticeRules || []).some(rule => String(rule.message || '').trim()) && (
                                        <div style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', background: '#fdf6d8', border: '1px solid #e7cf55' }}>
                                            <p style={{ margin: 0, color: '#7c2d12', fontSize: '11px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                                {((brandingForm.noticeRules || []).find(rule => rule.scope === 'all' && String(rule.message || '').trim())
                                                    || (brandingForm.noticeRules || []).find(rule => String(rule.message || '').trim())
                                                )?.message}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
            {transferringUser && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={() => setTransferringUser(null)}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '450px', width: '90%', background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px' }}>
                        <h3 style={{ marginBottom: '16px' }}>Transfer License to New Email</h3>

                        <div style={{ padding: '12px', background: '#dbeafe', borderRadius: '6px', marginBottom: '16px' }}>
                            <strong style={{ color: '#1e40af' }}>Current User:</strong>
                            <p style={{ margin: '8px 0 0', color: '#1e40af' }}>{transferringUser.email}</p>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#3b82f6' }}>
                                Duration: {getDurationLabel(transferringUser.durationKey, transferringUser)} • Expires: {new Date(transferringUser.expiresAt).toLocaleDateString()}
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#3b82f6' }}>
                                Current Org: {transferringUser.organizationName
                                    || organizationAccess.find(o => String(o.organizationId) === String(transferringUser.organizationId))?.organizationName
                                    || `Organization ${transferringUser.organizationId}`}
                            </p>
                        </div>

                        <div style={{ padding: '12px', background: 'var(--app-success-bg)', borderRadius: '6px', marginBottom: '16px', border: '1px solid rgba(27, 138, 90, 0.35)' }}>
                            <strong style={{ color: 'var(--app-success-text)' }}>No credits will be charged</strong>
                            <p style={{ fontSize: '12px', color: 'var(--app-success-text)', marginTop: '4px' }}>
                                The license duration and expiration date will remain the same.
                            </p>
                        </div>

                        {/* The admin switched email changes to "records only": the new address is
                            not added to Adobe by this window, so say so before the reseller confirms. */}
                        {stats.emailChangeWithoutApi === true && (
                            <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '6px', marginBottom: '16px', border: '1px solid #fde68a' }}>
                                <strong style={{ color: '#92400e' }}>Saved in the records only</strong>
                                <p style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                                    The new email is not added to Adobe automatically. It has to be added in Adobe separately, and the license will not work until that is done.
                                </p>
                            </div>
                        )}

                        {/* Destination team — the new address can land on a different org.
                            Only teams of the same tier are offered, or the server refuses
                            the move after the fact. Hidden in records-only mode: a move
                            to another team needs Adobe. */}
                        {canUseCPTransfer && stats.emailChangeWithoutApi !== true && (() => {
                            const currentOrgId = String(transferringUser.organizationId ?? '');
                            const currentAccess = organizationAccess.find(o => String(o.organizationId) === currentOrgId);
                            const userIsK12 = transferringUser.k12 === true || isOrgK12(transferringUser.organizationId);
                            const otherOrgs = organizationAccess
                                .filter(o => String(o.organizationId) !== currentOrgId)
                                .filter(o => isZelyxmain ? true : (userIsK12 ? o.k12 === true : o.k12 !== true));

                            if (otherOrgs.length === 0) return null;

                            const selectedOrgId = String(transferTargetOrg || currentOrgId);
                            const isOrgChange = selectedOrgId !== currentOrgId;
                            const currentOrgName = currentAccess?.organizationName
                                || transferringUser.organizationName
                                || `Organization ${currentOrgId}`;
                            const targetOrgName = isOrgChange
                                ? (otherOrgs.find(o => String(o.organizationId) === selectedOrgId)?.organizationName || 'the selected team')
                                : currentOrgName;

                            return (
                                <div className="form-group" style={{ marginBottom: '16px' }}>
                                    <label className="form-label">Destination Organization</label>
                                    <select
                                        className="form-input"
                                        value={selectedOrgId}
                                        onChange={e => setTransferTargetOrg(e.target.value)}
                                    >
                                        <option value={currentOrgId}>{currentOrgName} (current)</option>
                                        {otherOrgs.map(o => (
                                            <option key={o.organizationId} value={String(o.organizationId)}>
                                                {o.organizationName}
                                            </option>
                                        ))}
                                    </select>
                                    {isOrgChange ? (
                                        <div style={{ marginTop: '8px', padding: '10px', background: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                                            <strong style={{ fontSize: '12px', color: '#9a3412' }}>This also moves the license</strong>
                                            <p style={{ fontSize: '11px', color: '#9a3412', margin: '4px 0 0' }}>
                                                {transferringUser.email} is removed from {currentOrgName} and {newEmailForTransfer || 'the new address'} is created in {targetOrgName}. Duration and expiry stay the same.
                                            </p>
                                        </div>
                                    ) : (
                                        <small style={{ color: '#64748b', display: 'block', marginTop: '4px' }}>
                                            Keep the current team, or pick another one to change the email and move the license in one step.
                                        </small>
                                    )}
                                    {!isZelyxmain && userIsK12 && (
                                        <small style={{ color: 'var(--app-link)', display: 'block', marginTop: '4px' }}>
                                            This user is on a K12 team, so only K12 teams are listed.
                                        </small>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Product Mapping - Allow user to map each old product to a product they have access to */}
                        {transferringUser && (() => {
                            let allowedProducts = getAllowedProductsForOrg(transferTargetOrg || transferringUser.organizationId);
                            const userProducts = transferringUser.products || [];
                            const noOrgAccess = allowedProducts.length === 0;

                            // Fallback: use all products from all accessible orgs when user's org has no access
                            if (noOrgAccess) {
                                allowedProducts = getAllAllowedProducts();
                            }

                            // If still no products at all, show info message
                            if (allowedProducts.length === 0) {
                                return (
                                    <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', borderRadius: '6px', border: '1px solid rgba(96, 165, 250, 0.45)' }}>
                                        <strong style={{ fontSize: '13px', color: '#1e40af', display: 'block', marginBottom: '6px' }}>Using existing products</strong>
                                        <p style={{ fontSize: '11px', color: '#1e40af', margin: 0 }}>
                                            No products available. The new email will keep the same products as the current user.
                                        </p>
                                    </div>
                                );
                            }

                            const includedIndices = userProducts.map((_, i) => i).filter(i => !transferRemovedIndices.has(i));
                            const includedProducts = includedIndices.map(i => userProducts[i]);
                            const includedMappings = includedIndices.map(i => transferProductMapping[i]);

                            const needsMapping = includedProducts.some((p, idx) => !allowedProducts.includes(p) || !includedMappings[idx]);
                            const hasUnmapped = includedMappings.some((m) => !m);
                            const canRemoveLicense = includedIndices.length > 1; // Keep at least 1 license

                            return (
                                <div style={{ marginBottom: '16px', padding: '12px', background: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                                    <strong style={{ fontSize: '13px', color: '#9a3412', display: 'block', marginBottom: '8px' }}>Map Products (select target for each license):</strong>
                                    <p style={{ fontSize: '11px', color: '#9a3412', marginBottom: '12px' }}>
                                        {noOrgAccess
                                            ? 'This organization has no admin-assigned products. Select a product from your other available products.'
                                            : needsMapping || hasUnmapped
                                                ? 'Some products are not available for your account. Select which product each license should be mapped to from your available products.'
                                                : 'Optional: Change the target product for each license if needed.'}
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {includedIndices.map((origIdx, displayIdx) => (
                                            <div key={origIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '12px', color: '#64748b', minWidth: '120px' }}>
                                                    License {displayIdx + 1}: {userProducts[origIdx]}
                                                </span>
                                                <span style={{ color: '#9a3412' }}>→</span>
                                                <select
                                                    className="form-input"
                                                    value={transferProductMapping[origIdx] || ''}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setTransferProductMapping(prev => {
                                                            const next = [...prev];
                                                            next[origIdx] = val;
                                                            return next;
                                                        });
                                                    }}
                                                    style={{ flex: 1, minWidth: '180px', fontSize: '12px', padding: '6px 8px' }}
                                                >
                                                    <option value="">-- Select product to map to --</option>
                                                    {allowedProducts.map(ap => (
                                                        <option key={ap} value={ap}>{ap}</option>
                                                    ))}
                                                </select>
                                                {canRemoveLicense && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setTransferRemovedIndices(prev => new Set([...prev, origIdx]))}
                                                        title="Remove this license from transfer"
                                                        style={{ padding: '4px 8px', fontSize: '11px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                                {(!transferProductMapping[origIdx] && hasUnmapped) && (
                                                    <span style={{ fontSize: '11px', color: '#dc2626' }}>Required</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        <form onSubmit={handleTransferEmail}>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label className="form-label">New Email Address</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={newEmailForTransfer}
                                    onChange={e => setNewEmailForTransfer(e.target.value)}
                                    placeholder="newuser@example.com"
                                    required
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button type="submit" disabled={loading || !newEmailForTransfer}
                                    className="btn btn-primary" style={{ flex: 1 }}>
                                    {loading ? 'Transferring...' : 'Transfer License'}
                                </button>
                                <button type="button" onClick={() => setTransferringUser(null)}
                                    className="btn btn-secondary">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Transfer Modal */}
            {showBulkTransferModal && canUseCPBulkTransfer && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={() => setShowBulkTransferModal(false)}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '500px', width: '90%', background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3 style={{ marginBottom: '16px' }}>Bulk Transfer Users</h3>

                        <div style={{ padding: '12px', background: '#dbeafe', borderRadius: '6px', marginBottom: '16px' }}>
                            <strong style={{ color: '#1e40af' }}>Selected Users:</strong>
                            <p style={{ margin: '8px 0 0', color: '#1e40af', fontWeight: 'bold' }}>{selectedUsersForBulk.length} users</p>
                        </div>

                        <div style={{ padding: '12px', background: '#fff7ed', borderRadius: '6px', marginBottom: '16px' }}>
                            <strong style={{ color: '#9a3412' }}>Warning</strong>
                            <p style={{ fontSize: '12px', color: '#9a3412', marginTop: '4px' }}>
                                {stats.transferSourceOrgAction === 'keep'
                                    ? 'This will add the selected users to the new target organization while keeping their current organization(s).'
                                    : 'This will remove the selected users from their current organization(s) and add them to the new target organization.'}
                                Licenses and expiration dates will be preserved.
                            </p>
                        </div>

                        <form onSubmit={handleBulkTransfer}>
                            <div className="form-group">
                                <label className="form-label">Target Organization</label>
                                <select className="form-input"
                                    value={selectedTransferOrgForBulk}
                                    onChange={(e) => {
                                        setSelectedTransferOrgForBulk(e.target.value);
                                        setSelectedTransferProductsForBulk([]);
                                    }}
                                    required>
                                    <option value="">-- Select Organization --</option>
                                    {organizationAccess.map(org => (
                                        <option key={org.organizationId} value={org.organizationId}>
                                            {org.organizationName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedTransferOrgForBulk && (
                                <div className="form-group">
                                    <label className="form-label">Products to Assign</label>
                                    {bulkDerivedProducts.length === 0 ? (
                                        <p style={{ color: '#dc2626', fontSize: '13px' }}>No products available in this organization</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                                            {bulkDerivedProducts.map((product, idx) => (
                                                <label key={idx} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px',
                                                    background: selectedTransferProductsForBulk.includes(product) ? '#dbeafe' : '#f8fafc',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer'
                                                }}>
                                                    <input type="checkbox"
                                                        checked={selectedTransferProductsForBulk.includes(product)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) setSelectedTransferProductsForBulk([...selectedTransferProductsForBulk, product]);
                                                            else setSelectedTransferProductsForBulk(selectedTransferProductsForBulk.filter(p => p !== product));
                                                        }} />
                                                    {product}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                                <button type="submit"
                                    disabled={!selectedTransferOrgForBulk || loading}
                                    className="btn btn-primary" style={{ flex: 1 }}>
                                    {loading ? 'Processing...' : 'Start Transfer'}
                                </button>
                                <button type="button"
                                    onClick={() => setShowBulkTransferModal(false)}
                                    className="btn btn-secondary">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {transferringOrgUser && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={() => setTransferringOrgUser(null)}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '450px', width: '90%', background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px' }}>
                        <h3 style={{ marginBottom: '16px' }}>Transfer to Different Organization</h3>

                        <div style={{ padding: '12px', background: '#dbeafe', borderRadius: '6px', marginBottom: '16px' }}>
                            <strong style={{ color: '#1e40af' }}>User:</strong>
                            <p style={{ margin: '8px 0 0', color: '#1e40af' }}>{transferringOrgUser.email}</p>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#3b82f6' }}>
                                Current Org: {transferringOrgUser.organizationName}
                            </p>
                        </div>

                        <div style={{ padding: '12px', background: '#fff7ed', borderRadius: '6px', marginBottom: '16px' }}>
                            <strong style={{ color: '#9a3412' }}>Warning</strong>
                            <p style={{ fontSize: '12px', color: '#9a3412', marginTop: '4px' }}>
                                {stats.transferSourceOrgAction === 'keep'
                                    ? 'This will add the user to the new Adobe organization and keep the current one.'
                                    : 'This will remove the user from the current Adobe organization and add them to the new one.'}
                                You must select which products to assign in the new organization. Duration and expiration will stay the same.
                            </p>
                        </div>

                        <form onSubmit={handleTransferOrg}>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label className="form-label">Select Target Organization</label>
                                <select
                                    className="form-input"
                                    value={selectedTransferOrg}
                                    onChange={e => {
                                        setSelectedTransferOrg(e.target.value);
                                        setSelectedTransferProducts([]); // Reset products when org changes
                                    }}
                                    required
                                >
                                    <option value="">-- Select Organization --</option>
                                    {organizationAccess
                                        .filter(org => String(org.organizationId) !== String(transferringOrgUser.organizationId))
                                        // A user on a K12 team stays on a K12 team, so only offer
                                        // targets of the same tier — otherwise the server refuses
                                        // the transfer after the fact.
                                        .filter(org => {
                                            if (isZelyxmain) return true;
                                            const userIsK12 = transferringOrgUser.k12 === true
                                                || isOrgK12(transferringOrgUser.organizationId);
                                            return userIsK12 ? org.k12 === true : org.k12 !== true;
                                        })
                                        .map(org => (
                                            <option key={org.organizationId} value={org.organizationId}>
                                                {org.organizationName}
                                            </option>
                                        ))}
                                </select>
                                {!isZelyxmain && (transferringOrgUser.k12 === true || isOrgK12(transferringOrgUser.organizationId)) && (
                                    <small style={{ color: 'var(--app-link)', display: 'block', marginTop: '4px' }}>
                                        This user is on a K12 team, so only K12 teams are listed.
                                    </small>
                                )}
                            </div>

                            {error && (
                                <div style={{ padding: '10px 12px', marginBottom: '14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '13px' }}>
                                    {error}
                                </div>
                            )}

                            {/* Product Selection - User selects which products to assign in target org */}
                            {selectedTransferOrg && transferringOrgUser && (
                                <div style={{ marginBottom: '16px' }}>
                                    {(() => {
                                        const targetOrg = organizationAccess.find(o => String(o.organizationId) === String(selectedTransferOrg));
                                        const availableProducts = assignableProducts(targetOrg ? targetOrg.products : []);
                                        const userCurrentProducts = transferringOrgUser.products || [];

                                        if (!targetOrg) return null;

                                        return (
                                            <>
                                                {/* Show current products info */}
                                                <div style={{ marginBottom: '12px', padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                    <strong style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Current Products:</strong>
                                                    <span style={{ fontSize: '13px', color: '#334155' }}>
                                                        {userCurrentProducts.join(', ') || 'None'}
                                                    </span>
                                                </div>

                                                {/* Product Selection */}
                                                <div style={{ padding: '12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #e0f2fe' }}>
                                                    <strong style={{ fontSize: '13px', color: '#0369a1', display: 'block', marginBottom: '8px' }}>
                                                        Select Products to Assign in {targetOrg.organizationName}:
                                                    </strong>

                                                    {availableProducts.length === 0 ? (
                                                        <div style={{ color: '#dc2626', fontSize: '12px' }}>
                                                            No products available in target organization
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            {availableProducts.map((product, idx) => (
                                                                <label key={idx} style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    padding: '8px',
                                                                    background: selectedTransferProducts.includes(product) ? '#dbeafe' : '#ffffff',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    border: selectedTransferProducts.includes(product) ? '1px solid #3b82f6' : '1px solid #e2e8f0'
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedTransferProducts.includes(product)}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedTransferProducts([...selectedTransferProducts, product]);
                                                                            } else {
                                                                                setSelectedTransferProducts(selectedTransferProducts.filter(p => p !== product));
                                                                            }
                                                                        }}
                                                                        style={{ width: '16px', height: '16px' }}
                                                                    />
                                                                    <span style={{ fontSize: '13px', color: '#334155' }}>{product}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {selectedTransferProducts.length === 0 && availableProducts.length > 0 && (
                                                        <div style={{ marginTop: '8px', color: '#dc2626', fontSize: '12px' }}>
                                                            Please select at least one product
                                                        </div>
                                                    )}

                                                    {selectedTransferProducts.length > 0 && (
                                                        <div style={{ marginTop: '8px', color: '#059669', fontSize: '12px' }}>
                                                            {selectedTransferProducts.length} product(s) selected
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button type="submit" disabled={loading || !selectedTransferOrg || selectedTransferProducts.length === 0}
                                    className="btn btn-primary" style={{ flex: 1 }}>
                                    {loading ? 'Transferring...' : 'Transfer User'}
                                </button>
                                <button type="button" onClick={() => {
                                    setTransferringOrgUser(null);
                                    setSelectedTransferProducts([]);
                                }}
                                    className="btn btn-secondary">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Extend Duration Modal */}
            {extendingUser && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={() => setExtendingUser(null)}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '450px', width: '90%', background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px' }}>
                        <h3 style={{ marginBottom: '16px' }}>Extend License Duration</h3>

                        <div style={{ padding: '12px', background: '#dbeafe', borderRadius: '6px', marginBottom: '16px' }}>
                            <strong style={{ color: '#1e40af' }}>User:</strong>
                            <p style={{ margin: '8px 0 0', color: '#1e40af' }}>{extendingUser.email}</p>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#3b82f6' }}>
                                Current: {getDurationLabel(extendingUser.durationKey, extendingUser)} • Expires: {new Date(extendingUser.expiresAt).toLocaleDateString()}
                            </p>
                        </div>

                        <form onSubmit={handleExtendUser}>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label className="form-label">Extension Period</label>
                                <select className="form-input" value={extensionMonths}
                                    onChange={e => setExtensionMonths(e.target.value.match(/^\d+$/) ? parseInt(e.target.value, 10) : e.target.value)}>
                                    {getVisibleActivationDurationOptions(stats.hiddenMonths || [])
                                        .map(option => {
                                            const credits = getOrgActivationCredits(extendingUser?.organizationId, option.key, extendingUser?.k12);
                                            return (
                                                <option key={option.key} value={option.key} disabled={credits > stats.creditsBalance}>
                                                    {option.label} ({formatCredits(credits)} credits)
                                                    {credits > stats.creditsBalance ? ' - Insufficient credits' : ''}
                                                </option>
                                            );
                                        })}
                                </select>
                            </div>

                            <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '6px', marginBottom: '16px' }}>
                                <strong style={{ color: '#92400e' }}>{formatCredits(getOrgActivationCredits(extendingUser?.organizationId, extensionMonths, extendingUser?.k12))} credit(s) will be deducted</strong>
                                <p style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                                    New expiry: {(() => {
                                        const current = new Date(extendingUser.expiresAt);
                                        const now = new Date();
                                        const base = current > now ? current : now;
                                        const newExpiry = new Date(base);
                                        if (String(extensionMonths) === '7d') newExpiry.setDate(newExpiry.getDate() + 7);
                                        else if (String(extensionMonths) === '14d') newExpiry.setDate(newExpiry.getDate() + 14);
                                        else newExpiry.setMonth(newExpiry.getMonth() + parseInt(extensionMonths, 10));
                                        return newExpiry.toLocaleDateString();
                                    })()}
                                </p>
                                <p style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                                    Balance after: {formatCredits(stats.creditsBalance - getOrgActivationCredits(extendingUser?.organizationId, extensionMonths, extendingUser?.k12))} credits
                                </p>
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button type="submit" disabled={loading || getOrgActivationCredits(extendingUser?.organizationId, extensionMonths, extendingUser?.k12) > stats.creditsBalance}
                                    className="btn btn-primary" style={{ flex: 1 }}>
                                    {loading ? 'Extending...' : 'Extend Duration'}
                                </button>
                                <button type="button" onClick={() => setExtendingUser(null)}
                                    className="btn btn-secondary">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Duration Modification Modal */}
            {showBulkDurationModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '450px', width: '90%' }}>
                        <h3 style={{ marginBottom: '16px' }}>Modify Duration</h3>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                            Change subscription duration for <strong>{selectedUsersForBulk.length} user(s)</strong>
                            <br /><small style={{ color: '#888' }}>Only users assigned more than 24 hours ago will be modified</small>
                        </p>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label className="form-label">New Duration (months)</label>
                            <select className="form-input" value={bulkNewDuration}
                                onChange={(e) => setBulkNewDuration(parseInt(e.target.value))}>
                                {[1, 2, 3, 6, 12]
                                    .filter(m => !(stats.hiddenMonths || []).map(String).includes(String(m)))
                                    .map(m => (
                                    <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
                            <strong>Note:</strong> Credits will be automatically adjusted:
                            <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                                <li>Downgrade (reduce duration) → Credits refunded</li>
                                <li>Upgrade (increase duration) → Credits charged</li>
                            </ul>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={handleBulkModifyDuration} disabled={loading}
                                className="btn btn-primary" style={{ flex: 1 }}>
                                {loading ? 'Modifying...' : 'Apply Changes'}
                            </button>
                            <button onClick={() => { setShowBulkDurationModal(false); setBulkNewDuration(1); }}
                                className="btn btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Single Reinvite Product Mapping Modal */}
            {reinvitingUser && canUseCPReinvite && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={() => { setReinvitingUser(null); setReinviteProductMapping([]); setReinviteSelectedProducts([]); }}>
                    <div style={{ background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '90%' }}
                        onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginBottom: '16px' }}>Reinvite User</h3>

                        <div style={{ padding: '12px', background: '#dbeafe', borderRadius: '6px', marginBottom: '16px' }}>
                            <strong style={{ color: '#1e40af' }}>User:</strong>
                            <p style={{ margin: '8px 0 0', color: '#1e40af' }}>{reinvitingUser.email}</p>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#3b82f6' }}>
                                Organization: {reinvitingUser.organizationName || `Org ${reinvitingUser.organizationId}`}
                            </p>
                        </div>

                        <div style={{ padding: '12px', background: 'var(--app-success-bg)', borderRadius: '6px', marginBottom: '16px', border: '1px solid rgba(27, 138, 90, 0.35)' }}>
                            <strong style={{ color: 'var(--app-success-text)' }}>This will re-send the Adobe invitation</strong>
                            <p style={{ fontSize: '12px', color: 'var(--app-success-text)', marginTop: '4px' }}>
                                If the user is already in Adobe, no action will be taken. No credits will be charged.
                            </p>
                        </div>

                        {/* Product Mapping & Selection */}
                        {(() => {
                            let allowedProducts = getAllowedProductsForOrg(reinvitingUser.organizationId);
                            const noOrgAccess = allowedProducts.length === 0;
                            // Fallback: use all products from all accessible orgs
                            if (noOrgAccess) allowedProducts = getAllAllowedProducts();
                            const userProducts = reinvitingUser.products || [];

                            if (allowedProducts.length === 0) {
                                return (
                                    <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', borderRadius: '6px', border: '1px solid rgba(96, 165, 250, 0.45)' }}>
                                        <strong style={{ fontSize: '13px', color: '#1e40af', display: 'block', marginBottom: '6px' }}>Using existing products</strong>
                                        <p style={{ fontSize: '11px', color: '#1e40af', margin: 0 }}>
                                            No products available. The user will be reinvited with their current stored products.
                                        </p>
                                    </div>
                                );
                            }

                            // Case 1: User has no stored products - show product selection checkboxes
                            if (userProducts.length === 0) {
                                return (
                                    <div style={{ marginBottom: '16px', padding: '12px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                                        <strong style={{ fontSize: '13px', color: '#92400e', display: 'block', marginBottom: '8px' }}>No products assigned to this user</strong>
                                        <p style={{ fontSize: '11px', color: '#92400e', marginBottom: '12px' }}>
                                            Select the products to assign during reinvite:
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {allowedProducts.map((product) => (
                                                <label key={product} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={reinviteSelectedProducts.includes(product)}
                                                        onChange={e => {
                                                            if (e.target.checked) {
                                                                setReinviteSelectedProducts(prev => [...prev, product]);
                                                            } else {
                                                                setReinviteSelectedProducts(prev => prev.filter(p => p !== product));
                                                            }
                                                        }}
                                                    />
                                                    {product}
                                                </label>
                                            ))}
                                        </div>
                                        {reinviteSelectedProducts.length === 0 && (
                                            <p style={{ fontSize: '11px', color: '#dc2626', marginTop: '8px' }}>Please select at least one product</p>
                                        )}
                                    </div>
                                );
                            }

                            // Case 2: User has stored products - show mapping + option to add more
                            const hasUnmapped = reinviteProductMapping.some((m) => !m);
                            const needsMapping = userProducts.some((p) => !allowedProducts.includes(p));

                            return (
                                <>
                                    <div style={{ marginBottom: '16px', padding: '12px', background: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                                        <strong style={{ fontSize: '13px', color: '#9a3412', display: 'block', marginBottom: '8px' }}>Product Mapping:</strong>
                                        <p style={{ fontSize: '11px', color: '#9a3412', marginBottom: '12px' }}>
                                            {needsMapping || hasUnmapped
                                                ? 'Some products need to be mapped to your available products.'
                                                : 'Products are auto-matched. Change if needed.'}
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {userProducts.map((product, idx) => (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '12px', color: '#64748b', minWidth: '120px' }}>
                                                        {product}
                                                    </span>
                                                    <span style={{ color: '#9a3412' }}>→</span>
                                                    <select
                                                        className="form-input"
                                                        value={reinviteProductMapping[idx] || ''}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            setReinviteProductMapping(prev => {
                                                                const next = [...prev];
                                                                next[idx] = val;
                                                                return next;
                                                            });
                                                        }}
                                                        style={{ flex: 1, minWidth: '180px', fontSize: '12px', padding: '6px 8px' }}
                                                    >
                                                        <option value="">-- Select product --</option>
                                                        {allowedProducts.map(ap => (
                                                            <option key={ap} value={ap}>{ap}</option>
                                                        ))}
                                                    </select>
                                                    {!reinviteProductMapping[idx] && hasUnmapped && (
                                                        <span style={{ fontSize: '11px', color: '#dc2626' }}>Required</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            );
                        })()}

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={confirmReinvite} disabled={loading}
                                className="btn btn-primary" style={{ flex: 1, background: '#0b7da6', color: 'white', border: 'none' }}>
                                {loading ? 'Reinviting...' : 'Confirm Reinvite'}
                            </button>
                            <button onClick={() => { setReinvitingUser(null); setReinviteProductMapping([]); setReinviteSelectedProducts([]); }}
                                className="btn btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Reinvite Config Modal - Product mapping per organization */}
            {showBulkReinviteConfig && canUseCPReinvite && (() => {
                // Group selected users by organization
                const orgGroups = {};
                selectedUsersForBulk.forEach(email => {
                    const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
                    if (!user) return;
                    const orgId = String(user.organizationId || 'unknown');
                    const orgName = user.organizationName || 'Unknown Organization';
                    if (!orgGroups[orgId]) {
                        orgGroups[orgId] = {
                            orgId,
                            orgName,
                            availableProducts: [],
                            users: []
                        };
                    }
                    orgGroups[orgId].users.push(user);
                });
                // Compute availableProducts per org with fallback to user products
                Object.values(orgGroups).forEach(og => {
                    let products = getAllowedProductsForOrg(og.orgId);
                    if (products.length === 0) {
                        const userProds = [];
                        og.users.forEach(u => (u.products || []).forEach(p => { if (!userProds.includes(p)) userProds.push(p); }));
                        products = userProds;
                    }
                    og.availableProducts = assignableProducts(products);
                });
                const orgList = Object.values(orgGroups);
                const allOrgsHaveProducts = orgList.every(og =>
                    (bulkReinviteOrgProducts[og.orgId] || []).length > 0
                );

                return (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                        onClick={() => setShowBulkReinviteConfig(false)}>
                        <div style={{ background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px', maxWidth: '600px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
                            onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h3 style={{ margin: 0 }}>Bulk Reinvite - Product Selection</h3>
                                <button onClick={() => setShowBulkReinviteConfig(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                            </div>

                            <div style={{ padding: '12px', background: 'var(--app-success-bg)', borderRadius: '6px', marginBottom: '16px', border: '1px solid rgba(27, 138, 90, 0.35)' }}>
                                <strong style={{ color: 'var(--app-success-text)' }}>Select products to assign for each organization</strong>
                                <p style={{ fontSize: '12px', color: 'var(--app-success-text)', marginTop: '4px' }}>
                                    {selectedUsersForBulk.length} user(s) across {orgList.length} organization(s). Users already in Adobe will have products added.
                                </p>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
                                {orgList.map((og) => (
                                    <div key={og.orgId} style={{ marginBottom: '16px', padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{og.orgName}</strong>
                                            <span style={{ fontSize: '12px', color: '#64748b', background: '#e2e8f0', padding: '2px 8px', borderRadius: '10px' }}>
                                                {og.users.length} user(s)
                                            </span>
                                        </div>

                                        {og.availableProducts.length === 0 ? (
                                            <p style={{ fontSize: '12px', color: '#dc2626' }}>No products available for this organization</p>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {og.availableProducts.map(product => {
                                                    const selected = (bulkReinviteOrgProducts[og.orgId] || []).includes(product);
                                                    return (
                                                        <label key={product} style={{
                                                            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                                                            cursor: 'pointer', padding: '8px 10px', borderRadius: '6px',
                                                            background: selected ? '#dbeafe' : '#fff',
                                                            border: selected ? '1px solid #3b82f6' : '1px solid #e2e8f0'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selected}
                                                                onChange={e => {
                                                                    setBulkReinviteOrgProducts(prev => {
                                                                        const current = prev[og.orgId] || [];
                                                                        return {
                                                                            ...prev,
                                                                            [og.orgId]: e.target.checked
                                                                                ? [...current, product]
                                                                                : current.filter(p => p !== product)
                                                                        };
                                                                    });
                                                                }}
                                                            />
                                                            {product}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {(bulkReinviteOrgProducts[og.orgId] || []).length === 0 && og.availableProducts.length > 0 && (
                                            <p style={{ fontSize: '11px', color: '#dc2626', marginTop: '8px' }}>Please select at least one product</p>
                                        )}

                                        {/* Show user emails in this org */}
                                        <details style={{ marginTop: '8px' }}>
                                            <summary style={{ fontSize: '11px', color: 'var(--text-muted)', cursor: 'pointer' }}>
                                                Show {og.users.length} user(s) in this org
                                            </summary>
                                            <div style={{ marginTop: '4px', padding: '6px 8px', background: '#fff', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace', maxHeight: '100px', overflowY: 'auto', lineHeight: '1.6' }}>
                                                {og.users.map((u, i) => <div key={i}>{u.email}</div>)}
                                            </div>
                                        </details>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={executeBulkReinvite}
                                    disabled={!allOrgsHaveProducts}
                                    className="btn btn-primary"
                                    style={{ flex: 1, background: '#0b7da6', color: 'white', border: 'none' }}>
                                    Confirm Reinvite ({selectedUsersForBulk.length} users)
                                </button>
                                <button onClick={() => setShowBulkReinviteConfig(false)} className="btn btn-secondary">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Bulk Reinvite Progress Modal */}
            {bulkReinviteProgress && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px', maxWidth: '600px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
                        onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0 }}>Bulk Reinvite Progress</h3>
                            {bulkReinviteProgress.current === bulkReinviteProgress.total && (
                                <button onClick={() => setBulkReinviteProgress(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                            )}
                        </div>

                        {/* Progress Bar */}
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                                <span>Processing {bulkReinviteProgress.current} / {bulkReinviteProgress.total}</span>
                                <span>{Math.round((bulkReinviteProgress.current / bulkReinviteProgress.total) * 100)}%</span>
                            </div>
                            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%',
                                    width: `${(bulkReinviteProgress.current / bulkReinviteProgress.total) * 100}%`,
                                    background: bulkReinviteProgress.current === bulkReinviteProgress.total ? '#059669' : '#3b82f6',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease'
                                }} />
                            </div>
                        </div>

                        {/* Current user being checked */}
                        {bulkReinviteProgress.current < bulkReinviteProgress.total && (
                            <div style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}><Icon name="clock" size={16} /></span>
                                Checking: <strong>{bulkReinviteProgress.currentEmail}</strong>
                            </div>
                        )}

                        {/* Summary counters */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', fontSize: '13px' }}>
                            <span style={{ padding: '4px 10px', background: '#dcfce7', color: '#166534', borderRadius: '12px' }}>
                                Invited: {bulkReinviteProgress.results.filter(r => r.status === 'invited').length}
                            </span>
                            <span style={{ padding: '4px 10px', background: '#dff4fd', color: '#0b7da6', borderRadius: '12px' }}>
                                Skipped: {bulkReinviteProgress.results.filter(r => r.status === 'skipped').length}
                            </span>
                            <span style={{ padding: '4px 10px', background: '#fef2f2', color: '#991b1b', borderRadius: '12px' }}>
                                Failed: {bulkReinviteProgress.results.filter(r => r.status === 'failed').length}
                            </span>
                        </div>

                        {/* Live results list */}
                        <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', maxHeight: '300px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>#</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Products</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkReinviteProgress.results.map((r, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '6px 10px', color: '#6b7280' }}>{idx + 1}</td>
                                            <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{r.email}</td>
                                            <td style={{ padding: '6px 10px', fontSize: '11px', color: '#475569' }}>
                                                {Array.isArray(r.products) && r.products.length > 0 ? r.products.join(', ') : '-'}
                                            </td>
                                            <td style={{ padding: '6px 10px' }}>
                                                {r.status === 'invited' && <span style={{ color: '#059669' }}>Invited</span>}
                                                {r.status === 'skipped' && <span style={{ color: '#0b7da6' }}>{r.message}</span>}
                                                {r.status === 'failed' && <span style={{ color: '#dc2626' }}>{r.message}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Done button */}
                        {bulkReinviteProgress.current === bulkReinviteProgress.total && (
                            <button onClick={() => setBulkReinviteProgress(null)}
                                className="btn btn-primary" style={{ marginTop: '12px', width: '100%' }}>
                                Done
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Failures Modal */}
            {viewingFailures && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={() => setViewingFailures(null)}>
                    <div style={{ background: 'var(--surface-primary)', borderRadius: '12px', padding: '24px', maxWidth: '700px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
                        onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0 }}>Failed Users ({viewingFailures.failures.length})</h3>
                            <button onClick={() => setViewingFailures(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                        </div>

                        {viewingFailures.failures.length === 0 ? (
                            <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>No failure details available.</p>
                        ) : (
                            <>
                                <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '16px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                                                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                                                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewingFailures.failures.map((f, idx) => (
                                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '10px', fontFamily: 'monospace', userSelect: 'text' }}>{f.email}</td>
                                                    <td style={{ padding: '10px', color: '#dc2626', userSelect: 'text' }}>{f.error || 'Unknown error'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        onClick={() => exportFailuresToCsv(viewingFailures.failures, viewingFailures.operationType)}
                                        className="btn btn-primary"
                                        style={{ flex: 1 }}>
                                        Export to CSV
                                    </button>
                                    <button
                                        onClick={() => {
                                            const text = viewingFailures.failures.map(f => f.email).join('\n');
                                            navigator.clipboard.writeText(text);
                                            alert('Emails copied to clipboard!');
                                        }}
                                        className="btn btn-secondary"
                                        style={{ flex: 1 }}>
                                        Copy Emails Only
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default CreditsPoolDashboard;
